import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { usePedirTexto, useConfirmarPerigo } from './shared/ConfirmDialog';
import { expensesService, expenseCategoriesService, suppliersService, bankAccountsService, recurringExpensesService, clientsService, cnpjService } from '../services/api';
import { fmtDate, monthNames, navigateMonth, formatMonthLabel, getTodayStr } from '../utils/formatters';
import ListToolbar, { ToolbarSearch } from './shared/ListToolbar';
import PeriodSelector, { periodToParams } from './PeriodSelector';
import { usePeriodoNaUrl } from '../utils/periodoNaUrl';
import ClientPopover from './ClientPopover';
import { useSortState } from './shared/SortableTableHeader';
import { TONE_BADGE, TONE_BORDER, TONE_SURFACE, TONE_TEXT } from '../utils/statusColors';
import CascaDeModal from './shared/CascaDeModal';
import MaisAcoes from './shared/MaisAcoes';
import {
  CELULA, CascaDaLista, TabelaDaLista, CabecalhoDaLista, ColunaDaLista, LinhaDaLista, CelulaAcoes, CelulaPrazo,
  CabecalhoInterno, CartaoDoTopo, barraDoEstado,
} from './shared/lista/PecasDaLista';
import { useEstadoDaCarga, EstadoDaLista, A_PROPRIA_LISTA_DIZ_O_VAZIO, mensagemDoErroDeCarga, lerEmLote, lerSecao, campo, textos, opcaoAtualForaDaLista } from '../design';
import { rotuloDoRecorte } from '../utils/notasFiscais';
import { confirmacaoDeSaidaDaDespesa, mesParaGerarRecorrentes, confirmacaoDeGerarRecorrentes, exportacaoDasDespesas, AVISO_DO_CSV, avisoDaFaturaDoCartao, confirmacaoDeDesativar } from '../utils/despesas';
import { nomeDeExibicao } from '../utils/nomeDeExibicao';
// Exclusao de despesa, import de CSV e regras de cartao sao @Roles(ADMIN) no
// backend. Sem esta checagem os botoes apareciam para EDITOR e VIEWER, que
// levavam 403 com a mensagem generica "Erro ao excluir" — o usuario nao
// descobria que o problema era permissao. Em escopo de modulo porque os modais
// deste arquivo sao componentes separados e precisam da mesma resposta.
const isAdmin = (): boolean => {
  try { return JSON.parse(localStorage.getItem('user') || '{}')?.role === 'admin'; }
  catch { return false; }
};

const fmt = (v: any) => {
  const n = parseFloat(String(v));
  return isNaN(n) ? '0,00' : n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// Despesa com parte paga e parte em aberto: o mesmo critério da barra da lista de Despesas
// (paidAmount > 0 e status ≠ Pago). Fora disso, null, e a linha mostra só o valor.
const pagamentoParcial = (e: any): { pago: number; total: number; falta: number; pct: number } | null => {
  const pago = parseFloat(String(e?.paidAmount)) || 0;
  const total = parseFloat(String(e?.amount)) || 0;
  if (pago <= 0 || e?.status === 'Pago' || e?.status === 'Cancelado') return null;
  return {
    pago,
    total,
    falta: Math.max(0, Math.round((total - pago) * 100) / 100),
    pct: total > 0 ? Math.min(100, (pago / total) * 100) : 0,
  };
};

// A conciliacao sobrescreve `amount` com o valor que saiu de fato do banco e
// guarda o previsto original em `plannedAmount` (bank-transactions.service.ts,
// updateParentStatus faz o mesmo no pai quando a primeira filha e conciliada).
// Os dois campos ja chegavam no payload do GET /expenses e a tabela mostrava so
// o `amount`: o valor da linha mudava sozinho entre uma visita e outra, sem nada
// na tela dizendo que aquilo era realizado e nao mais previsao.
const PlannedHint = ({ expense }: { expense: any }) => {
  const raw = expense?.plannedAmount;
  if (raw === null || raw === undefined) return null;
  const planned = parseFloat(String(raw));
  const amount = parseFloat(String(expense?.amount));
  if (isNaN(planned) || isNaN(amount)) return null;
  if (Math.abs(planned - amount) < 0.01) return null;
  const diff = amount - planned;
  const lado = diff > 0 ? `R$ ${fmt(diff)} acima` : `R$ ${fmt(Math.abs(diff))} abaixo`;
  return (
    <div
      className="text-xs text-slate-500 whitespace-nowrap"
      title={`Previsto R$ ${fmt(planned)} antes da conciliação. O valor exibido é o realizado, ${lado} do previsto.`}
    >
      prev R$ {fmt(planned)}
    </div>
  );
};

// `variableAmount` marca "o valor muda todo mes" (conta de luz, cartao). Ja era
// editavel no formulario e invisivel na tabela, entao uma estimativa de R$ 1.180
// era lida como numero fechado.
const VariableMark = ({ expense }: { expense: any }) =>
  expense?.variableAmount ? (
    <span className="text-slate-500 mr-0.5" title="Valor variável: este é o valor estimado">~</span>
  ) : null;

// Glifos como string JS, nunca como texto solto no JSX. Escritos direto no meio
// da marcacao eles chegaram a virar "—" e "⚖" LITERAIS na tela: o texto
// entre tags e conteudo cru, entao um escape ali nao e interpretado. Dentro de
// chaves o valor e sempre a string JS, independente de como o arquivo foi gravado.
const TRACO = '—'; // em-dash, o vazio padrao da UI
const GLIFO_CLIENTE = '👤'; // repassado a cliente
const GLIFO_SOCIO = '⚖'; // deduzido de socio
const SETA_FILHA = '↳'; // lancamento dentro de um grupo
const LABEL_LANCADO = 'lanç.'; // prefixo da data propria da linha
const TITULO_DATA_LANCAMENTO = 'Data do lançamento. O vencimento é o do grupo.';

// A importacao da fatura carimba no `notes` do lancamento a identificacao vinda da
// regra (o que e a cobranca, cadencia, dono), prefixada pela parcela quando existe.
// A parcela ja aparece no InstallmentBadge, entao aqui tiramos ela pra nao repetir.
const identificacaoDoItem = (notes?: string | null): string =>
  (notes || '').replace(/^Parcela\s+\d+\/\d+\s*(·\s*)?/i, '').trim();

// Popover do fornecedor, no mesmo espirito do ClientPopover, com duas diferencas
// deliberadas:
//  - `position: fixed`, nao `absolute`. O wrapper da tabela tem overflow-x-auto e
//    pela spec o outro eixo deixa de ser `visible`, entao um popover absolute e
//    recortado nas ultimas linhas (foi o mesmo defeito do menu de acoes).
//  - sem fetch: o `leftJoinAndSelect('e.supplier')` do findAll ja traz o objeto
//    inteiro na linha, entao buscar de novo seria round-trip para dado em maos.
const SupplierPopover = ({ supplier, onFilter, onEdit }: { supplier: any; onFilter: () => void; onEdit: () => void }) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  // Fecha no clique fora pelo document (como o MaisAcoes), sem o fundo de tela inteira montado à mão (10/10/2026).
  const caixaRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const fora = (e: MouseEvent) => { if (caixaRef.current && !caixaRef.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', fora);
    return () => document.removeEventListener('mousedown', fora);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    // o Esc é do dropdown aberto: preventDefault para a casca de fora não fechar junto
    const fechar = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.preventDefault(); setOpen(false); } };
    document.addEventListener('keydown', fechar);
    return () => document.removeEventListener('keydown', fechar);
  }, [open]);

  if (!supplier?.name) return <span className="text-slate-600">{TRACO}</span>;

  const doc = supplier.cnpj || supplier.cpf || supplier.taxId;
  const abrir = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (open) { setOpen(false); return; }
    const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const LARGURA = 300;
    const ALTURA = 220;
    setPos({
      top: r.bottom + ALTURA > window.innerHeight ? Math.max(8, r.top - ALTURA) : r.bottom + 4,
      left: Math.min(r.left, window.innerWidth - LARGURA - 8),
    });
    setOpen(true);
  };

  return (
    <div ref={caixaRef} className="contents">
      <button
        onClick={abrir}
        title={supplier.name}
        className="text-slate-400 hover:text-white hover:underline text-left truncate block w-full"
      >
        {supplier.name}
      </button>
      {open && pos && (
        <>
          <div
            className="fixed z-50 bg-slate-800 border border-slate-600 rounded-xl shadow-2xl p-4 w-[300px] text-sm"
            style={{ top: pos.top, left: pos.left }}
          >
            <div className="pb-2 border-b border-slate-700">
              <div className="text-white font-semibold">{supplier.name}</div>
              {supplier.companyName && supplier.companyName !== supplier.name && (
                <div className="text-slate-400 text-xs mt-0.5">{supplier.companyName}</div>
              )}
              {supplier.isActive === false && (
                <span className="inline-block mt-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-red-500/20 text-red-400">
                  Inativo
                </span>
              )}
            </div>
            <div className="space-y-1.5 mt-2 text-xs">
              {doc && (
                <div className="flex justify-between gap-2">
                  <span className="text-slate-500">Documento</span>
                  <span className="text-slate-300 font-mono">{doc}</span>
                </div>
              )}
              {supplier.email && (
                <div className="flex justify-between gap-2">
                  <span className="text-slate-500">E-mail</span>
                  <span className="text-slate-300 truncate">{supplier.email}</span>
                </div>
              )}
              {supplier.phone && (
                <div className="flex justify-between gap-2">
                  <span className="text-slate-500">Telefone</span>
                  <span className="text-slate-300">{supplier.phone}</span>
                </div>
              )}
              {supplier.defaultCategory?.name && (
                <div className="flex justify-between gap-2">
                  <span className="text-slate-500">Categoria padrão</span>
                  <span className="text-slate-300">{supplier.defaultCategory.name}</span>
                </div>
              )}
              {!doc && !supplier.email && !supplier.phone && (
                <div className="text-slate-500">Sem dados de contato cadastrados.</div>
              )}
            </div>
            <div className="mt-3 flex gap-2">
              <button
                onClick={() => { onFilter(); setOpen(false); }}
                className="flex-1 px-3 py-1.5 text-xs font-medium bg-slate-700 text-slate-300 rounded-lg hover:bg-slate-600 hover:text-white"
              >
                Ver despesas
              </button>
              {/* Abre o mesmo SupplierForm da aba Fornecedores, no lugar. Mandar
                  para a outra sub-view faria perder o filtro e a posicao da fila. */}
              <button
                onClick={() => { onEdit(); setOpen(false); }}
                className="flex-1 px-3 py-1.5 text-xs font-medium bg-slate-700 text-slate-300 rounded-lg hover:bg-slate-600 hover:text-white"
              >
                Editar cadastro
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// Parcela como badge, com a ULTIMA destacada em ambar: "37/37" e a informacao
// de que aquela despesa recorrente acaba agora, e nao vai reaparecer no mes que
// vem. Como texto solto isso passava batido.
// Identificação estruturada do item de fatura (vem da regra do cartão, editável no
// lançamento): cadência e quem usa viram chips; a observação vira só um aviso com o
// texto no hover, para não competir com o nome na linha.
const CADENCE_LABEL: Record<string, string> = { mensal: 'Mensal', anual: 'Anual', avulso: 'Avulso' };
const IdentChips = ({ expense }: { expense: any }) => {
  if (!expense?.cadence && !expense?.owner) return null;
  return (
    <>
      {expense.cadence && (
        <span className={`px-1.5 py-px rounded text-[10px] font-medium ${expense.cadence === 'anual' ? 'bg-amber-500/15 text-amber-300' : 'bg-slate-700/70 text-slate-300'}`}>
          {CADENCE_LABEL[expense.cadence] || expense.cadence}
        </span>
      )}
      {expense.owner && (
        <span className="px-1.5 py-px rounded text-[10px] font-medium bg-violet-500/15 text-violet-300">{expense.owner}</span>
      )}
    </>
  );
};
const NoteHint = ({ notes }: { notes?: string | null }) => {
  const texto = (notes || '').replace(/^Parcela \d+\/\d+( · )?/, '').trim();
  if (!texto) return null;
  const alerta = /^ATEN|INDEVID|CANCEL/i.test(texto);
  return (
    <span
      title={texto}
      className={`inline-flex items-center justify-center w-4 h-4 rounded-full text-[10px] font-bold cursor-help ${alerta ? 'bg-red-500/20 text-red-300' : 'bg-slate-700 text-slate-400'}`}
    >
      {alerta ? '!' : 'i'}
    </span>
  );
};

// Formulário de despesa: campos e seções no padrão do guia visual (inputs bg-slate-900 /
// border-slate-700 / foco laranja; seção = superfície calma com borda; cor só em texto e borda).
const INPUT = `w-full px-3 py-2 ${campo} rounded-lg focus:outline-none`;
const LABEL = 'block text-xs text-slate-400 mb-1';
const FormSection = ({ title, hint, tone, action, children }: { title: string; hint?: string; tone?: 'warning' | 'danger'; action?: React.ReactNode; children: React.ReactNode }) => {
  const border = tone === 'danger' ? 'border-red-600/50' : tone === 'warning' ? 'border-amber-600/60' : 'border-slate-700';
  const color = tone === 'danger' ? 'text-red-300' : tone === 'warning' ? 'text-amber-300' : 'text-slate-400';
  return (
    <section className={`rounded-xl border ${border} bg-slate-800/50 p-4 space-y-3`}>
      <div className="flex items-baseline justify-between gap-3">
        <h4 className={`text-xs font-semibold uppercase tracking-wider ${color}`}>{title}</h4>
        <div className="flex items-center gap-3">
          {hint && <span className="text-xs text-slate-500">{hint}</span>}
          {action}
        </div>
      </div>
      {children}
    </section>
  );
};

const InstallmentBadge = ({ expense }: { expense: any }) => {
  const total = Number(expense?.totalInstallments || 0);
  if (!total) return null;
  const atual = Number(expense?.installmentNumber || 0);
  const ultima = atual > 0 && atual >= total;
  return (
    <span
      className={`px-1.5 py-0.5 text-xs rounded font-medium tabular-nums ${
        ultima ? 'bg-amber-500/20 text-amber-400' : 'bg-slate-600/50 text-slate-300'
      }`}
      title={ultima ? 'Última parcela' : `Parcela ${atual || '?'} de ${total}`}
    >
      {atual || '?'}/{total}
    </span>
  );
};

const paymentMethodLabels: Record<string, string> = {
  pix: 'PIX',
  boleto: 'Boleto',
  cartao: 'Cartão',
  transferencia: 'Transferência',
  debito: 'Débito',
  dinheiro: 'Dinheiro',
};

// Classes COMPLETAS de proposito. A versao anterior guardava so o nome da cor
// ('yellow') e a linha do chip montava `bg-${color}-500/20` em runtime. O
// tailwind.config.js deste repo nao tem `safelist`, entao classe montada assim
// so existe no CSS final por acidente, quando o literal aparece escrito em
// algum outro arquivo do src/. Status novo com cor nova renderizaria o chip
// sem cor nenhuma, sem erro no console.
const statusChipClass: Record<string, string> = {
  Pendente: TONE_BADGE.warning,
  // Parcial fica laranja de propósito: mesmo vocabulário do botão Parcial do Recebíveis
  Parcial: 'bg-orange-500/20 text-orange-400',
  Pago: TONE_BADGE.open,
  Vencido: TONE_BADGE.danger,
  Cancelado: TONE_BADGE.neutral,
};

const Icon = ({ name, className = 'w-5 h-5' }: { name: string; className?: string }) => {
  const icons: any = {
    plus: <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M12 4v16m8-8H4' /></svg>,
    edit: <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z' /></svg>,
    trash: <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16' /></svg>,
    check: <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M5 13l4 4L19 7' /></svg>,
    upload: <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12' /></svg>,
    x: <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M6 18L18 6M6 6l12 12' /></svg>,
    search: <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z' /></svg>,
    refresh: <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15' /></svg>,
    download: <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4' /></svg>,
    ban: <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636' /></svg>,
    alertCircle: <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z' /></svg>,
    loader: <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15' /></svg>,
    scissors: <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><circle cx='6' cy='6' r='3' strokeWidth={2}/><circle cx='6' cy='18' r='3' strokeWidth={2}/><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M20 4L8.12 15.88M14.47 14.48L20 20M8.12 8.12L12 12'/></svg>,
    'chevron-right': <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M9 5l7 7-7 7' /></svg>,
    'chevron-down': <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M19 9l-7 7-7-7' /></svg>,
    paperclip: <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13' /></svg>,
    list: <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M4 6h16M4 12h16M4 18h16' /></svg>,
    // 'credit-card' ja era usado pelo botao Importar Fatura e nao existia neste
    // mapa: o fallback e null, entao o botao vinha sem icone nenhum, em silencio.
    'credit-card': <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><rect x='2' y='5' width='20' height='14' rx='2' strokeWidth={2}/><path strokeLinecap='round' strokeWidth={2} d='M2 10h20' /></svg>,
  };
  return icons[name] || null;
};

type SubView = 'expenses' | 'recurring' | 'suppliers' | 'budget' | 'partners';

const CardStatementImportModal = ({ onClose, categories, setCategories, bankAccounts, suppliers, showToast, loadExpenses, loadBase }: { onClose: () => void; categories: any[]; setCategories: (v: any[]) => void; bankAccounts: any[]; suppliers: any[]; showToast: (message: string, type?: 'success' | 'error') => void; loadExpenses: () => void; loadBase: () => void }) => {
    const confirmarPerigo = useConfirmarPerigo();
    const [step, setStep] = useState<1 | 2 | 3>(1);
    const [file, setFile] = useState<File | null>(null);
    const [cardBank, setCardBank] = useState('auto');
    const [parsing, setParsing] = useState(false);
    const [importing, setImporting] = useState(false);
    const [parseResult, setParseResult] = useState<any>(null);

    // Editable fields for step 2
    const [description, setDescription] = useState('');
    const [parentCategoryId, setParentCategoryId] = useState<number | ''>('');
    const [selectedBankAccountId, setSelectedBankAccountId] = useState<number | ''>('');
    const [editDueDate, setEditDueDate] = useState('');
    const [editRefMonth, setEditRefMonth] = useState('');
    const [editGroups, setEditGroups] = useState<any[]>([]);
    const [editUnclassified, setEditUnclassified] = useState<any[]>([]);
    const [expandedGroups, setExpandedGroups] = useState<Set<number>>(new Set());

    // "+ Novo grupo..." no Mover para (02/11/2026): o grupo nasce ali mesmo, na linha da transação, sem passar por
    // Gerenciar Regras e reimportar. A categoria também pode nascer ali ('nova').
    const [novoGrupoPara, setNovoGrupoPara] = useState<any>(null);
    const [novoGrupoNome, setNovoGrupoNome] = useState('');
    const [novoGrupoCat, setNovoGrupoCat] = useState<number | '' | 'nova'>('');
    const [novaCatNome, setNovaCatNome] = useState('');
    const [criandoCat, setCriandoCat] = useState(false);

    // Rules panel
    const [showRules, setShowRules] = useState(false);
    const [rules, setRules] = useState<any[]>([]);
    const [newRulePattern, setNewRulePattern] = useState('');
    const [newRuleGroup, setNewRuleGroup] = useState('');
    const [newRuleCategoryId, setNewRuleCategoryId] = useState<number | ''>('');
    const [editingRuleId, setEditingRuleId] = useState<number | null>(null);
    const emptyRuleIdent = { displayName: '', cadence: '', owner: '', amountMin: '', amountMax: '', notes: '' };
    const [ruleIdent, setRuleIdent] = useState<{ displayName: string; cadence: string; owner: string; amountMin: string; amountMax: string; notes: string }>(emptyRuleIdent);
    const [reapplying, setReapplying] = useState(false);
    const handleReapplyRules = async (onlyMissing: boolean) => {
      if (!onlyMissing && !await confirmarPerigo('Sobrescrever nome, cadência e dono em TODOS os itens de fatura que casarem com alguma regra? Edições feitas à mão nos itens serão perdidas (as observações ficam).')) return;
      setReapplying(true);
      try {
        const r = await expensesService.reapplyCardStatementRules(onlyMissing);
        showToast(`${r.updated} de ${r.scanned} itens de fatura atualizados pelas regras`, 'success');
        await loadExpenses();
      } catch (err: any) {
        showToast(err.response?.data?.message || 'Erro ao reaplicar regras', 'error');
      }
      setReapplying(false);
    };

    // Existing expense to replace
    const [existingExpenses, setExistingExpenses] = useState<any[]>([]);
    const [selectedExistingId, setSelectedExistingId] = useState<number | null>(null);

    // Import result
    const [importResult, setImportResult] = useState<any>(null);

    const [rulesErro, setRulesErro] = useState<string | null>(null);
    const loadRules = async () => {
      try {
        const r = await expensesService.getCardStatementRules();
        setRules(Array.isArray(r) ? r : []);
        setRulesErro(null);
      } catch (e) {
        // Calava: "Nenhuma regra cadastrada" com a API fora (e o aprender criava regra que já existia).
        setRulesErro(mensagemDoErroDeCarga(e));
      }
    };

    const handleParse = async () => {
      if (!file) return;
      setParsing(true);
      try {
        const res = await expensesService.parseCardStatement(file, cardBank);
        setParseResult(res);
        setEditDueDate(res.dueDate || '');
        setEditRefMonth(res.referenceMonth || '');
        setEditGroups(res.groups || []);
        setEditUnclassified(res.unclassified || []);
        // Auto-select "Cartao de Credito" category
        const ccCat = categories.find((c: any) => /cart[aã]o/i.test(c.name));
        if (ccCat) setParentCategoryId(ccCat.id);
        // Auto-select bank account based on detected bank
        const bankName = res.detectedBank || '';
        const bankLabel: Record<string, string> = { santander: 'Santander', caixa: 'Caixa', sicredi: 'Sicredi' };
        const label = bankLabel[bankName] || bankName;
        const matchedBank = bankAccounts.find((b: any) => new RegExp(bankName, 'i').test(b.bankName));
        if (matchedBank) setSelectedBankAccountId(matchedBank.id);
        // Build description: "Fatura Cartão Santander 0209 - Mar/2026"
        const monthNames = ['Jan', 'Feb', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
        const refMonth = res.referenceMonth || '';
        const [ry, rm] = refMonth.split('-');
        const monthLabel = rm ? `${monthNames[parseInt(rm, 10) - 1]}/${ry}` : '';
        const last4 = res.cardLast4 ? ` ${res.cardLast4}` : '';
        setDescription(`Fatura Cartao ${label}${last4}${monthLabel ? ` - ${monthLabel}` : ''}`);
        // Check for existing expenses to replace
        if (res.existingExpenses?.length > 0) {
          setExistingExpenses(res.existingExpenses);
          // NAO pre-seleciona. Substituir APAGA as filhas da despesa escolhida, e o
          // backend so ORDENA os candidatos, nao filtra: qualquer fatura do mesmo
          // mes com status Pendente/Vencido entra na lista. Com Santander, Caixa e
          // Sicredi no mesmo mes (a base tem 13 rotulos de fatura), o default de
          // "ja marcado no primeiro" tornava a acao destrutiva sobre o cartao
          // errado a opcao de quem so clica em continuar.
          setSelectedExistingId(null);
        }
        await loadRules();
        setStep(2);
      } catch (err: any) {
        showToast(err.response?.data?.message || 'Erro ao analisar PDF', 'error');
      }
      setParsing(false);
    };

    // Deriva um padrão de regra a partir da descrição do cartão (remove valores/moeda
    // e pega o 1º token significativo do estabelecimento, sem dígitos no fim):
    // "Anthropic Anthropic Com Ca US$ 20,00" → "anthropic"; "Abastec Abastece Ai" →
    // "abastec"; "Bright Data Inc..." → "bright"; "Fixpay20 Tntinf" → "fixpay".
    const deriveRulePattern = (description: string): string => {
      const cleaned = String(description || '')
        .toLowerCase()
        .replace(/us\$|r\$|\d+[.,]\d+/g, ' ')
        .replace(/[^a-z0-9\s]/g, ' ')
        .trim();
      const tokens = cleaned.split(/\s+/).filter(Boolean);
      for (const tk of tokens) {
        const t = tk.replace(/\d+$/, '');
        if (t.length >= 3) return t;
      }
      return (tokens[0] || cleaned).slice(0, 30);
    };

    const handleMoveToGroup = async (tx: any, groupIdx: number) => {
      const group = editGroups[groupIdx];
      const pattern = deriveRulePattern(tx.description);
      // Pré-identifica: move JUNTO as demais não classificadas do MESMO
      // estabelecimento neste lote (ex.: 3× "Sakana Ai" vão de uma vez)
      const siblings = pattern
        ? editUnclassified.filter(t => t === tx || deriveRulePattern(t.description) === pattern)
        : [tx];
      const newUnc = editUnclassified.filter(t => !siblings.includes(t));
      const addTotal = siblings.reduce((s, t) => s + (t.isCredit ? -t.amount : t.amount), 0);
      const newGroups = [...editGroups];
      newGroups[groupIdx] = {
        ...newGroups[groupIdx],
        transactions: [...newGroups[groupIdx].transactions, ...siblings],
        total: Math.round((newGroups[groupIdx].total + addTotal) * 100) / 100,
      };
      setEditUnclassified(newUnc);
      setEditGroups(newGroups);
      if (siblings.length > 1) {
        showToast(`${siblings.length} transações de "${pattern}" movidas para ${group?.groupName}`, 'success');
      }
      await aprenderRegra(pattern, group?.groupName, group?.categoryId);
    };

    // APRENDE: cria uma regra persistente pra classificar esse estabelecimento
    // automaticamente nas próximas faturas (a menos que já exista uma igual).
    // Um caminho só para o Mover para e o Novo grupo.
    const aprenderRegra = async (pattern: string, groupName: string | undefined, categoryId?: number | null) => {
      const already = rules.some((r: any) => String(r.pattern || '').toLowerCase() === pattern && r.groupName === groupName);
      if (!pattern || !groupName || already) return;
      try {
        await expensesService.createCardStatementRule({
          pattern,
          groupName,
          categoryId: categoryId || undefined,
        });
        await loadRules();
        showToast(`Aprendido: "${pattern}" → ${groupName}. As próximas faturas classificam sozinhas.`, 'success');
      } catch {
        /* regra pode já existir / falhar — o move já foi aplicado, não bloqueia */
      }
    };

    const handleCreateGroupFromTx = async (tx: any, groupName: string, catId: number) => {
      const pattern = deriveRulePattern(tx.description);
      const siblings = pattern
        ? editUnclassified.filter(t => t === tx || deriveRulePattern(t.description) === pattern)
        : [tx];
      const newUnc = editUnclassified.filter(t => !siblings.includes(t));
      const newGroup = {
        groupName,
        categoryId: catId,
        supplierId: null,
        deductFromPartnerId: null,
        transactions: siblings,
        total: Math.round(siblings.reduce((s, t) => s + (t.isCredit ? -t.amount : t.amount), 0) * 100) / 100,
      };
      setEditUnclassified(newUnc);
      setEditGroups([...editGroups, newGroup]);
      if (siblings.length > 1) {
        showToast(`${siblings.length} transações de "${pattern}" agrupadas em ${groupName}`, 'success');
      }
      await aprenderRegra(pattern, groupName, catId);
    };

    const abrirNovoGrupo = (tx: any) => {
      setNovoGrupoPara(tx);
      setNovoGrupoNome('');
      setNovoGrupoCat('');
      setNovaCatNome('');
    };

    // Categoria nova no próprio form: grava e relê a lista (que o modal só recebia na abertura). Nome que já existe
    // escolhe a existente em vez de duplicar.
    const criarCategoriaNoGrupo = async () => {
      const nome = novaCatNome.trim();
      if (!nome) return;
      const existente = categories.find((c: any) => String(c.name || '').trim().toLowerCase() === nome.toLowerCase());
      if (existente) { setNovoGrupoCat(existente.id); return; }
      setCriandoCat(true);
      try {
        const criada = await expenseCategoriesService.create({ name: nome });
        const data = await expenseCategoriesService.getAll();
        setCategories(Array.isArray(data) ? data : []);
        setNovoGrupoCat(criada?.id ?? '');
        showToast(`Categoria ${nome} criada`, 'success');
      } catch (e: any) {
        showToast(e?.response?.data?.message || `Não consegui criar a categoria: ${mensagemDoErroDeCarga(e)}`, 'error');
      }
      setCriandoCat(false);
    };

    // Nome igual ao de um grupo que já está na revisão move para ele, em vez de abrir um segundo grupo com o mesmo nome.
    const criarGrupoNovo = async () => {
      const nome = novoGrupoNome.trim();
      if (!novoGrupoPara || !nome || typeof novoGrupoCat !== 'number') return;
      const tx = novoGrupoPara;
      setNovoGrupoPara(null);
      const idx = editGroups.findIndex((g: any) => String(g.groupName || '').trim().toLowerCase() === nome.toLowerCase());
      if (idx >= 0) await handleMoveToGroup(tx, idx);
      else await handleCreateGroupFromTx(tx, nome, novoGrupoCat);
    };

    const handleImport = async () => {
      if (!file || !parentCategoryId) return;
      setImporting(true);
      try {
        const data = {
          description,
          categoryId: parentCategoryId,
          bankAccountId: selectedBankAccountId || undefined,
          dueDate: editDueDate,
          referenceMonth: editRefMonth,
          existingExpenseId: selectedExistingId || undefined,
          groups: editGroups.map((g: any) => ({
            groupName: g.groupName,
            categoryId: g.categoryId || parentCategoryId,
            supplierId: g.supplierId || undefined,
            deductFromPartnerId: g.deductFromPartnerId || undefined,
            transactions: g.transactions.map((t: any) => ({
              description: t.description,
              amount: t.amount,
              date: t.date,
              installment: t.installment || undefined,
              isCredit: t.isCredit || false,
            })),
          })),
        };
        const res = await expensesService.importCardStatement(file, data);
        setImportResult(res);
        setStep(3);
        showToast(`Fatura ${res.replaced ? 'atualizada' : 'importada'}: ${res.transactionCount} transacoes em ${res.groupCount} grupos`);
        await loadExpenses();
        await loadBase();
      } catch (err: any) {
        showToast(err.response?.data?.message || 'Erro ao importar fatura', 'error');
      }
      setImporting(false);
    };

    const handleSaveRule = async () => {
      if (!newRulePattern || !newRuleGroup) return;
      try {
        // null (não '') para o backend conseguir LIMPAR o campo.
        const ident = {
          displayName: ruleIdent.displayName.trim() || null,
          cadence: ruleIdent.cadence || null,
          owner: ruleIdent.owner.trim() || null,
          amountMin: ruleIdent.amountMin !== '' ? Number(ruleIdent.amountMin) : null,
          amountMax: ruleIdent.amountMax !== '' ? Number(ruleIdent.amountMax) : null,
          notes: ruleIdent.notes.trim() || null,
        };
        if (editingRuleId) {
          await expensesService.updateCardStatementRule(editingRuleId, {
            pattern: newRulePattern,
            groupName: newRuleGroup,
            categoryId: newRuleCategoryId || undefined,
            ...ident,
          });
        } else {
          await expensesService.createCardStatementRule({
            pattern: newRulePattern,
            groupName: newRuleGroup,
            categoryId: newRuleCategoryId || undefined,
            ...ident,
          });
        }
        setNewRulePattern('');
        setNewRuleGroup('');
        setNewRuleCategoryId('');
        setRuleIdent(emptyRuleIdent);
        setEditingRuleId(null);
        await loadRules();
      } catch (err: any) {
        showToast(err.response?.data?.message || 'Erro ao salvar regra', 'error');
      }
    };

    const handleDeleteRule = async (id: number) => {
      try {
        await expensesService.deleteCardStatementRule(id);
        await loadRules();
      } catch (e) {
        showToast(`Não consegui excluir a regra: ${mensagemDoErroDeCarga(e)}`, 'error');
      }
    };

    const fmtCurrency = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

    const toggleGroup = (idx: number) => {
      const s = new Set(expandedGroups);
      s.has(idx) ? s.delete(idx) : s.add(idx);
      setExpandedGroups(s);
    };

    const totalGeral = editGroups.reduce((s: number, g: any) => s + g.total, 0)
      + editUnclassified.reduce((s: number, t: any) => s + (t.isCredit ? -t.amount : t.amount), 0);

    // O PDF declara o "Total desta Fatura". Se o que vai ser lançado não bater com ele,
    // a despesa nasce com valor diferente do que o banco vai debitar e a conciliação
    // fecha Parcial — foi o que aconteceu com os R$ 108,50 de encargos de ago/2026.
    const totalExtrato: number | undefined = parseResult?.statementTotal;
    const difExtrato = totalExtrato != null ? totalGeral - totalExtrato : 0;
    const divergeDoExtrato = totalExtrato != null && Math.abs(difExtrato) >= 0.01;

    return (
      // Assistente em 3 etapas: não fecha ao clicar fora (como já era). A largura
      // cresce na revisão, como antes; o rodapé muda por etapa; "Gerenciar Regras"
      // fica no cabeçalho, ao lado do ×.
      <CascaDeModal
        largura={step === 2 ? 'lg' : 'sm'}
        titulo={step === 1 ? 'Importar Fatura de Cartão' : step === 2 ? 'Revisar Transações' : 'Importação Concluída'}
        selos={step === 2 ? (
          <button onClick={() => setShowRules(!showRules)} className="text-sm text-slate-300 hover:text-white">
            {showRules ? 'Fechar Regras' : 'Gerenciar Regras'}
          </button>
        ) : undefined}
        onClose={onClose}
        corpoClassName="p-6 space-y-4"
        rodape={
          step === 1 ? (
            <>
              <button onClick={onClose} className="flex-1 px-4 py-2.5 border border-slate-600 text-slate-300 rounded-lg hover:bg-slate-700 font-medium">Cancelar</button>
              <button onClick={handleParse} disabled={!file || parsing} className="flex-1 px-4 py-2.5 bg-primary text-white rounded-lg hover:bg-primary-strong font-medium disabled:opacity-50">
                {parsing ? 'Analisando...' : 'Analisar'}
              </button>
            </>
          ) : step === 2 ? (
            <>
              <button onClick={() => setStep(1)} className="flex-1 px-4 py-2.5 border border-slate-600 text-slate-300 rounded-lg hover:bg-slate-700 font-medium">Voltar</button>
              <button onClick={handleImport} disabled={importing || !parentCategoryId || editGroups.length === 0} className="flex-1 px-4 py-2.5 bg-primary text-white rounded-lg hover:bg-primary-strong font-medium disabled:opacity-50">
                {importing ? 'Importando...' : `${selectedExistingId ? 'Substituir' : 'Importar'} ${editGroups.reduce((s: number, g: any) => s + g.transactions.length, 0)} transacoes`}
              </button>
            </>
          ) : (
            <button onClick={onClose} className="w-full px-4 py-2.5 bg-slate-700 text-white rounded-lg hover:bg-slate-600 font-medium">Fechar</button>
          )
        }
      >
            {/* Step 1: Upload */}
            {step === 1 && (
              <>
                <p className="text-slate-400 text-sm">
                  Envie o PDF da fatura do cartao de credito para classificar automaticamente as transacoes.
                </p>
                <div>
                  <label className="block text-sm text-slate-400 mb-1">Banco</label>
                  <select value={cardBank} onChange={e => setCardBank(e.target.value)} className={`w-full px-3 py-2 ${campo} rounded text-sm`}>
                    <option value="auto">Auto-detectar</option>
                    <option value="santander">Santander</option>
                    <option value="caixa">Caixa</option>
                    <option value="sicredi">Sicredi</option>
                  </select>
                </div>
                <div
                  className="rounded-lg border border-dashed border-transparent"
                  onDragOver={e => { e.preventDefault(); e.currentTarget.classList.add('border-primary'); }}
                  onDragLeave={e => { e.currentTarget.classList.remove('border-primary'); }}
                  onDrop={e => { e.preventDefault(); e.currentTarget.classList.remove('border-primary'); setFile(e.dataTransfer?.files?.[0] || null); }}
                >
                  <label className="block text-sm text-slate-400 mb-1">Arquivo PDF</label>
                  <input type="file" accept=".pdf" className="w-full text-sm text-slate-400 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-slate-700 file:text-slate-300 hover:file:bg-slate-600" onChange={e => setFile(e.target.files?.[0] || null)} />
                </div>
              </>
            )}

            {/* Step 2: Review */}
            {step === 2 && (
              <div className={`flex gap-4 ${showRules ? '' : ''}`}>
                <div className={`${showRules ? 'w-2/3' : 'w-full'} space-y-4`}>
                  {/* Existing expense replacement */}
                  {existingExpenses.length > 0 && (
                    <div className="bg-amber-900/30 border border-amber-700/50 rounded-lg p-3">
                      <label className="flex items-center gap-2 text-sm text-amber-300 mb-2">
                        <input type="checkbox" checked={selectedExistingId !== null} onChange={e => setSelectedExistingId(e.target.checked ? existingExpenses[0].id : null)} className="accent-amber-500" />
                        Substituir despesa existente <span className="text-amber-400/70">(apaga os lançamentos dela)</span>
                      </label>
                      {selectedExistingId !== null && (
                        <select value={selectedExistingId} onChange={e => setSelectedExistingId(+e.target.value)} className={`w-full px-3 py-1.5 ${campo} rounded text-sm`}>
                          {existingExpenses.map((e: any) => (
                            <option key={e.id} value={e.id}>{e.id} · {e.description} — R$ {e.amount.toLocaleString('pt-BR', { minimumFractionDigits: 2 })} ({e.status}{e.childCount != null ? `, ${e.childCount} lançamento${e.childCount === 1 ? '' : 's'}` : ''})</option>
                          ))}
                        </select>
                      )}
                      <p className="text-xs text-slate-400 mt-1">{selectedExistingId ? 'Os lançamentos da despesa selecionada serão APAGADOS e trocados pelos grupos abaixo. Confira o cartão.' : 'Será criada uma nova despesa pai.'}</p>
                      {avisoDaFaturaDoCartao(existingExpenses.length, selectedExistingId !== null) && (
                        <p className={`text-xs mt-1 font-medium ${TONE_TEXT.warning}`} data-testid="aviso-fatura-duplicada">{avisoDaFaturaDoCartao(existingExpenses.length, selectedExistingId !== null)}</p>
                      )}
                    </div>
                  )}
                  {/* Parent fields */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-slate-400 mb-1">Descricao do pai</label>
                      <input value={description} onChange={e => setDescription(e.target.value)} className={`w-full px-3 py-2 ${campo} rounded text-sm`} />
                    </div>
                    <div>
                      <label className="block text-xs text-slate-400 mb-1">Categoria pai</label>
                      <select value={parentCategoryId} onChange={e => setParentCategoryId(e.target.value ? +e.target.value : '')} className={`w-full px-3 py-2 ${campo} rounded text-sm`}>
                        <option value="">Selecione...</option>
                        {categories.map((c: any) => <option key={c.id} value={c.id}>{c.name}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-slate-400 mb-1">Vencimento</label>
                      <input type="date" value={editDueDate} onChange={e => setEditDueDate(e.target.value)} className={`w-full px-3 py-2 ${campo} rounded text-sm`} />
                    </div>
                    <div>
                      <label className="block text-xs text-slate-400 mb-1">Mes referencia</label>
                      <input type="month" value={editRefMonth} onChange={e => setEditRefMonth(e.target.value)} className={`w-full px-3 py-2 ${campo} rounded text-sm`} />
                    </div>
                    <div>
                      <label className="block text-xs text-slate-400 mb-1">Conta bancaria</label>
                      <select value={selectedBankAccountId} onChange={e => setSelectedBankAccountId(e.target.value ? +e.target.value : '')} className={`w-full px-3 py-2 ${campo} rounded text-sm`}>
                        <option value="">Nenhuma</option>
                        {bankAccounts.map((b: any) => <option key={b.id} value={b.id}>{b.bankName} - {b.accountNumber}</option>)}
                      </select>
                    </div>
                    <div className="flex items-end">
                      <div className={`rounded-lg px-3 py-2 w-full text-center ${divergeDoExtrato ? 'bg-red-500/15 border border-red-500/40' : 'bg-slate-700/50'}`}>
                        <span className="text-slate-400 text-xs">Total: </span>
                        <span className={`font-bold ${divergeDoExtrato ? 'text-red-300' : 'text-white'}`}>{fmtCurrency(totalGeral)}</span>
                        {totalExtrato != null && (
                          <div className="text-[11px] text-slate-400 mt-0.5">extrato: {fmtCurrency(totalExtrato)}</div>
                        )}
                      </div>
                    </div>
                  </div>

                  {divergeDoExtrato && (
                    <div className="bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2 text-xs text-red-200">
                      O que sera lancado esta {fmtCurrency(Math.abs(difExtrato))} {difExtrato > 0 ? 'ACIMA' : 'ABAIXO'} do
                      "Total desta Fatura" do PDF ({fmtCurrency(totalExtrato!)}). Importar assim faz o banco debitar um
                      valor diferente do lancado, e a conciliacao fecha Parcial. Confira se falta alguma linha antes de continuar.
                    </div>
                  )}

                  {/* Classified groups */}
                  <div className="space-y-2">
                    <h4 className="text-sm font-medium text-slate-300">Grupos ({editGroups.length})</h4>
                    {editGroups.map((g: any, idx: number) => (
                      <div key={idx} className="bg-slate-700/50 rounded-lg border border-slate-600">
                        <div className="flex items-center justify-between px-3 py-2 cursor-pointer" onClick={() => toggleGroup(idx)}>
                          <div className="flex items-center gap-2">
                            <Icon name={expandedGroups.has(idx) ? 'chevron-down' : 'chevron-right'} className="w-4 h-4 text-slate-400" />
                            <input
                              value={g.groupName}
                              onChange={e => {
                                const n = [...editGroups];
                                n[idx] = { ...n[idx], groupName: e.target.value };
                                setEditGroups(n);
                              }}
                              onClick={e => e.stopPropagation()}
                              className="bg-transparent text-white text-sm font-medium border-b border-transparent hover:border-slate-500 focus:border-primary outline-none px-1"
                            />
                          </div>
                          <div className="flex items-center gap-3">
                            <select
                              value={g.categoryId || ''}
                              onChange={e => {
                                const n = [...editGroups];
                                n[idx] = { ...n[idx], categoryId: e.target.value ? +e.target.value : null };
                                setEditGroups(n);
                              }}
                              onClick={e => e.stopPropagation()}
                              className="px-2 py-1 bg-slate-600 rounded border border-slate-500 text-white text-xs"
                            >
                              <option value="">Categoria...</option>
                              {categories.map((c: any) => <option key={c.id} value={c.id}>{c.name}</option>)}
                            </select>
                            <span className="text-xs text-slate-400">{g.transactions.length} itens</span>
                            <span className="text-sm font-medium text-white">{fmtCurrency(g.total)}</span>
                          </div>
                        </div>
                        {expandedGroups.has(idx) && (
                          <div className="border-t border-slate-600 px-3 py-2 space-y-1 max-h-48 overflow-y-auto">
                            {g.transactions.map((t: any, ti: number) => (
                              <div key={ti} className="flex justify-between text-xs text-slate-300">
                                <span className="truncate mr-2">{t.date.substring(8, 10)}/{t.date.substring(5, 7)} {t.description}</span>
                                <div className="flex items-center gap-2 shrink-0">
                                  {t.installment && <span className="px-1.5 py-0.5 rounded bg-slate-600/50 text-slate-400 font-mono text-[10px]">{t.installment}</span>}
                                  <span className={`whitespace-nowrap ${t.isCredit ? TONE_TEXT.success : ''}`}>{t.isCredit ? '-' : ''}{fmtCurrency(t.amount)}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Unclassified */}
                  {editUnclassified.length > 0 && (
                    <div className="space-y-2">
                      <h4 className="text-sm font-medium text-yellow-400">Nao classificadas ({editUnclassified.length})</h4>
                      <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 space-y-2 max-h-64 overflow-y-auto">
                        {editUnclassified.map((t: any, ti: number) => (
                          <React.Fragment key={`${t.fitId || ''}-${t.date}-${t.description}-${t.amount}-${ti}`}>
                          <div className="flex items-center justify-between text-xs">
                            <span className="text-slate-300 truncate mr-2">{t.date.substring(8, 10)}/{t.date.substring(5, 7)} {t.description}</span>
                            <div className="flex items-center gap-2 shrink-0">
                              {t.installment && <span className="px-1.5 py-0.5 rounded bg-slate-600/50 text-yellow-400 font-mono text-[10px]">{t.installment}</span>}
                              <span className="text-white">{fmtCurrency(t.amount)}</span>
                              <select
                                value=""
                                onChange={e => {
                                  const val = e.target.value;
                                  if (val === 'novo') abrirNovoGrupo(t);
                                  else if (val && val !== '') {
                                    handleMoveToGroup(t, +val);
                                  }
                                }}
                                className="px-1 py-0.5 bg-slate-600 rounded border border-slate-500 text-slate-300 text-xs"
                              >
                                <option value="">Mover para...</option>
                                {editGroups.map((g: any, gi: number) => <option key={gi} value={gi}>{g.groupName}</option>)}
                                <option value="novo">+ Novo grupo...</option>
                              </select>
                            </div>
                          </div>
                          {novoGrupoPara === t && (
                            <div data-novo-grupo className="flex flex-wrap items-center gap-2 bg-central-elevado/60 border border-central-borda rounded p-2 text-xs">
                              <input
                                autoFocus
                                value={novoGrupoNome}
                                onChange={e => setNovoGrupoNome(e.target.value)}
                                onKeyDown={e => { if (e.key === 'Enter') criarGrupoNovo(); }}
                                placeholder="Nome do grupo (ex: Correios)"
                                aria-label="Nome do grupo"
                                list="nomesDeGrupo"
                                className={`px-2 py-1 ${campo} rounded text-xs`}
                              />
                              <select
                                value={novoGrupoCat}
                                onChange={e => setNovoGrupoCat(e.target.value === 'nova' ? 'nova' : e.target.value ? +e.target.value : '')}
                                aria-label="Categoria do grupo"
                                className={`px-2 py-1 ${campo} rounded text-xs`}
                              >
                                <option value="">Categoria...</option>
                                {categories.map((c: any) => <option key={c.id} value={c.id}>{c.name}</option>)}
                                {opcaoAtualForaDaLista(categories, typeof novoGrupoCat === 'number' ? novoGrupoCat : null, novaCatNome.trim() || 'Categoria criada', false)}
                                <option value="nova">+ Nova categoria...</option>
                              </select>
                              {novoGrupoCat === 'nova' && (
                                <>
                                  <input
                                    value={novaCatNome}
                                    onChange={e => setNovaCatNome(e.target.value)}
                                    onKeyDown={e => { if (e.key === 'Enter') criarCategoriaNoGrupo(); }}
                                    placeholder="Nome da categoria"
                                    aria-label="Nome da categoria nova"
                                    className={`px-2 py-1 ${campo} rounded text-xs`}
                                  />
                                  <button onClick={criarCategoriaNoGrupo} disabled={!novaCatNome.trim() || criandoCat} className="px-2 py-1 bg-slate-600 text-white rounded hover:bg-slate-500 disabled:opacity-50">
                                    {criandoCat ? 'Criando...' : 'Criar categoria'}
                                  </button>
                                </>
                              )}
                              <button onClick={criarGrupoNovo} disabled={!novoGrupoNome.trim() || typeof novoGrupoCat !== 'number'} className="px-2 py-1 bg-primary text-white rounded hover:bg-primary-strong disabled:opacity-50">Criar</button>
                              <button onClick={() => setNovoGrupoPara(null)} className="px-2 py-1 text-slate-300 hover:text-white">Cancelar</button>
                            </div>
                          )}
                          </React.Fragment>
                        ))}
                        <datalist id="nomesDeGrupo">
                          {Array.from(new Set(rules.map((r: any) => r.groupName).filter(Boolean))).map(n => <option key={n} value={n} />)}
                        </datalist>
                      </div>
                    </div>
                  )}
                </div>

                {/* Rules panel */}
                {showRules && (
                  <div className="w-1/3 border-l border-slate-600 pl-4 space-y-3">
                    <h4 className="text-sm font-medium text-white">Regras de Classificacao</h4>
                    <div className="space-y-2">
                      <input value={newRulePattern} onChange={e => setNewRulePattern(e.target.value)} placeholder="Texto (ex: aws, uber)" className={`w-full px-2 py-1.5 ${campo} rounded text-xs`} />
                      <input value={newRuleGroup} onChange={e => setNewRuleGroup(e.target.value)} placeholder="Grupo (ex: Assinaturas)" className={`w-full px-2 py-1.5 ${campo} rounded text-xs`} list="groupNames" />
                      <datalist id="groupNames">
                        {Array.from(new Set(rules.map((r: any) => r.groupName))).map(n => <option key={n} value={n} />)}
                      </datalist>
                      <select value={newRuleCategoryId} onChange={e => setNewRuleCategoryId(e.target.value ? +e.target.value : '')} className={`w-full px-2 py-1.5 ${campo} rounded text-xs`}>
                        <option value="">Categoria (opcional)</option>
                        {categories.map((c: any) => <option key={c.id} value={c.id}>{c.name}</option>)}
                      </select>
                      <input value={ruleIdent.displayName} onChange={e => setRuleIdent({ ...ruleIdent, displayName: e.target.value })} placeholder="O que é (ex: Claude Max 20x)" className={`w-full px-2 py-1.5 ${campo} rounded text-xs`} />
                      <div className="grid grid-cols-2 gap-2">
                        <select value={ruleIdent.cadence} onChange={e => setRuleIdent({ ...ruleIdent, cadence: e.target.value })} className={`w-full px-2 py-1.5 ${campo} rounded text-xs`}>
                          <option value="">Cadência</option>
                          <option value="mensal">Mensal</option>
                          <option value="anual">Anual</option>
                          <option value="avulso">Avulso</option>
                        </select>
                        <input value={ruleIdent.owner} onChange={e => setRuleIdent({ ...ruleIdent, owner: e.target.value })} placeholder="Quem usa" list="ruleOwners" className={`w-full px-2 py-1.5 ${campo} rounded text-xs`} />
                        <datalist id="ruleOwners">
                          {Array.from(new Set(['Empresa', 'Fábio', 'Heitor', 'Camilla', 'Marketing', 'Dev', ...rules.map((r: any) => r.owner).filter(Boolean)])).map(o => <option key={o} value={o} />)}
                        </datalist>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <input type="number" step="0.01" value={ruleIdent.amountMin} onChange={e => setRuleIdent({ ...ruleIdent, amountMin: e.target.value })} placeholder="Valor mín. (opcional)" className={`w-full px-2 py-1.5 ${campo} rounded text-xs`} />
                        <input type="number" step="0.01" value={ruleIdent.amountMax} onChange={e => setRuleIdent({ ...ruleIdent, amountMax: e.target.value })} placeholder="Valor máx. (opcional)" className={`w-full px-2 py-1.5 ${campo} rounded text-xs`} />
                      </div>
                      <textarea value={ruleIdent.notes} onChange={e => setRuleIdent({ ...ruleIdent, notes: e.target.value })} placeholder="Observação (aviso, contexto)" rows={2} className={`w-full px-2 py-1.5 ${campo} rounded text-xs`} />
                      <button onClick={handleSaveRule} disabled={!newRulePattern || !newRuleGroup} className="w-full px-2 py-1.5 bg-primary text-white rounded text-xs hover:bg-primary-strong disabled:opacity-50">
                        {editingRuleId ? 'Atualizar' : '+ Nova Regra'}
                      </button>
                      {editingRuleId && (
                        <button onClick={() => { setEditingRuleId(null); setNewRulePattern(''); setNewRuleGroup(''); setNewRuleCategoryId(''); setRuleIdent(emptyRuleIdent); }} className="w-full px-2 py-1 text-slate-400 hover:text-white text-xs">Cancelar edição</button>
                      )}
                      <div className="flex gap-2 pt-1">
                        <button onClick={() => handleReapplyRules(true)} disabled={reapplying} title="Preenche nome, cadência e dono nos itens de fatura que ainda não têm" className="flex-1 px-2 py-1.5 bg-slate-700 text-slate-200 rounded text-xs hover:bg-slate-600 disabled:opacity-50">
                          {reapplying ? 'Aplicando...' : 'Aplicar nos itens sem identificação'}
                        </button>
                        <button onClick={() => handleReapplyRules(false)} disabled={reapplying} title="Sobrescreve a identificação de todos os itens que casarem com alguma regra" className="px-2 py-1.5 bg-slate-700 text-slate-400 rounded text-xs hover:bg-slate-600 disabled:opacity-50">
                          Todos
                        </button>
                      </div>
                    </div>
                    <div className="space-y-1 max-h-60 overflow-y-auto">
                      {rules.map((r: any) => (
                        <div key={r.id} className="flex items-center justify-between bg-slate-700/50 rounded px-2 py-1.5">
                          <div className="text-xs min-w-0">
                            <span className="text-white">{r.pattern}</span>
                            {(r.amountMin != null || r.amountMax != null) && (
                              <span className="text-slate-500 ml-1">[{r.amountMin != null ? Number(r.amountMin).toLocaleString('pt-BR') : '…'} a {r.amountMax != null ? Number(r.amountMax).toLocaleString('pt-BR') : '…'}]</span>
                            )}
                            <span className="text-slate-400 ml-1">→ {r.groupName}</span>
                            {(r.displayName || r.cadence || r.owner || r.notes) && (
                              <div className="flex items-center gap-1 flex-wrap mt-0.5">
                                {r.displayName && <span className="text-slate-300">{r.displayName}</span>}
                                <IdentChips expense={r} />
                                <NoteHint notes={r.notes} />
                              </div>
                            )}
                          </div>
                          <div className="flex gap-1 flex-none">
                            <button onClick={() => { setEditingRuleId(r.id); setNewRulePattern(r.pattern); setNewRuleGroup(r.groupName); setNewRuleCategoryId(r.categoryId || ''); setRuleIdent({ displayName: r.displayName || '', cadence: r.cadence || '', owner: r.owner || '', amountMin: r.amountMin != null ? String(r.amountMin) : '', amountMax: r.amountMax != null ? String(r.amountMax) : '', notes: r.notes || '' }); }} className="text-slate-300 hover:text-white text-xs">
                              <Icon name="edit" className="w-3 h-3" />
                            </button>
                            <button onClick={() => handleDeleteRule(r.id)} className="text-red-400 hover:text-red-300 text-xs">
                              <Icon name="trash" className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                      ))}
                      {rulesErro
                        ? <p role="alert" className={`text-xs ${TONE_TEXT.warning}`} data-testid="regras-erro">{textos.naoConseguiCarregar('as regras')} {rulesErro}.</p>
                        : rules.length === 0 && <p className="text-xs text-slate-500">Nenhuma regra cadastrada</p>}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Step 3: Done */}
            {step === 3 && importResult && (
              <>
                <div className="space-y-2">
                  <div className="flex justify-between bg-slate-700/50 rounded-lg px-3 py-2">
                    <span className="text-slate-400">Grupos criados</span>
                    <span className={`${TONE_TEXT.success} font-bold`}>{importResult.groupCount}</span>
                  </div>
                  <div className="flex justify-between bg-slate-700/50 rounded-lg px-3 py-2">
                    <span className="text-slate-400">Transacoes importadas</span>
                    <span className={`${TONE_TEXT.success} font-bold`}>{importResult.transactionCount}</span>
                  </div>
                  <div className="flex justify-between bg-slate-700/50 rounded-lg px-3 py-2">
                    <span className="text-slate-400">Total</span>
                    <span className="text-white font-bold">{fmtCurrency(totalGeral)}</span>
                  </div>
                </div>
              </>
            )}
      </CascaDeModal>
    );
  };

type ExpenseModalCtx = {
  showToast: (message: string, type?: 'success' | 'error') => void;
  loadExpenses: () => void; loadBase: () => void; loadRecurring: () => void;
  suppliers: any[]; setSuppliers: (v: any) => void;
  categories: any[]; setCategories: (v: any) => void;
  expenses: any[]; bankAccounts: any[]; clients: any[];
  periodFilter: any; currentMonthStr: string;
  attachmentTypeOptions: any; attachmentTypeLabels: any;
  clearSelection: () => void;
};

  const ExpenseForm = ({ onClose, expense, defaults , ctx }: { onClose: () => void; expense?: any; defaults?: any; ctx: ExpenseModalCtx }) => {
  const confirmarPerigo = useConfirmarPerigo();
  const { showToast, loadExpenses, loadBase, loadRecurring, suppliers, setSuppliers, categories, setCategories, expenses, bankAccounts, clients, periodFilter, currentMonthStr, attachmentTypeOptions, attachmentTypeLabels, clearSelection } = ctx;
    const d = defaults || {};
    const formDe = (e: any) => ({
      description: e?.description || '',
      categoryId: e?.categoryId || d.categoryId || '',
      supplierId: e?.supplierId || d.supplierId || '',
      bankAccountId: e?.bankAccountId || d.bankAccountId || '',
      amount: e?.amount || '',
      dueDate: e?.dueDate ? e.dueDate.split('T')[0] : '',
      paymentMethod: e?.paymentMethod || '',
      notes: e?.notes || '',
      displayName: e?.displayName || '',
      cadence: e?.cadence || '',
      owner: e?.owner || '',
      referenceMonth: e?.referenceMonth || d.referenceMonth || (periodFilter.months.length > 0 ? periodFilter.months[0] : currentMonthStr),
      clientId: e?.clientId || d.clientId || '',
      deductFromPartnerId: e?.deductFromPartnerId || '',
      installmentNumber: e?.installmentNumber || '',
      totalInstallments: e?.totalInstallments || '',
      parentExpenseId: e?.parentExpenseId || d.parentExpenseId || '',
      variableAmount: e?.variableAmount || false,
    });
    const [form, setForm] = useState(() => formDe(expense));
    // O formulário como abriu (09/10/2026): o Esc da casca só fecha direto se nada mudou; senão pergunta.
    const [formAoAbrir, setFormAoAbrir] = useState(form);
    // D3 (10/10/2026): a edição RELÊ a despesa ao abrir. Ela abria da cópia da lista, e o Salvar mandava o valor, o
    // vencimento e a conta dessa cópia: uma conciliação que tinha ajustado o previsto (juros, tarifa) voltava ao
    // valor velho. Enquanto lê, e se a leitura falha, o Salvar fica travado, com a frase à vista.
    const [atual, setAtual] = useState<any>(expense);
    const [leitura, setLeitura] = useState<{ estado: 'lendo' | 'ok' | 'falhou'; motivo?: string }>(expense?.id ? { estado: 'lendo' } : { estado: 'ok' });
    const formRef = useRef(form);
    formRef.current = form;
    useEffect(() => {
      if (!expense?.id) return;
      let vivo = true;
      const comoAbriu = formRef.current;
      expensesService.getById(expense.id).then(
        (fresca: any) => {
          if (!vivo) return;
          if (!fresca || Number(fresca.id) !== Number(expense.id)) {
            setLeitura({ estado: 'falhou', motivo: 'a resposta não trouxe esta despesa' });
            return;
          }
          setAtual(fresca);
          // Só troca o formulário se ninguém digitou ainda; o que foi digitado não some.
          if (JSON.stringify(formRef.current) === JSON.stringify(comoAbriu)) {
            const f = formDe(fresca);
            setForm(f);
            setFormAoAbrir(f);
          }
          setLeitura({ estado: 'ok' });
        },
        (e: any) => { if (vivo) setLeitura({ estado: 'falhou', motivo: mensagemDoErroDeCarga(e) }); },
      );
      return () => { vivo = false; };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [expense?.id]);
    const [saving, setSaving] = useState(false);
    const [siblings, setSiblings] = useState<any[]>([]);
    const [siblingsErro, setSiblingsErro] = useState<string | null>(null);
    const [selectedSiblingIds, setSelectedSiblingIds] = useState<Set<number>>(new Set());
    const [updateRecurringTemplate, setUpdateRecurringTemplate] = useState(true);
    // Replicar pros meses futuros (previsão editável). Default: até dezembro do ano da despesa.
    const baseRefMonth = (atual?.referenceMonth || (atual?.dueDate ? String(atual.dueDate).split('T')[0].slice(0, 7) : currentMonthStr));
    const [replicateUntil, setReplicateUntil] = useState(`${baseRefMonth.slice(0, 4)}-12`);
    const [replicating, setReplicating] = useState(false);
    const [showRecurrence, setShowRecurrence] = useState(false);
    const fmtRefMonth = (ym: string) => {
      const mn = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
      const [y, m] = (ym || '').split('-');
      const i = parseInt(m, 10) - 1;
      return i >= 0 && i < 12 ? `${mn[i]}/${y}` : ym;
    };
    // A replicação começa no mês seguinte ao da despesa.
    const nextRefMonth = (() => {
      const [y, m] = baseRefMonth.split('-').map(Number);
      const d = new Date(y, m, 1); // m (1-based) → próximo mês em índice 0-based
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    })();

    const handleReplicate = async () => {
      if (!replicateUntil || replicateUntil <= baseRefMonth) {
        showToast('Escolha um mês final depois do mês da despesa', 'error');
        return;
      }
      // Replicar SALVA o formulario antes de copiar (ver comentario abaixo), entao
      // quem abriu "Editar", mexeu no valor pra ver e clicou em Replicar gravava a
      // alteracao tambem no mes corrente, sem ter pedido. Agora o confirm diz isso.
      if (!window.confirm(
        `Replicar de ${fmtRefMonth(nextRefMonth)} até ${fmtRefMonth(replicateUntil)}?\n\n` +
        `As alterações abertas neste formulário serão salvas também em ${fmtRefMonth(baseRefMonth)}.`
      )) return;
      setReplicating(true);
      try {
        // Salva o que está no form ANTES de replicar — senão a réplica usa os valores
        // antigos do banco (ex.: 'valor variável' recém-marcado não ia pras cópias).
        await expensesService.update(expense.id, {
          ...form,
          categoryId: Number(form.categoryId),
          supplierId: form.supplierId ? Number(form.supplierId) : null,
          bankAccountId: form.bankAccountId ? Number(form.bankAccountId) : null,
          clientId: form.clientId ? Number(form.clientId) : null,
          deductFromPartnerId: form.deductFromPartnerId ? Number(form.deductFromPartnerId) : null,
          amount: parseFloat(String(form.amount)),
          paymentMethod: form.paymentMethod || null,
          installmentNumber: form.installmentNumber ? Number(form.installmentNumber) : null,
          totalInstallments: form.totalInstallments ? Number(form.totalInstallments) : null,
          parentExpenseId: form.parentExpenseId ? Number(form.parentExpenseId) : null,
          variableAmount: !!form.variableAmount,
          displayName: form.displayName?.trim() || null,
          cadence: form.cadence || null,
          owner: form.owner?.trim() || null,
        });
        const r = await expensesService.replicate(expense.id, replicateUntil);
        const parts: string[] = [];
        if (r.created > 0) parts.push(`${r.created} criada(s)`);
        if (r.updated > 0) parts.push(`${r.updated} atualizada(s)`);
        showToast(parts.length > 0
          ? `Replicado: ${parts.join(' + ')} em ${(r.months || []).length} mês(es)`
          : 'Nada a replicar (já estava tudo igual)', 'success');
        await loadExpenses();
        onClose();
      } catch (e: any) {
        showToast('Erro ao replicar: ' + (e?.response?.data?.message || e?.message || e), 'error');
      } finally {
        setReplicating(false);
      }
    };

    const [deletingFuture, setDeletingFuture] = useState(false);
    const handleDeleteWithFuture = async () => {
      if (!await confirmarPerigo(
        `Excluir "${expense.description}" e TODAS as ocorrências futuras (mesma descrição/categoria/fornecedor) a partir de ${fmtRefMonth(baseRefMonth)}?\n\n` +
        `As despesas já PAGAS são preservadas (histórico). Use para cancelamento de assinatura/recorrência.\n\nEsta ação não pode ser desfeita.`
      )) return;
      setDeletingFuture(true);
      try {
        const r = await expensesService.deleteWithFuture(expense.id);
        showToast(`Excluídas ${r.deleted} despesa(s) — ${(r.months || []).length} mês(es). Pagas preservadas.`, 'success');
        await loadExpenses();
        onClose();
      } catch (e: any) {
        showToast('Erro ao excluir: ' + (e?.response?.data?.message || e?.message || e), 'error');
      } finally {
        setDeletingFuture(false);
      }
    };

    const originalDueDate: string = atual?.dueDate ? String(atual.dueDate).split('T')[0] : '';
    const originalDay = originalDueDate ? parseInt(originalDueDate.split('-')[2], 10) : null;
    const newDay = form.dueDate ? parseInt(form.dueDate.split('-')[2], 10) : null;
    const dayChanged = !!expense?.recurrenceId && originalDay !== null && newDay !== null && originalDay !== newDay;

    useEffect(() => {
      if (!expense?.recurrenceId) return;
      let cancelled = false;
      (async () => {
        try {
          const data = await expensesService.getRecurringSiblings(expense.id);
          if (cancelled) return;
          const arr = Array.isArray(data) ? data : [];
          setSiblings(arr);
          setSiblingsErro(null);
          setSelectedSiblingIds(new Set(arr.map((s: any) => s.id)));
        } catch (e) {
          // Calava: o painel "aplicar às parcelas" sumia e o Salvar mudava só esta (e o modelo), sem dizer.
          if (!cancelled) { setSiblings([]); setSiblingsErro(mensagemDoErroDeCarga(e)); }
        }
      })();
      return () => { cancelled = true; };
    }, [expense?.id, expense?.recurrenceId]);

    const toggleSibling = (id: number) => {
      const next = new Set(selectedSiblingIds);
      if (next.has(id)) next.delete(id); else next.add(id);
      setSelectedSiblingIds(next);
    };
    const toggleAllSiblings = () => {
      if (selectedSiblingIds.size === siblings.length) setSelectedSiblingIds(new Set());
      else setSelectedSiblingIds(new Set(siblings.map(s => s.id)));
    };

    const handleSubmit = async (e: React.FormEvent) => {
      e.preventDefault();
      if (leitura.estado !== 'ok') return;
      if (!form.description || !form.categoryId || !form.amount || !form.dueDate) {
        showToast('Preencha os campos obrigatórios', 'error');
        return;
      }
      setSaving(true);
      try {
        const payload = {
          ...form,
          categoryId: Number(form.categoryId),
          supplierId: form.supplierId ? Number(form.supplierId) : null,
          bankAccountId: form.bankAccountId ? Number(form.bankAccountId) : null,
          clientId: form.clientId ? Number(form.clientId) : null,
          deductFromPartnerId: form.deductFromPartnerId ? Number(form.deductFromPartnerId) : null,
          amount: parseFloat(String(form.amount)),
          // null, nunca '': o @IsOptional do backend só ignora null/undefined,
          // então string vazia cai no @IsEnum e o PUT/POST devolve 400 (salvar
          // despesa sem forma de pagamento estava quebrado). null em vez de
          // undefined é o que permite LIMPAR o campo.
          paymentMethod: form.paymentMethod || null,
          installmentNumber: form.installmentNumber ? Number(form.installmentNumber) : null,
          totalInstallments: form.totalInstallments ? Number(form.totalInstallments) : null,
          parentExpenseId: form.parentExpenseId ? Number(form.parentExpenseId) : null,
          variableAmount: !!form.variableAmount,
          displayName: form.displayName?.trim() || null,
          cadence: form.cadence || null,
          owner: form.owner?.trim() || null,
        };
        if (expense) {
          await expensesService.update(expense.id, payload);
          if (dayChanged && newDay !== null && (selectedSiblingIds.size > 0 || updateRecurringTemplate)) {
            const res = await expensesService.applyDueDateShift(expense.id, {
              targetIds: Array.from(selectedSiblingIds),
              newDay,
              updateRecurringTemplate,
            });
            const parts = [`Despesa atualizada`];
            if (res?.updated) parts.push(`${res.updated} parcela${res.updated > 1 ? 's' : ''} ajustada${res.updated > 1 ? 's' : ''}`);
            if (res?.templateUpdated) parts.push('recorrência atualizada');
            showToast(parts.join(' · '));
          } else {
            showToast('Despesa atualizada');
          }
        } else {
          await expensesService.create(payload);
          showToast('Despesa criada');
        }
        await loadExpenses();
        onClose();
      } catch (err: any) {
        showToast(err.response?.data?.message || 'Erro ao salvar despesa', 'error');
      }
      setSaving(false);
    };

    return (
      // Editor: não fecha ao clicar fora (como já era). Quem é a despesa vai na linha
      // mono e nos selos (status e categoria); o corpo rola, cabeça e rodapé não.
      <CascaDeModal
        largura="md"
        temEdicaoNaoSalva={() => JSON.stringify(form) !== JSON.stringify(formAoAbrir)}
        titulo={expense ? 'Editar Despesa' : d.parentExpenseId ? 'Nova Despesa Filho' : 'Nova Despesa'}
        subtitulo={expense ? [
          `#${expense.id}`,
          expense.parentExpense?.description ? `dentro de ${expense.parentExpense.description}` : null,
          expense.recurrenceId ? 'recorrente' : null,
        ].filter(Boolean).join(' · ') : undefined}
        selos={expense ? (
          <>
            {expense.status && <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${statusChipClass[expense.status] || statusChipClass.Cancelado}`}>{expense.status}</span>}
            {expense.category?.name && (
              <span className="inline-flex items-center gap-1 text-xs text-slate-400">
                <span className="w-1.5 h-1.5 rounded-full inline-block" style={{ backgroundColor: expense.category.color || '#f97316' }} />
                {expense.category.name}
              </span>
            )}
          </>
        ) : undefined}
        onClose={onClose}
        corpoClassName="px-6 py-5"
        rodape={
          <div className="ml-auto flex items-center gap-3">
            <button type="button" onClick={onClose} className="px-4 py-2.5 border border-slate-600 text-slate-300 rounded-lg hover:bg-slate-700 font-medium">Cancelar</button>
            <button type="submit" form="expense-form" disabled={saving || leitura.estado !== 'ok'} className="px-5 py-2.5 bg-primary text-white rounded-lg hover:bg-primary-strong font-medium disabled:opacity-50">
              {saving ? 'Salvando...' : (expense ? 'Salvar alterações' : 'Criar despesa')}
            </button>
          </div>
        }
      >
          <form id="expense-form" onSubmit={handleSubmit} className="space-y-4">
            {leitura.estado !== 'ok' && (
              <div role={leitura.estado === 'falhou' ? 'alert' : 'status'} data-testid="leitura-da-despesa" className={`text-sm ${leitura.estado === 'falhou' ? TONE_TEXT.warning : 'text-slate-400'}`}>
                {leitura.estado === 'lendo'
                  ? 'Lendo a despesa atual…'
                  : `Não consegui ler a despesa atual: ${leitura.motivo}. O Salvar está travado para não gravar a cópia da lista por cima.`}
              </div>
            )}
            <FormSection title="Lançamento">
              <div>
                <label className={LABEL}>Descrição *</label>
                <input className={INPUT} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={LABEL}>Categoria *</label>
                  <select className={INPUT} value={form.categoryId} onChange={e => setForm({ ...form, categoryId: e.target.value })}>
                    <option value="">Selecione</option>
                    {categories.filter(c => c.isActive).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className={LABEL}>Fornecedor</label>
                  <select className={INPUT} value={form.supplierId} onChange={e => {
                  const sid = e.target.value;
                  const sup = suppliers.find((s: any) => String(s.id) === sid);
                  setForm(prev => ({
                    ...prev,
                    supplierId: sid,
                    categoryId: (!prev.categoryId && sup?.defaultCategoryId) ? String(sup.defaultCategoryId) : prev.categoryId,
                  }));
                }}>
                  <option value="">Nenhum</option>
                  {suppliers.filter(s => s.isActive !== false).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={LABEL}>Valor (R$) *</label>
                  <input type="number" step="0.01" min="0" className={`${INPUT} text-lg font-semibold tabular-nums`} value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} />
                </div>
                <div>
                  <label className={LABEL}>Vencimento *</label>
                  <input type="date" className={INPUT} value={form.dueDate} onChange={e => setForm({ ...form, dueDate: e.target.value })} />
                </div>
              </div>
              <label className="flex items-start gap-2 cursor-pointer">
                <input type="checkbox" checked={!!form.variableAmount} onChange={e => setForm({ ...form, variableAmount: e.target.checked })} className="accent-primary mt-0.5" />
                <span className="text-sm text-slate-300">
                  Valor variável (o valor é só previsão)
                  <span className="block text-xs text-slate-500 mt-0.5">Ao pagar ou conciliar, o valor é ajustado para o real e marcado como Pago, sem saldo parcial. Impostos, tarifas por uso, comissões.</span>
                </span>
              </label>
            </FormSection>

            <FormSection title="Pagamento">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={LABEL}>Conta bancária</label>
                  <select className={INPUT} value={form.bankAccountId} onChange={e => setForm({ ...form, bankAccountId: e.target.value })}>
                    <option value="">Nenhuma</option>
                    {bankAccounts.filter(b => b.isActive !== false).map(b => <option key={b.id} value={b.id}>{b.bankName} - {b.accountNumber}</option>)}
                  </select>
                </div>
                <div>
                  <label className={LABEL}>Método de pagamento</label>
                  <select className={INPUT} value={form.paymentMethod} onChange={e => setForm({ ...form, paymentMethod: e.target.value })}>
                    <option value="">Selecione</option>
                    {Object.entries(paymentMethodLabels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className={LABEL}>Mês de referência</label>
                  <input type="month" className={INPUT} value={form.referenceMonth} onChange={e => setForm({ ...form, referenceMonth: e.target.value })} />
                </div>
                <div>
                  <label className={LABEL}>Parcela atual</label>
                  <input type="number" min="1" className={INPUT} placeholder="Ex: 3" value={form.installmentNumber} onChange={e => setForm({ ...form, installmentNumber: e.target.value })} />
                </div>
                <div>
                  <label className={LABEL}>Total de parcelas</label>
                  <input type="number" min="1" className={INPUT} placeholder="Ex: 12" value={form.totalInstallments} onChange={e => setForm({ ...form, totalInstallments: e.target.value })} />
                </div>
              </div>
            </FormSection>

            <FormSection title="Vínculos">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={LABEL}>Cliente associado</label>
                  <select className={INPUT} value={form.clientId} onChange={e => setForm({ ...form, clientId: e.target.value })}>
                    <option value="">Nenhum</option>
                    {clients.map(c => <option key={c.id} value={c.id}>{nomeDeExibicao(c)}</option>)}
                  </select>
                </div>
                <div>
                  <label className={LABEL}>Deduzir do sócio</label>
                  <select className={INPUT} value={form.deductFromPartnerId} onChange={e => setForm({ ...form, deductFromPartnerId: e.target.value })}>
                    <option value="">Nenhum</option>
                    {suppliers.filter(s => s.isActive !== false).map(s => <option key={s.id} value={s.id}>{s.tradeName || s.name}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className={LABEL}>Despesa pai (agrupar dentro de)</label>
                <select className={INPUT} value={form.parentExpenseId} onChange={e => setForm({ ...form, parentExpenseId: e.target.value })}>
                <option value="">Nenhuma (nível principal)</option>
                {expenses.filter((ex: any) => ex.id !== expense?.id).map((ex: any) => <option key={ex.id} value={ex.id} disabled={ex.status === 'Pago'}>{ex.description} (R$ {Number(ex.amount).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}) {ex.status === 'Pago' ? '- PAGO (bloqueado)' : `(${ex.status})`}</option>)}
              </select>
                {form.parentExpenseId && expenses.find((ex: any) => String(ex.id) === String(form.parentExpenseId) && ex.status === 'Pago') && (
                <p className="text-xs text-red-400 mt-1">Despesa-pai ja esta paga/conciliada. Desvincule o pagamento antes de adicionar itens.</p>
              )}
              </div>
            </FormSection>

            <FormSection title="Identificação" hint="como aparece na lista">
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className={LABEL}>O que é</label>
                  <input type="text" className={INPUT} placeholder="Ex: Claude Max 20x" value={form.displayName} onChange={e => setForm({ ...form, displayName: e.target.value })} />
                </div>
                <div>
                  <label className={LABEL}>Cadência</label>
                  <select className={INPUT} value={form.cadence} onChange={e => setForm({ ...form, cadence: e.target.value })}>
                    <option value="">Não informada</option>
                    <option value="mensal">Mensal</option>
                    <option value="anual">Anual</option>
                    <option value="avulso">Avulso</option>
                  </select>
                </div>
                <div>
                  <label className={LABEL}>Quem usa</label>
                  <input type="text" className={INPUT} placeholder="Empresa, Fábio..." list="expenseOwners" value={form.owner} onChange={e => setForm({ ...form, owner: e.target.value })} />
                  <datalist id="expenseOwners">
                    {['Empresa', 'Fábio', 'Heitor', 'Camilla', 'Marketing', 'Dev'].map(o => <option key={o} value={o} />)}
                  </datalist>
                </div>
              </div>
              <div>
                <label className={LABEL}>Observações</label>
                <textarea className={INPUT} rows={2} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />
              </div>
            </FormSection>

            {dayChanged && siblingsErro && (
              <FormSection title="Dia do vencimento mudou" tone="warning">
                <div className={`text-sm ${TONE_TEXT.warning}`} data-testid="parcelas-irmas-erro">
                  Não consegui ler as outras parcelas desta recorrência: {siblingsErro}. Salvar muda só esta despesa
                  {updateRecurringTemplate ? ' e o modelo da recorrência' : ''}; as outras parcelas ficam no dia de antes.
                </div>
                <label className="flex items-center gap-2 text-xs text-slate-300 pt-1">
                  <input type="checkbox" checked={updateRecurringTemplate} onChange={e => setUpdateRecurringTemplate(e.target.checked)} />
                  Atualizar também o modelo da recorrência
                </label>
              </FormSection>
            )}
            {dayChanged && siblings.length > 0 && (
              <FormSection title="Dia do vencimento mudou" tone="warning">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className={`text-sm font-medium ${TONE_TEXT.warning}`}>
                      Aplicar novo dia ({String(newDay).padStart(2, '0')}) a outras parcelas desta recorrência?
                    </div>
                    <div className="text-xs text-slate-400 mt-0.5">
                      O mês de cada parcela é preservado — só o dia do vencimento muda.
                    </div>
                  </div>
                  <button type="button" onClick={toggleAllSiblings} className="text-xs text-slate-300 hover:text-white whitespace-nowrap">
                    {selectedSiblingIds.size === siblings.length ? 'Desmarcar tudo' : 'Marcar tudo'}
                  </button>
                </div>
                <div className="max-h-40 overflow-y-auto space-y-1 pr-1">
                  {siblings.map((s: any) => {
                    const checked = selectedSiblingIds.has(s.id);
                    const dueStr = String(s.dueDate).split('T')[0];
                    return (
                      <label key={s.id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-700/40 cursor-pointer">
                        <input type="checkbox" checked={checked} onChange={() => toggleSibling(s.id)} className="accent-primary" />
                        <span className="text-xs text-slate-300 flex-1">
                          {fmtDate(dueStr)} · R$ {Number(s.amount).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                        </span>
                        <span className={`text-xs px-1.5 py-0.5 rounded ${s.status === 'Vencido' ? 'bg-red-500/20 text-red-300' : 'bg-slate-600/40 text-slate-300'}`}>
                          {s.status}
                        </span>
                      </label>
                    );
                  })}
                </div>
                <label className="flex items-center gap-2 text-xs text-slate-300 pt-1 border-t border-central-borda/50">
                  <input type="checkbox" checked={updateRecurringTemplate} onChange={e => setUpdateRecurringTemplate(e.target.checked)} className="accent-primary" />
                  Atualizar também o dia padrão da recorrência (afeta gerações futuras)
                </label>
              </FormSection>
            )}

            {expense && (
              <FormSection
                title="Recorrência"
                hint={showRecurrence ? undefined : 'replicar para os próximos meses ou cancelar'}
                action={<button type="button" onClick={() => setShowRecurrence(v => !v)} className="text-xs text-slate-400 hover:text-white">{showRecurrence ? 'Recolher' : 'Abrir'}</button>}
              >
                {showRecurrence && (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <div className="text-sm font-medium text-slate-200">Replicar para os próximos meses</div>
                      <p className="text-xs text-slate-500">De {fmtRefMonth(nextRefMonth)} até o mês escolhido. Onde a despesa já existir (mesma descrição, categoria, fornecedor e cliente) o valor é atualizado; onde não existir, é criada. Pagas não são tocadas.</p>
                      <div className="flex items-end gap-2">
                        <div className="flex-1">
                          <label className={LABEL}>Até o mês</label>
                          <input type="month" min={nextRefMonth} value={replicateUntil} onChange={e => setReplicateUntil(e.target.value)} className={INPUT} />
                        </div>
                        <button type="button" onClick={handleReplicate} disabled={replicating} className="px-4 py-2 border border-slate-600 text-slate-200 rounded-lg hover:bg-slate-700 font-medium disabled:opacity-50 whitespace-nowrap">
                          {replicating ? 'Replicando...' : 'Replicar'}
                        </button>
                      </div>
                    </div>
                    <div className="space-y-2 border-l border-central-borda pl-4">
                      <div className="text-sm font-medium text-red-300">Cancelar recorrência</div>
                      <p className="text-xs text-slate-500">Exclui esta despesa e as ocorrências futuras (mesma descrição, categoria e fornecedor) a partir de {fmtRefMonth(baseRefMonth)}. As já pagas ficam. É o caminho para assinatura cancelada.</p>
                      <button type="button" onClick={handleDeleteWithFuture} disabled={deletingFuture} className="px-4 py-2 border border-red-600/60 text-red-300 rounded-lg hover:bg-red-500/10 font-medium disabled:opacity-50">
                        {deletingFuture ? 'Excluindo...' : 'Excluir esta e as futuras'}
                      </button>
                    </div>
                  </div>
                )}
              </FormSection>
            )}
          </form>
      </CascaDeModal>
    );
  };

  const PayModal = ({ expense, onClose , ctx }: { expense: any; onClose: () => void; ctx: ExpenseModalCtx }) => {
  const confirmarPerigo = useConfirmarPerigo();
  const { showToast, loadExpenses, loadBase, loadRecurring, suppliers, setSuppliers, categories, setCategories, expenses, bankAccounts, clients, periodFilter, currentMonthStr, attachmentTypeOptions, attachmentTypeLabels, clearSelection } = ctx;
    const [mode, setMode] = useState<'full' | 'partial' | 'history'>('full');
    const [paymentDate, setPaymentDate] = useState(getTodayStr());
    const [paymentMethod, setPaymentMethod] = useState(expense.paymentMethod || '');
    const [amount, setAmount] = useState('');
    const [notes, setNotes] = useState('');
    const [saving, setSaving] = useState(false);
    const [payments, setPayments] = useState<any[]>([]);
    // Fatura de cartao = a cascata do backend age aqui (markCardChildrenAsPaid).
    const isCardInvoice = /cart[aã]o\s+de\s+cr[eé]dito/i.test(expense.category?.name || '');
    const childCount = Number(expense.childCount || 0);
    const [paymentsLoading, setPaymentsLoading] = useState(false);
    const [paymentsErro, setPaymentsErro] = useState<string | null>(null);

    const expAmount = parseFloat(expense.amount) || 0;
    const paidAmount = parseFloat(expense.paidAmount) || 0;
    const remaining = Math.max(0, expAmount - paidAmount);

    useEffect(() => {
      if (mode === 'history') loadPayments();
    }, [mode]);

    const loadPayments = async () => {
      setPaymentsLoading(true);
      try {
        const data = await expensesService.getPayments(expense.id);
        setPayments(Array.isArray(data) ? data : []);
        setPaymentsErro(null);
      } catch (e) {
        // Calava: "Nenhum pagamento registrado" com a API fora.
        setPayments([]);
        setPaymentsErro(mensagemDoErroDeCarga(e));
      }
      setPaymentsLoading(false);
    };

    const handlePayFull = async () => {
      setSaving(true);
      try {
        await expensesService.pay(expense.id, { paymentDate, paymentMethod: paymentMethod || undefined });
        showToast('Despesa marcada como paga');
        await loadExpenses();
        onClose();
      } catch (err: any) {
        showToast(err.response?.data?.message || 'Erro ao pagar', 'error');
      }
      setSaving(false);
    };

    const handlePayPartial = async () => {
      const val = parseFloat(amount);
      if (!val || val <= 0) { showToast('Informe o valor do pagamento', 'error'); return; }
      if (val > remaining + 0.01) { showToast(`Valor excede o restante (R$ ${fmt(remaining)})`, 'error'); return; }
      setSaving(true);
      try {
        await expensesService.addPayment(expense.id, {
          amount: val,
          paymentDate,
          paymentMethod: paymentMethod || undefined,
          notes: notes || undefined,
        });
        showToast(val >= remaining - 0.01 ? 'Despesa totalmente paga' : 'Pagamento parcial registrado');
        await loadExpenses();
        onClose();
      } catch (err: any) {
        showToast(err.response?.data?.message || 'Erro ao registrar pagamento', 'error');
      }
      setSaving(false);
    };

    const handleRemovePayment = async (paymentId: number) => {
      if (!await confirmarPerigo('Remover este pagamento?\n\nA despesa volta a Parcial, se houver outro pagamento, ou a Pendente ou Vencida, conforme o vencimento.')) return;
      try {
        await expensesService.removePayment(paymentId);
        showToast('Pagamento removido');
        await loadPayments();
        await loadExpenses();
      } catch (err: any) {
        showToast(err.response?.data?.message || 'Erro ao remover', 'error');
      }
    };

    const pctPaid = expAmount > 0 ? Math.min(100, (paidAmount / expAmount) * 100) : 0;

    return (
      // Valores digitados: não fecha ao clicar fora (como já era). Os três modos são
      // as abas da casca; Total/Pago/Restante, a faixa; o rodapé muda por modo.
      <CascaDeModal
        largura="sm"
        titulo="Pagamento"
        subtitulo={expense.description}
        onClose={onClose}
        faixa={[
          { k: 'Total', v: `R$ ${fmt(expAmount)}` },
          ...(paidAmount > 0 ? [
            { k: 'Pago', v: `R$ ${fmt(paidAmount)}`, cls: TONE_TEXT.open },
            { k: 'Restante', v: `R$ ${fmt(remaining)}`, cls: TONE_TEXT.warning },
          ] : []),
        ]}
        abas={[
          { chave: 'full', rotulo: 'Pagar Total' },
          { chave: 'partial', rotulo: 'Parcial' },
          { chave: 'history', rotulo: 'Histórico' },
        ]}
        abaAtiva={mode}
        onAba={(k) => setMode(k as any)}
        corpoClassName="p-6 space-y-4"
        rodape={
          mode === 'full' ? (
            <>
              <button onClick={onClose} className="flex-1 px-4 py-2.5 border border-slate-600 text-slate-300 rounded-lg hover:bg-slate-700 font-medium">Cancelar</button>
              <button onClick={handlePayFull} disabled={saving} className="flex-1 px-4 py-2.5 bg-primary text-white rounded-lg hover:bg-primary-strong font-medium disabled:opacity-50">
                {saving ? 'Salvando...' : `Pagar R$ ${fmt(expAmount)}${isCardInvoice && childCount > 0 ? ` e ${childCount} item${childCount === 1 ? '' : 's'}` : ''}`}
              </button>
            </>
          ) : mode === 'partial' ? (
            <>
              <button onClick={onClose} className="flex-1 px-4 py-2.5 border border-slate-600 text-slate-300 rounded-lg hover:bg-slate-700 font-medium">Cancelar</button>
              <button onClick={handlePayPartial} disabled={saving || !amount} className="flex-1 px-4 py-2.5 bg-primary text-white rounded-lg hover:bg-primary-strong font-medium disabled:opacity-50">
                {saving ? 'Salvando...' : `Registrar R$ ${amount ? fmt(parseFloat(amount)) : '0,00'}`}
              </button>
            </>
          ) : (
            <button onClick={onClose} className="flex-1 px-4 py-2.5 border border-slate-600 text-slate-300 rounded-lg hover:bg-slate-700 font-medium">Fechar</button>
          )
        }
      >
            {paidAmount > 0 && (
              <div>
                <div className="bg-slate-600 rounded-full h-2.5">
                  <div className={`${TONE_TEXT.success} bg-current h-2.5 rounded-full transition-all`} style={{ width: `${pctPaid}%` }} />
                </div>
                <p className="text-xs text-slate-400 mt-1 text-right">{pctPaid.toFixed(0)}% pago</p>
              </div>
            )}

            {mode === 'full' && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-slate-400 mb-1">Data do Pagamento *</label>
                  <input type="date" className={`w-full px-3 py-2 ${campo} rounded`} value={paymentDate} onChange={e => setPaymentDate(e.target.value)} />
                </div>
                <div>
                  <label className="block text-sm text-slate-400 mb-1">Metodo de Pagamento</label>
                  <select className={`w-full px-3 py-2 ${campo} rounded`} value={paymentMethod} onChange={e => setPaymentMethod(e.target.value)}>
                    <option value="">Selecione</option>
                    {Object.entries(paymentMethodLabels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </div>
                {/* Pagar a FATURA de cartao cascateia: o backend marca todas as
                    compras (filhas) e rateios (netas) como Pago junto. O botao
                    prometia pagar uma despesa e mexia em dezenas, sem dizer. */}
                {isCardInvoice && childCount > 0 && (
                  <div className={`border rounded-lg p-3 text-xs ${TONE_SURFACE.warning} ${TONE_BORDER.warning} ${TONE_TEXT.warning}`}>
                    Ao pagar esta fatura, {childCount} lançamento{childCount === 1 ? '' : 's'} dentro dela {childCount === 1 ? 'será marcado' : 'serão marcados'} como Pago.
                  </div>
                )}
              </div>
            )}

            {mode === 'partial' && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-slate-400 mb-1">Valor do Pagamento * (restante: R$ {fmt(remaining)})</label>
                  <input type="number" step="0.01" min="0.01" max={remaining} className={`w-full px-3 py-2 ${campo} rounded`} placeholder={`Max: ${fmt(remaining)}`} value={amount} onChange={e => setAmount(e.target.value)} />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-slate-400 mb-1">Data *</label>
                    <input type="date" className={`w-full px-3 py-2 ${campo} rounded`} value={paymentDate} onChange={e => setPaymentDate(e.target.value)} />
                  </div>
                  <div>
                    <label className="block text-sm text-slate-400 mb-1">Metodo</label>
                    <select className={`w-full px-3 py-2 ${campo} rounded`} value={paymentMethod} onChange={e => setPaymentMethod(e.target.value)}>
                      <option value="">Selecione</option>
                      {Object.entries(paymentMethodLabels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-slate-400 mb-1">Observacao</label>
                  <input type="text" className={`w-full px-3 py-2 ${campo} rounded`} placeholder="Ex: PIX parcial ref. fevereiro" value={notes} onChange={e => setNotes(e.target.value)} />
                </div>
              </div>
            )}

            {mode === 'history' && (
              <div className="space-y-3">
                {paymentsLoading ? (
                  <div className="text-center py-8 text-slate-400">Carregando...</div>
                ) : paymentsErro ? (
                  <div role="alert" className={`text-center py-8 text-sm ${TONE_TEXT.warning}`} data-testid="pagamentos-erro">{textos.naoConseguiCarregar('os pagamentos')} {paymentsErro}.</div>
                ) : payments.length === 0 ? (
                  <div className="text-center py-8 text-slate-400">Nenhum pagamento registrado</div>
                ) : (
                  <div className="space-y-2">
                    {payments.map((p: any) => (
                      <div key={p.id} className="flex items-center justify-between bg-slate-700/50 rounded-lg p-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className={`${TONE_TEXT.success} font-medium`}>R$ {fmt(p.amount)}</span>
                            <span className="text-slate-500 text-xs">{fmtDate(p.paymentDate)}</span>
                            {p.paymentMethod && <span className="px-1.5 py-0.5 text-xs rounded bg-slate-600 text-slate-300">{paymentMethodLabels[p.paymentMethod] || p.paymentMethod}</span>}
                          </div>
                          {p.notes && <p className="text-xs text-slate-400 mt-1">{p.notes}</p>}
                          {p.bankTransaction && <p className="text-xs text-slate-400 mt-0.5">Transacao: {p.bankTransaction.description}</p>}
                        </div>
                        <button onClick={() => handleRemovePayment(p.id)} className="p-1.5 text-slate-400 hover:text-red-400 hover:bg-red-500/10 rounded" title="Remover">
                          <Icon name="trash" className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
      </CascaDeModal>
    );
  };

  const ReceiptModal = ({ expense, onClose , ctx }: { expense: any; onClose: () => void; ctx: ExpenseModalCtx }) => {
  const confirmarPerigo = useConfirmarPerigo();
  const { showToast, loadExpenses, loadBase, loadRecurring, suppliers, setSuppliers, categories, setCategories, expenses, bankAccounts, clients, periodFilter, currentMonthStr, attachmentTypeOptions, attachmentTypeLabels, clearSelection } = ctx;
    const [file, setFile] = useState<File | null>(null);
    const [uploading, setUploading] = useState(false);
    const [attachments, setAttachments] = useState<any[]>([]);
    const [loadingAttachments, setLoadingAttachments] = useState(true);
    const [selectedType, setSelectedType] = useState('comprovante');
    const [description, setDescription] = useState('');
    const [deletingId, setDeletingId] = useState<number | null>(null);
    const [editingTypeId, setEditingTypeId] = useState<number | null>(null);

    const [attachmentsErro, setAttachmentsErro] = useState<string | null>(null);
    const loadAttachments = useCallback(async () => {
      try {
        const data = await expensesService.getAttachments(expense.id);
        setAttachments(data);
        setAttachmentsErro(null);
      } catch (e) {
        // Calava: sem anexos na tela, ao lado do upload.
        setAttachmentsErro(mensagemDoErroDeCarga(e));
      }
      setLoadingAttachments(false);
    }, [expense.id]);

    useEffect(() => { loadAttachments(); }, [loadAttachments]);

    const handleUpload = async () => {
      if (!file) return;
      setUploading(true);
      try {
        await expensesService.uploadAttachment(expense.id, file, selectedType, description || undefined);
        showToast('Anexo enviado');
        setFile(null);
        setDescription('');
        await loadAttachments();
        await loadExpenses();
      } catch (err: any) {
        showToast(err.response?.data?.message || 'Erro no upload', 'error');
      }
      setUploading(false);
    };

    const handleViewReceipt = async () => {
      try {
        const { url } = await expensesService.getReceiptUrl(expense.id);
        window.open(url, '_blank');
      } catch { showToast('Erro ao obter comprovante', 'error'); }
    };

    const handleDownload = async (att: any) => {
      try {
        const { url } = await expensesService.getAttachmentUrl(expense.id, att.id);
        window.open(url, '_blank');
      } catch { showToast('Erro ao obter anexo', 'error'); }
    };

    const handleChangeType = async (att: any, newType: string) => {
      try {
        await expensesService.updateAttachmentType(expense.id, att.id, newType);
        setEditingTypeId(null);
        await loadAttachments();
      } catch { showToast('Erro ao alterar tipo', 'error'); }
    };

    const handleDelete = async (att: any) => {
      if (!await confirmarPerigo(`Excluir "${att.originalName}"?`)) return;
      setDeletingId(att.id);
      try {
        await expensesService.removeAttachment(expense.id, att.id);
        showToast('Anexo removido');
        await loadAttachments();
      } catch { showToast('Erro ao remover anexo', 'error'); }
      setDeletingId(null);
    };

    const formatFileSize = (bytes: number) => {
      if (bytes < 1024) return bytes + ' B';
      if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
      return (bytes / 1048576).toFixed(1) + ' MB';
    };

    const getTypeIcon = (type: string) => {
      switch (type) {
        case 'holerite': return 'file-text';
        case 'nfs-e': return 'file-check';
        case 'fatura_cartao': return 'credit-card';
        case 'guia_imposto': return 'landmark';
        default: return 'paperclip';
      }
    };

    return (
      // Arquivo escolhido: não fecha ao clicar fora (como já era).
      <CascaDeModal
        largura="sm"
        titulo="Anexos"
        subtitulo={`${expense.description} — R$ ${fmt(expense.amount)}`}
        onClose={onClose}
        corpoClassName="p-6 space-y-4"
        rodape={
          <>
            <button onClick={onClose} className="flex-1 px-4 py-2.5 border border-slate-600 text-slate-300 rounded-lg hover:bg-slate-700 font-medium">Fechar</button>
            <button onClick={handleUpload} disabled={!file || uploading} className="flex-1 px-4 py-2.5 bg-primary text-white rounded-lg hover:bg-primary-strong font-medium disabled:opacity-50">
              {uploading ? 'Enviando...' : 'Enviar Anexo'}
            </button>
          </>
        }
      >

            {/* Legacy receipt */}
            {expense.attachmentS3Key && (
              <button onClick={handleViewReceipt} className="w-full px-4 py-2 bg-slate-700 text-slate-300 border border-slate-600 rounded-lg hover:bg-slate-600 flex items-center justify-center gap-2 text-sm">
                <Icon name="download" className="w-4 h-4" /> Comprovante (legado)
              </button>
            )}

            {/* Attachments list */}
            {loadingAttachments ? (
              <p className="text-slate-500 text-sm text-center py-2">Carregando anexos...</p>
            ) : attachmentsErro ? (
              <p role="alert" className={`text-sm text-center py-2 ${TONE_TEXT.warning}`} data-testid="anexos-erro">{textos.naoConseguiCarregar('os anexos')} {attachmentsErro}.</p>
            ) : attachments.length > 0 ? (
              <div className="space-y-2">
                <label className="block text-sm text-slate-400 font-medium">Anexos ({attachments.length})</label>
                {attachments.map((att: any) => (
                  <div key={att.id} className="flex items-center gap-3 p-3 bg-slate-700/50 rounded-lg border border-slate-600/50">
                    <Icon name={getTypeIcon(att.type)} className="w-5 h-5 text-slate-400 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-white truncate">{att.originalName}</p>
                      <div className="flex items-center gap-2 text-xs text-slate-400">
                        {editingTypeId === att.id ? (
                          <select
                            value={att.type}
                            onChange={e => handleChangeType(att, e.target.value)}
                            onBlur={() => setEditingTypeId(null)}
                            autoFocus
                            className="px-1.5 py-0.5 bg-slate-600 border border-slate-500 rounded text-slate-200 text-xs focus:outline-none focus:border-primary"
                          >
                            {attachmentTypeOptions.map(([value, label]) => (
                              <option key={value} value={value}>{label}</option>
                            ))}
                          </select>
                        ) : (
                          <button onClick={() => setEditingTypeId(att.id)} className="px-1.5 py-0.5 bg-slate-600/50 rounded text-slate-200 hover:bg-slate-600 cursor-pointer" title="Clique para alterar tipo">
                            {attachmentTypeLabels[att.type] || att.type}
                          </button>
                        )}
                        <span>{formatFileSize(att.fileSize)}</span>
                        <span>{fmtDate(att.createdAt)}</span>
                      </div>
                      {att.description && <p className="text-xs text-slate-500 mt-0.5">{att.description}</p>}
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      <button onClick={() => handleDownload(att)} className="p-1.5 text-slate-400 hover:text-white hover:bg-slate-600/50 rounded" title="Download">
                        <Icon name="download" className="w-4 h-4" />
                      </button>
                      {/* D1: excluir mora no ⋯, em perigo; a pergunta é a de antes. */}
                      <MaisAcoes
                        title={`Mais ações do anexo ${att.fileName || att.id}`}
                        ancoragem="fixa"
                        itens={[{ label: deletingId === att.id ? 'Excluindo…' : 'Excluir', danger: true, disabled: deletingId === att.id, onClick: () => handleDelete(att) }]}
                      />
                    </div>
                  </div>
                ))}
              </div>
            ) : null}

            {/* Upload section */}
            <div className="border-t border-central-borda pt-4 space-y-3">
              <label className="block text-sm text-slate-400 font-medium">Novo anexo</label>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Tipo</label>
                  <select value={selectedType} onChange={e => setSelectedType(e.target.value)} className={`w-full px-3 py-2 rounded-lg text-sm focus:border-transparent ${campo}`}>
                    {attachmentTypeOptions.map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Descricao (opcional)</label>
                  <input type="text" value={description} onChange={e => setDescription(e.target.value)} placeholder="Ex: Jan/2026" className={`w-full px-3 py-2 rounded-lg text-sm focus:border-transparent placeholder-slate-500 ${campo}`} />
                </div>
              </div>
              <div
                className="rounded-lg border border-dashed border-transparent"
                onDragOver={e => { e.preventDefault(); e.currentTarget.classList.add('border-primary'); }}
                onDragLeave={e => { e.currentTarget.classList.remove('border-primary'); }}
                onDrop={e => { e.preventDefault(); e.currentTarget.classList.remove('border-primary'); setFile(e.dataTransfer?.files?.[0] || null); }}
              >
                <input type="file" accept=".pdf,.png,.jpg,.jpeg,.gif,.webp" className="w-full text-sm text-slate-400 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-slate-700 file:text-slate-300 hover:file:bg-slate-600" onChange={e => setFile(e.target.files?.[0] || null)} />
              </div>
            </div>
      </CascaDeModal>
    );
  };

  const RebalanceModal = ({ expense, onClose, onDone , ctx }: { expense: any; onClose: () => void; onDone?: (id: number) => void | Promise<void>; ctx: ExpenseModalCtx }) => {
  const { showToast, loadExpenses, loadBase, loadRecurring, suppliers, setSuppliers, categories, setCategories, expenses, bankAccounts, clients, periodFilter, currentMonthStr, attachmentTypeOptions, attachmentTypeLabels, clearSelection } = ctx;
    // D8 (10/10/2026): o campo abria com 20000 fixo, e "Confirmar" com a pré-visão vazia (a carga das parcelas calava)
    // reescrevia todas. Agora o valor é obrigatório e a pré-visão tem de ter carregado.
    const [firstAmount, setFirstAmount] = useState('');
    const [saving, setSaving] = useState(false);
    const [children, setChildren] = useState<any[]>([]);
    const [parcelas, setParcelas] = useState<{ estado: 'lendo' | 'ok' | 'falhou'; motivo?: string }>({ estado: 'lendo' });

    useEffect(() => {
      let mounted = true;
      expensesService.getChildren(expense.id).then(data => {
        if (mounted) { setChildren(Array.isArray(data) ? data : []); setParcelas({ estado: 'ok' }); }
      }, (e: any) => {
        if (mounted) setParcelas({ estado: 'falhou', motivo: mensagemDoErroDeCarga(e) });
      });
      return () => { mounted = false; };
    }, [expense.id]);
    const porqueNaoRebalancear = parcelas.estado === 'lendo' ? 'Lendo as parcelas…'
      : parcelas.estado === 'falhou' ? `Não consegui ler as parcelas: ${parcelas.motivo}. Sem a pré-visão, o re-balanceamento fica travado.`
      : !(parseFloat(firstAmount) > 0) ? 'Informe o valor da 1ª parcela.'
      : null;

    // Group children by supplier to show what will happen
    const groups = useMemo(() => {
      const map = new Map<string, any[]>();
      for (const c of children) {
        if (c.status === 'Cancelado') continue;
        const key = c.supplierId == null ? 'NULL' : String(c.supplierId);
        const arr = map.get(key) || [];
        arr.push(c);
        map.set(key, arr);
      }
      const result: { supplier: string; total: number; items: any[] }[] = [];
      map.forEach(items => {
        items.sort((a: any, b: any) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime());
        const total = items.reduce((sum: number, c: any) => sum + Number(c.amount), 0);
        const supplier = items[0]?.supplier?.name || items[0]?.supplier?.tradeName || 'Sem fornecedor';
        result.push({ supplier, total, items });
      });
      return result;
    }, [children]);

    const handleSubmit = async () => {
      const value = parseFloat(firstAmount);
      if (porqueNaoRebalancear) return;
      if (!value || value <= 0) { showToast('Informe um valor válido', 'error'); return; }
      setSaving(true);
      try {
        await expensesService.rebalance(expense.id, value);
        showToast('Parcelas re-balanceadas');
        await onDone?.(expense.id);
        onClose();
      } catch (err: any) {
        showToast(err.response?.data?.message || 'Erro ao re-balancear', 'error');
      }
      setSaving(false);
    };

    return (
      // Valores digitados: não fecha ao clicar fora (como já era). A altura
      // chumbada do corpo (max-h-[calc(90vh-160px)]) sai.
      <CascaDeModal
        largura="base"
        titulo="Re-balancear parcelas"
        subtitulo={`${expense.description} · Total R$ ${Number(expense.amount).toFixed(2)}`}
        onClose={onClose}
        corpoClassName="p-6 space-y-4"
        rodape={
          <>
            <button onClick={onClose} className="flex-1 px-4 py-2.5 border border-slate-600 text-slate-300 rounded-lg hover:bg-slate-700 font-medium">Cancelar</button>
            <button onClick={handleSubmit} disabled={saving || !!porqueNaoRebalancear} title={porqueNaoRebalancear || undefined} className="flex-1 px-4 py-2.5 bg-primary text-white rounded-lg hover:bg-primary-strong font-medium disabled:opacity-50">
              {saving ? 'Re-balanceando...' : 'Confirmar'}
            </button>
          </>
        }
      >
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">Valor da 1ª parcela por sócio (R$)</label>
              <input
                type="number"
                step="0.01"
                min="0"
                className={`w-full px-4 py-2.5 ${campo} rounded-lg`}
                value={firstAmount}
                onChange={e => setFirstAmount(e.target.value)}
                autoFocus
              />
              {porqueNaoRebalancear && <p className={`text-xs mt-1 ${parcelas.estado === 'falhou' ? TONE_TEXT.warning : 'text-slate-400'}`} data-testid="rebalancear-motivo">{porqueNaoRebalancear}</p>}
              <p className="text-xs text-slate-500 mt-1">Para cada sócio, a parcela com vencimento mais próximo recebe esse valor; as demais recebem o saldo distribuído proporcionalmente.</p>
            </div>

            {groups.length > 0 && (
              <div className="bg-central-elevado/50 rounded-lg p-4 border border-central-borda">
                <div className="text-xs text-slate-400 mb-2">Pré-visualização por sócio</div>
                <div className="space-y-3">
                  {groups.map((g, i) => {
                    const first = parseFloat(firstAmount) || 0;
                    const remainder = Math.max(0, g.total - first);
                    return (
                      <div key={i} className="text-sm">
                        <div className="text-slate-200 font-medium">{g.supplier} <span className="text-slate-500 font-normal text-xs">· total R$ {g.total.toFixed(2)}</span></div>
                        <div className="ml-2 mt-1 grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                          {g.items.map((c, idx) => {
                            const newAmount = idx === 0 ? first : (g.items.length === 2 ? remainder : (Number(c.amount) / g.items.slice(1).reduce((s: number, ic: any) => s + Number(ic.amount), 0)) * remainder);
                            return (
                              <React.Fragment key={c.id}>
                                <span className="text-slate-500">{c.description?.split(' (')[0]} · {fmtDate(c.dueDate)}</span>
                                <span className="text-slate-300">R$ {Number(c.amount).toFixed(2)} → <span className="text-white font-medium">R$ {newAmount.toFixed(2)}</span></span>
                              </React.Fragment>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
      </CascaDeModal>
    );
  };

  const SplitExpenseModal = ({ expense, onClose, onSplit , ctx }: { expense: any; onClose: () => void; onSplit?: (id: number) => void; ctx: ExpenseModalCtx }) => {
  const { showToast, loadExpenses, loadBase, loadRecurring, suppliers, setSuppliers, categories, setCategories, expenses, bankAccounts, clients, periodFilter, currentMonthStr, attachmentTypeOptions, attachmentTypeLabels, clearSelection } = ctx;
    const [items, setItems] = useState<{ description: string; amount: string; dueDate: string; bankAccountId: string; categoryId: string }[]>([
      { description: '', amount: '', dueDate: expense.dueDate?.split('T')[0] || '', bankAccountId: expense.bankAccountId?.toString() || '', categoryId: expense.categoryId?.toString() || '' },
      { description: '', amount: '', dueDate: expense.dueDate?.split('T')[0] || '', bankAccountId: expense.bankAccountId?.toString() || '', categoryId: expense.categoryId?.toString() || '' },
    ]);
    const [saving, setSaving] = useState(false);

    const itemsTotal = items.reduce((sum, i) => sum + (parseFloat(i.amount) || 0), 0);
    const parentTotal = Number(expense.amount);
    const diff = parentTotal - itemsTotal;

    const addItem = () => setItems([...items, { description: '', amount: '', dueDate: expense.dueDate?.split('T')[0] || '', bankAccountId: expense.bankAccountId?.toString() || '', categoryId: expense.categoryId?.toString() || '' }]);
    const removeItem = (idx: number) => { if (items.length > 2) setItems(items.filter((_, i) => i !== idx)); };
    const updateItem = (idx: number, field: string, value: string) => {
      const updated = [...items];
      updated[idx] = { ...updated[idx], [field]: value };
      setItems(updated);
    };

    const handleSplit = async () => {
      const validItems = items.filter(i => i.description && parseFloat(i.amount) > 0);
      if (validItems.length < 2) { showToast('Adicione pelo menos 2 itens', 'error'); return; }
      setSaving(true);
      try {
        await expensesService.split(expense.id, {
          items: validItems.map(i => ({
            description: i.description,
            amount: parseFloat(i.amount),
            dueDate: i.dueDate || undefined,
            bankAccountId: i.bankAccountId ? parseInt(i.bankAccountId) : undefined,
            categoryId: i.categoryId ? parseInt(i.categoryId) : undefined,
          })),
        });
        showToast(`Despesa desmembrada em ${validItems.length} itens`);
        await loadExpenses();
        onSplit?.(expense.id);
        onClose();
      } catch (err: any) {
        showToast(err.response?.data?.message || 'Erro ao desmembrar', 'error');
      } finally {
        setSaving(false);
      }
    };

    return (
      // Itens digitados: antes fechava ao clicar fora; agora só pelo × ou Cancelar.
      // O resumo (original, soma, diferença) vira a faixa da casca.
      <CascaDeModal
        largura="md"
        titulo="Desmembrar Despesa"
        subtitulo={expense.description}
        onClose={onClose}
        faixa={[
          { k: 'Total original', v: `R$ ${parentTotal.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` },
          { k: 'Soma dos itens', v: `R$ ${itemsTotal.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`, cls: Math.abs(diff) < 0.01 ? TONE_TEXT.open : TONE_TEXT.warning },
          { k: 'Diferença', v: `R$ ${diff.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`, cls: Math.abs(diff) < 0.01 ? TONE_TEXT.open : diff > 0 ? TONE_TEXT.warning : TONE_TEXT.danger },
        ]}
        corpoClassName="p-5 space-y-4"
        rodape={
          <>
            <button onClick={onClose} className="flex-1 px-4 py-2.5 border border-slate-600 text-slate-300 rounded-lg hover:bg-slate-700 font-medium">Cancelar</button>
            <button onClick={handleSplit} disabled={saving || items.filter(i => i.description && parseFloat(i.amount) > 0).length < 2} className="flex-1 px-4 py-2.5 bg-primary text-white rounded-lg hover:bg-primary-strong font-medium disabled:opacity-50 disabled:cursor-not-allowed">
              {saving ? 'Desmembrando...' : `Desmembrar em ${items.filter(i => i.description && parseFloat(i.amount) > 0).length} itens`}
            </button>
          </>
        }
      >

            {/* Items */}
            <div className="space-y-3">
              {items.map((item, idx) => (
                <div key={idx} className="bg-slate-700/30 rounded-lg p-3 border border-slate-600/50">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-slate-400 font-medium">Item {idx + 1}</span>
                    {items.length > 2 && (
                      <button onClick={() => removeItem(idx)} className="text-slate-500 hover:text-red-400 text-xs">Remover</button>
                    )}
                  </div>
                  <div className="grid grid-cols-12 gap-2">
                    <div className="col-span-5">
                      <input
                        type="text"
                        placeholder="Descrição"
                        value={item.description}
                        onChange={(e) => updateItem(idx, 'description', e.target.value)}
                        className={`w-full rounded-lg px-3 py-2 text-sm placeholder-slate-500 focus:outline-none ${campo}`}
                      />
                    </div>
                    <div className="col-span-2">
                      <input
                        type="number"
                        step="0.01"
                        placeholder="Valor"
                        value={item.amount}
                        onChange={(e) => updateItem(idx, 'amount', e.target.value)}
                        className={`w-full rounded-lg px-3 py-2 text-sm placeholder-slate-500 focus:outline-none ${campo}`}
                      />
                    </div>
                    <div className="col-span-2">
                      <input
                        type="date"
                        value={item.dueDate}
                        onChange={(e) => updateItem(idx, 'dueDate', e.target.value)}
                        className={`w-full ${campo} rounded-lg px-3 py-2 text-sm focus:outline-none`}
                      />
                    </div>
                    <div className="col-span-3">
                      <select
                        value={item.bankAccountId}
                        onChange={(e) => updateItem(idx, 'bankAccountId', e.target.value)}
                        className={`w-full ${campo} rounded-lg px-3 py-2 text-sm focus:outline-none`}
                      >
                        <option value="">Conta bancária</option>
                        {bankAccounts.map((ba: any) => (
                          <option key={ba.id} value={ba.id}>{ba.bankName}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <button onClick={addItem} className="w-full py-2 border border-dashed border-slate-600 rounded-lg text-sm text-slate-400 hover:text-white hover:border-slate-500 transition-colors">
              + Adicionar Item
            </button>
      </CascaDeModal>
    );
  };

  const GroupExpensesModal = ({ selectedIds, onClose , ctx }: { selectedIds: number[]; onClose: () => void; ctx: ExpenseModalCtx }) => {
  const { showToast, loadExpenses, loadBase, loadRecurring, suppliers, setSuppliers, categories, setCategories, expenses, bankAccounts, clients, periodFilter, currentMonthStr, attachmentTypeOptions, attachmentTypeLabels, clearSelection } = ctx;
    const [description, setDescription] = useState('');
    const [saving, setSaving] = useState(false);
    const selectedItems = expenses.filter((e: any) => selectedIds.includes(e.id));
    const total = selectedItems.reduce((sum: number, e: any) => sum + Number(e.amount), 0);

    const handleGroup = async () => {
      if (!description.trim()) { showToast('Informe a descrição do agrupamento', 'error'); return; }
      setSaving(true);
      try {
        await expensesService.group({ description: description.trim(), childIds: selectedIds });
        showToast(`${selectedIds.length} despesas agrupadas em "${description}"`);
        clearSelection();
        await loadExpenses();
        onClose();
      } catch (err: any) {
        showToast(err.response?.data?.message || 'Erro ao agrupar', 'error');
      } finally {
        setSaving(false);
      }
    };

    return (
      // Descrição digitada: antes fechava ao clicar fora; agora só pelo × ou Cancelar.
      <CascaDeModal
        largura="sm"
        titulo="Agrupar Despesas"
        onClose={onClose}
        faixa={[
          { k: 'Despesas', v: `${selectedIds.length} itens` },
          { k: 'Valor total', v: `R$ ${total.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` },
        ]}
        corpoClassName="p-5 space-y-4"
        rodape={
          <>
            <button onClick={onClose} className="flex-1 px-4 py-2.5 border border-slate-600 text-slate-300 rounded-lg hover:bg-slate-700 font-medium">Cancelar</button>
            <button onClick={handleGroup} disabled={saving || !description.trim()} className="flex-1 px-4 py-2.5 bg-primary text-white rounded-lg hover:bg-primary-strong font-medium disabled:opacity-50 disabled:cursor-not-allowed">
              {saving ? 'Agrupando...' : 'Agrupar'}
            </button>
          </>
        }
      >
            <div>
              <label className="block text-sm text-slate-400 mb-1">Descrição do agrupamento *</label>
              <input
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Ex: Combustível - Sicredi"
                className={`w-full rounded-lg px-3 py-2.5 placeholder-slate-500 focus:outline-none ${campo}`}
                autoFocus
              />
            </div>
            <div className="bg-slate-700/50 rounded-lg p-3 space-y-2">
              <div className="space-y-1">
                {selectedItems.map((e: any) => (
                  <div key={e.id} className="flex justify-between text-xs text-slate-400">
                    <span className="truncate mr-2">{e.description}</span>
                    <span className="flex-shrink-0">R$ {Number(e.amount).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                  </div>
                ))}
              </div>
            </div>
      </CascaDeModal>
    );
  };

  const RecurringForm = ({ onClose, recurring , ctx }: { onClose: () => void; recurring?: any; ctx: ExpenseModalCtx }) => {
  const { showToast, loadExpenses, loadBase, loadRecurring, suppliers, setSuppliers, categories, setCategories, expenses, bankAccounts, clients, periodFilter, currentMonthStr, attachmentTypeOptions, attachmentTypeLabels, clearSelection } = ctx;
    const [form, setForm] = useState({
      description: recurring?.description || '',
      categoryId: recurring?.categoryId || '',
      supplierId: recurring?.supplierId || '',
      bankAccountId: recurring?.bankAccountId || '',
      amount: recurring?.amount || '',
      dayOfMonth: recurring?.dayOfMonth || '',
      paymentMethod: recurring?.paymentMethod || '',
      startDate: recurring?.startDate ? recurring.startDate.split('T')[0] : getTodayStr(),
      endDate: recurring?.endDate ? recurring.endDate.split('T')[0] : '',
      variableAmount: recurring?.variableAmount || false,
    });
    const [saving, setSaving] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
      e.preventDefault();
      if (!form.description || !form.categoryId || !form.amount || !form.dayOfMonth || !form.startDate) {
        showToast('Preencha os campos obrigatórios', 'error');
        return;
      }
      setSaving(true);
      try {
        const payload = {
          ...form,
          categoryId: Number(form.categoryId),
          supplierId: form.supplierId ? Number(form.supplierId) : null,
          bankAccountId: form.bankAccountId ? Number(form.bankAccountId) : null,
          amount: parseFloat(String(form.amount)),
          dayOfMonth: Number(form.dayOfMonth),
          // mesmo motivo do form de despesa: '' cai no @IsEnum e devolve 400
          paymentMethod: form.paymentMethod || null,
          // null (não undefined) para conseguir limpar a data fim: undefined some
          // do JSON e o backend mantém o valor antigo em silêncio.
          endDate: form.endDate || null,
          variableAmount: !!form.variableAmount,
        };
        if (recurring) {
          await recurringExpensesService.update(recurring.id, payload);
          showToast('Recorrência atualizada');
        } else {
          await recurringExpensesService.create(payload);
          showToast('Recorrência criada');
        }
        await loadRecurring();
        onClose();
      } catch (err: any) {
        showToast(err.response?.data?.message || 'Erro ao salvar', 'error');
      }
      setSaving(false);
    };

    return (
      // Formulário: não fecha ao clicar fora (como já era). Envio no rodapé ligado
      // ao <form> pelo atributo `form`.
      <CascaDeModal
        largura="sm"
        titulo={recurring ? 'Editar Recorrência' : 'Nova Recorrência'}
        onClose={onClose}
        corpoClassName="p-6"
        rodape={
          <>
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2.5 border border-slate-600 text-slate-300 rounded-lg hover:bg-slate-700 font-medium">Cancelar</button>
            <button type="submit" form="form-recorrencia" disabled={saving} className="flex-1 px-4 py-2.5 bg-primary text-white rounded-lg hover:bg-primary-strong font-medium disabled:opacity-50">
              {saving ? 'Salvando...' : (recurring ? 'Atualizar' : 'Criar')}
            </button>
          </>
        }
      >
          <form id="form-recorrencia" onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm text-slate-400 mb-1">Descrição *</label>
              <input className={`w-full px-3 py-2 ${campo} rounded`} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-slate-400 mb-1">Categoria *</label>
                <select className={`w-full px-3 py-2 ${campo} rounded`} value={form.categoryId} onChange={e => setForm({ ...form, categoryId: e.target.value })}>
                  <option value="">Selecione</option>
                  {categories.filter(c => c.isActive).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-1">Fornecedor</label>
                <select className={`w-full px-3 py-2 ${campo} rounded`} value={form.supplierId} onChange={e => setForm({ ...form, supplierId: e.target.value })}>
                  <option value="">Nenhum</option>
                  {suppliers.filter(s => s.isActive !== false).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm text-slate-400 mb-1">Valor (R$) *</label>
                <input type="number" step="0.01" min="0" className={`w-full px-3 py-2 ${campo} rounded`} value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} />
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-1">Dia do Mês *</label>
                <input type="number" min="1" max="31" className={`w-full px-3 py-2 ${campo} rounded`} value={form.dayOfMonth} onChange={e => setForm({ ...form, dayOfMonth: e.target.value })} />
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-1">Método</label>
                <select className={`w-full px-3 py-2 ${campo} rounded`} value={form.paymentMethod} onChange={e => setForm({ ...form, paymentMethod: e.target.value })}>
                  <option value="">{TRACO}</option>
                  {Object.entries(paymentMethodLabels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-slate-400 mb-1">Conta Bancária</label>
                <select className={`w-full px-3 py-2 ${campo} rounded`} value={form.bankAccountId} onChange={e => setForm({ ...form, bankAccountId: e.target.value })}>
                  <option value="">Nenhuma</option>
                  {bankAccounts.filter(b => b.isActive !== false).map(b => <option key={b.id} value={b.id}>{b.bankName} - {b.accountNumber}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-1">Início *</label>
                <input type="date" className={`w-full px-3 py-2 ${campo} rounded`} value={form.startDate} onChange={e => setForm({ ...form, startDate: e.target.value })} />
              </div>
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-1">Fim (opcional)</label>
              <input type="date" className={`w-full px-3 py-2 ${campo} rounded`} value={form.endDate} onChange={e => setForm({ ...form, endDate: e.target.value })} />
            </div>
            <label className="flex items-start gap-2 px-3 py-2.5 bg-slate-700/40 rounded border border-slate-600 cursor-pointer">
              <input type="checkbox" checked={!!form.variableAmount} onChange={e => setForm({ ...form, variableAmount: e.target.checked })} className="accent-primary mt-0.5" />
              <span className="text-sm text-slate-300">
                Valor variável (o valor é só previsão)
                <span className="block text-xs text-slate-500 mt-0.5">
                  As despesas geradas ajustam o valor pro real na conciliação e marcam Pago — sem saldo "Parcial". Use pra fornecedor sem pagamento parcial (boleto, tarifa por uso, imposto).
                </span>
              </span>
            </label>
          </form>
      </CascaDeModal>
    );
  };

  const SupplierForm = ({ onClose, supplier , ctx }: { onClose: () => void; supplier?: any; ctx: ExpenseModalCtx }) => {
  const { showToast, loadExpenses, loadBase, loadRecurring, suppliers, setSuppliers, categories, setCategories, expenses, bankAccounts, clients, periodFilter, currentMonthStr, attachmentTypeOptions, attachmentTypeLabels, clearSelection } = ctx;
    const [form, setForm] = useState({
      name: supplier?.name || '',
      tradeName: supplier?.tradeName || '',
      companyName: supplier?.companyName || '',
      cnpj: supplier?.cnpj || '',
      cpf: supplier?.cpf || '',
      taxId: supplier?.taxId || '',
      country: supplier?.country || 'BR',
      email: supplier?.email || '',
      phone: supplier?.phone || '',
      address: supplier?.address || '',
      notes: supplier?.notes || '',
      defaultCategoryId: supplier?.defaultCategoryId || '',
      partnerAdvanceAmount: supplier?.partnerAdvanceAmount ? String(supplier.partnerAdvanceAmount) : '',
      partnerAdvanceDay: supplier?.partnerAdvanceDay ? String(supplier.partnerAdvanceDay) : '',
    });
    const [saving, setSaving] = useState(false);
    const [searchingCnpj, setSearchingCnpj] = useState(false);

    // Reset form when supplier changes (edit different supplier)
    useEffect(() => {
      setForm({
        name: supplier?.name || '',
        tradeName: supplier?.tradeName || '',
        companyName: supplier?.companyName || '',
        cnpj: supplier?.cnpj || '',
        cpf: supplier?.cpf || '',
        taxId: supplier?.taxId || '',
        country: supplier?.country || 'BR',
        email: supplier?.email || '',
        phone: supplier?.phone || '',
        address: supplier?.address || '',
        notes: supplier?.notes || '',
        defaultCategoryId: supplier?.defaultCategoryId || '',
        partnerAdvanceAmount: supplier?.partnerAdvanceAmount ? String(supplier.partnerAdvanceAmount) : '',
        partnerAdvanceDay: supplier?.partnerAdvanceDay ? String(supplier.partnerAdvanceDay) : '',
      });
    }, [supplier?.id]);

    const updateField = (field: string, value: string) => {
      setForm(prev => ({ ...prev, [field]: value }));
    };

    const maskCNPJ = (value: string) => {
      const digits = value.replace(/\D/g, '').slice(0, 14);
      return digits
        .replace(/^(\d{2})(\d)/, '$1.$2')
        .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
        .replace(/^(\d{2})\.(\d{3})\.(\d{3})(\d)/, '$1.$2.$3/$4')
        .replace(/^(\d{2})\.(\d{3})\.(\d{3})\/(\d{4})(\d)/, '$1.$2.$3/$4-$5');
    };

    const fetchCnpjData = async (cnpjClean: string) => {
      setSearchingCnpj(true);
      try {
        const data = await cnpjService.search(cnpjClean);
        if (!data || data.error || data.status === 'ERROR') {
          showToast(data?.message || 'CNPJ não encontrado', 'error');
          setSearchingCnpj(false);
          return;
        }
        const parts = [data.logradouro, data.numero, data.complemento, data.bairro, data.municipio, data.uf].filter(Boolean);
        if (data.cep) parts.push(`CEP ${data.cep}`);
        const phone = (data.telefone?.split('/')[0] || '').trim().replace(/\D/g, '').slice(0, 11);
        setForm(prev => ({
          ...prev,
          tradeName: data.fantasia || prev.tradeName,
          companyName: data.nome || prev.companyName,
          name: prev.name || data.fantasia || data.nome || '',
          email: data.email || prev.email,
          phone: phone || prev.phone,
          address: parts.length > 0 ? parts.join(', ') : prev.address,
        }));
        // NOTE: NÃO chamar showToast aqui — causa re-render do pai que recria
        // SupplierForm (inline), fazendo React desmontar e remontar, perdendo o state
      } catch {
        showToast('Erro ao buscar CNPJ', 'error');
      }
      setSearchingCnpj(false);
    };

    const handleCnpjChange = (value: string) => {
      const masked = maskCNPJ(value);
      updateField('cnpj', masked);
      const clean = masked.replace(/\D/g, '');
      if (clean.length === 14) {
        fetchCnpjData(clean);
      }
    };

    const handleSubmit = async (e: React.FormEvent) => {
      e.preventDefault();
      if (!form.name) { showToast('Nome é obrigatório', 'error'); return; }
      setSaving(true);
      // Limpar campos vazios para evitar erro de validação (ex: email vazio → @IsEmail rejeita)
      const payload: any = {};
      Object.entries(form).forEach(([key, value]) => {
        if (key === 'defaultCategoryId' || key === 'partnerAdvanceDay') {
          payload[key] = value ? Number(value) : null;
        } else if (key === 'partnerAdvanceAmount') {
          payload[key] = value && String(value).trim() !== '' ? Number(value) : null;
        } else {
          payload[key] = typeof value === 'string' && value.trim() === '' ? null : value;
        }
      });
      try {
        if (supplier) {
          await suppliersService.update(supplier.id, payload);
          showToast('Fornecedor atualizado');
        } else {
          await suppliersService.create(payload);
          showToast('Fornecedor criado');
        }
        const data = await suppliersService.getAll();
        setSuppliers(Array.isArray(data) ? data : data.data || []);
        onClose();
      } catch (err: any) {
        showToast(err.response?.data?.message || 'Erro ao salvar', 'error');
      }
      setSaving(false);
    };

    return (
      // Formulário: não fecha ao clicar fora (como já era). A altura chumbada do
      // form (max-h-[calc(90vh-80px)]) sai: o corpo da casca recebe o que sobra.
      <CascaDeModal
        largura="md"
        titulo={supplier ? 'Editar Fornecedor' : 'Novo Fornecedor'}
        onClose={onClose}
        corpoClassName="p-6"
        rodape={
          <>
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2.5 border border-slate-600 text-slate-300 rounded-lg hover:bg-slate-700 font-medium">Cancelar</button>
            <button type="submit" form="form-fornecedor" disabled={saving} className="flex-1 px-4 py-2.5 bg-primary text-white rounded-lg hover:bg-primary-strong font-medium disabled:opacity-50">
              {saving ? 'Salvando...' : (supplier ? 'Atualizar' : 'Criar')}
            </button>
          </>
        }
      >
          <form id="form-fornecedor" onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">CNPJ {form.country === 'BR' && '*'}</label>
                <div className="flex gap-2">
                  <input className={`flex-1 px-4 py-2.5 ${campo} rounded-lg`} value={form.cnpj} onChange={e => handleCnpjChange(e.target.value)} placeholder="00.000.000/0000-00" maxLength={18} />
                  <button type="button" onClick={() => { const c = form.cnpj.replace(/\D/g, ''); if (c.length === 14) fetchCnpjData(c); else showToast('CNPJ deve ter 14 dígitos', 'error'); }} disabled={searchingCnpj} className="px-4 py-2.5 bg-primary text-white rounded-lg hover:bg-primary-strong disabled:opacity-50 flex items-center justify-center" title="Buscar dados do CNPJ">
                    {searchingCnpj ? <Icon name="loader" className="w-4 h-4 animate-spin" /> : <Icon name="search" className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">País</label>
                <select className={`w-full px-4 py-2.5 ${campo} rounded-lg`} value={form.country} onChange={e => updateField('country', e.target.value)}>
                  <option value="BR">Brasil</option>
                  <option value="US">Estados Unidos</option>
                  <option value="PT">Portugal</option>
                  <option value="DE">Alemanha</option>
                  <option value="GB">Reino Unido</option>
                  <option value="ES">Espanha</option>
                  <option value="FR">França</option>
                  <option value="IT">Itália</option>
                  <option value="AR">Argentina</option>
                  <option value="UY">Uruguai</option>
                  <option value="PY">Paraguai</option>
                  <option value="CL">Chile</option>
                  <option value="CO">Colômbia</option>
                  <option value="MX">México</option>
                  <option value="CN">China</option>
                  <option value="JP">Japão</option>
                  <option value="KR">Coreia do Sul</option>
                  <option value="IN">Índia</option>
                  <option value="CA">Canadá</option>
                  <option value="AU">Austrália</option>
                  <option value="OTHER">Outro</option>
                </select>
              </div>
            </div>
            {form.country !== 'BR' && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">Tax ID / ID Fiscal</label>
                  <input className={`w-full px-4 py-2.5 ${campo} rounded-lg`} value={form.taxId} onChange={e => updateField('taxId', e.target.value)} placeholder="VAT, EIN, RFC, RUT..." />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">CPF</label>
                  <input className={`w-full px-4 py-2.5 ${campo} rounded-lg`} value={form.cpf} onChange={e => updateField('cpf', e.target.value)} placeholder="000.000.000-00" />
                </div>
              </div>
            )}
            {form.country === 'BR' && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">CPF</label>
                  <input className={`w-full px-4 py-2.5 ${campo} rounded-lg`} value={form.cpf} onChange={e => updateField('cpf', e.target.value)} placeholder="000.000.000-00" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">Categoria Padrão</label>
                  <select className={`w-full px-4 py-2.5 ${campo} rounded-lg`} value={form.defaultCategoryId} onChange={e => updateField('defaultCategoryId', e.target.value)}>
                    <option value="">Nenhuma</option>
                    {categories.filter(c => c.isActive).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">Nome / Descrição *</label>
              <input className={`w-full px-4 py-2.5 ${campo} rounded-lg`} value={form.name} onChange={e => updateField('name', e.target.value)} placeholder="Nome curto para identificação" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">Nome Fantasia</label>
                <input className={`w-full px-4 py-2.5 ${campo} rounded-lg`} value={form.tradeName} onChange={e => updateField('tradeName', e.target.value)} />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">Razão Social</label>
                <input className={`w-full px-4 py-2.5 ${campo} rounded-lg`} value={form.companyName} onChange={e => updateField('companyName', e.target.value)} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">Email</label>
                <input type="email" className={`w-full px-4 py-2.5 ${campo} rounded-lg`} value={form.email} onChange={e => updateField('email', e.target.value)} />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">Telefone</label>
                <input className={`w-full px-4 py-2.5 ${campo} rounded-lg`} value={form.phone} onChange={e => updateField('phone', e.target.value)} />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">Endereço</label>
              <input className={`w-full px-4 py-2.5 ${campo} rounded-lg`} value={form.address} onChange={e => updateField('address', e.target.value)} />
            </div>
            {form.country !== 'BR' && (
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">Categoria Padrão</label>
                <select className={`w-full px-4 py-2.5 ${campo} rounded-lg`} value={form.defaultCategoryId} onChange={e => updateField('defaultCategoryId', e.target.value)}>
                  <option value="">Nenhuma</option>
                  {categories.filter(c => c.isActive).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">Observações</label>
              <textarea className={`w-full px-4 py-2.5 ${campo} rounded-lg`} rows={2} value={form.notes} onChange={e => updateField('notes', e.target.value)} />
            </div>
            <div className="border-t border-central-borda pt-4 mt-2">
              <div className="text-sm font-medium text-slate-300 mb-1">Adiantamento mensal (Sócio · Divisão de Lucro)</div>
              <p className="text-xs text-slate-500 mb-3">Quando preenchido, ao gerar a recorrência mensal o sistema cria automaticamente uma despesa de adiantamento e reduz o valor da parcela principal.</p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-slate-400 mb-1">Valor do adiantamento (R$)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    className={`w-full px-4 py-2.5 ${campo} rounded-lg`}
                    value={form.partnerAdvanceAmount}
                    onChange={e => updateField('partnerAdvanceAmount', e.target.value)}
                    placeholder="ex: 20000"
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-400 mb-1">Dia do mês</label>
                  <input
                    type="number"
                    min="1"
                    max="28"
                    className={`w-full px-4 py-2.5 ${campo} rounded-lg`}
                    value={form.partnerAdvanceDay}
                    onChange={e => updateField('partnerAdvanceDay', e.target.value)}
                    placeholder="ex: 5"
                  />
                </div>
              </div>
            </div>
          </form>
      </CascaDeModal>
    );
  };

  const CategoryForm = ({ onClose, category , ctx }: { onClose: () => void; category?: any; ctx: ExpenseModalCtx }) => {
  const { showToast, loadExpenses, loadBase, loadRecurring, suppliers, setSuppliers, categories, setCategories, expenses, bankAccounts, clients, periodFilter, currentMonthStr, attachmentTypeOptions, attachmentTypeLabels, clearSelection } = ctx;
    const [form, setForm] = useState({
      name: category?.name || '',
      description: category?.description || '',
      color: category?.color || '#f97316',
      icon: category?.icon || '',
    });
    const [saving, setSaving] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
      e.preventDefault();
      if (!form.name) { showToast('Nome é obrigatório', 'error'); return; }
      setSaving(true);
      try {
        if (category) {
          await expenseCategoriesService.update(category.id, form);
          showToast('Categoria atualizada');
        } else {
          await expenseCategoriesService.create(form);
          showToast('Categoria criada');
        }
        const data = await expenseCategoriesService.getAll();
        setCategories(Array.isArray(data) ? data : []);
        onClose();
      } catch (err: any) {
        showToast(err.response?.data?.message || 'Erro ao salvar', 'error');
      }
      setSaving(false);
    };

    return (
      // Formulário: não fecha ao clicar fora (como já era). Envio no rodapé ligado
      // ao <form> pelo atributo `form`; o Enter nos campos continua enviando.
      <CascaDeModal
        largura="xs"
        titulo={category ? 'Editar Categoria' : 'Nova Categoria'}
        onClose={onClose}
        corpoClassName="p-6"
        rodape={
          <>
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2.5 border border-slate-600 text-slate-300 rounded-lg hover:bg-slate-700 font-medium">Cancelar</button>
            <button type="submit" form="form-categoria-despesa" disabled={saving} className="flex-1 px-4 py-2.5 bg-primary text-white rounded-lg hover:bg-primary-strong font-medium disabled:opacity-50">
              {saving ? 'Salvando...' : (category ? 'Atualizar' : 'Criar')}
            </button>
          </>
        }
      >
          <form id="form-categoria-despesa" onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm text-slate-400 mb-1">Nome *</label>
              <input className={`w-full px-3 py-2 ${campo} rounded`} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-1">Descrição</label>
              <input className={`w-full px-3 py-2 ${campo} rounded`} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-slate-400 mb-1">Cor</label>
                <input type="color" className="w-full h-10 bg-slate-700 rounded border border-slate-600 cursor-pointer" value={form.color} onChange={e => setForm({ ...form, color: e.target.value })} />
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-1">Ícone</label>
                <input className={`w-full px-3 py-2 ${campo} rounded`} value={form.icon} onChange={e => setForm({ ...form, icon: e.target.value })} placeholder="ex: home, dollar" />
              </div>
            </div>
          </form>
      </CascaDeModal>
    );
  };

  const CsvImportModal = ({ onClose , ctx }: { onClose: () => void; ctx: ExpenseModalCtx }) => {
  const { showToast, loadExpenses, loadBase, loadRecurring, suppliers, setSuppliers, categories, setCategories, expenses, bankAccounts, clients, periodFilter, currentMonthStr, attachmentTypeOptions, attachmentTypeLabels, clearSelection } = ctx;
    const [file, setFile] = useState<File | null>(null);
    const [uploading, setUploading] = useState(false);
    const [result, setResult] = useState<any>(null);

    const handleDownloadTemplate = async () => {
      try {
        const tmpl = await expensesService.getImportTemplate();
        const blob = new Blob([tmpl.example], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = 'template_despesas.csv'; a.click();
        URL.revokeObjectURL(url);
      } catch { showToast('Erro ao baixar template', 'error'); }
    };

    const handleImport = async () => {
      if (!file) return;
      setUploading(true);
      try {
        const res = await expensesService.importCsv(file);
        setResult(res);
        if (res.imported > 0) {
          showToast(`${res.imported} despesas importadas`);
          await loadExpenses();
          await loadBase();
        }
      } catch (err: any) {
        showToast(err.response?.data?.message || 'Erro ao importar CSV', 'error');
      }
      setUploading(false);
    };

    return (
      // Arquivo escolhido: não fecha ao clicar fora (como já era). O rodapé muda com
      // o estado: Cancelar + Importar antes, Fechar (neutro, não grava) depois.
      <CascaDeModal
        largura="sm"
        titulo="Importar Despesas (CSV)"
        onClose={onClose}
        corpoClassName="p-6 space-y-4"
        rodape={!result ? (
          <>
            <button onClick={onClose} className="flex-1 px-4 py-2.5 border border-slate-600 text-slate-300 rounded-lg hover:bg-slate-700 font-medium">Cancelar</button>
            <button onClick={handleImport} disabled={!file || uploading} className="flex-1 px-4 py-2.5 bg-primary text-white rounded-lg hover:bg-primary-strong font-medium disabled:opacity-50">
              {uploading ? 'Importando...' : 'Importar'}
            </button>
          </>
        ) : (
          <button onClick={onClose} className="w-full px-4 py-2.5 bg-slate-700 text-white rounded-lg hover:bg-slate-600 font-medium">Fechar</button>
        )}
      >
            {!result ? (
              <>
                <p className={`text-sm ${TONE_TEXT.warning}`} data-testid="aviso-do-csv">{AVISO_DO_CSV}</p>
                <p className="text-slate-400 text-sm">
                  Importe despesas historicas via arquivo CSV. Categorias e fornecedores serao criados automaticamente se nao existirem.
                </p>
                {isAdmin() && (<button onClick={handleDownloadTemplate} className="w-full px-4 py-2 bg-slate-700 text-slate-300 rounded-lg hover:bg-slate-600 text-sm font-medium flex items-center justify-center gap-2">
                  <Icon name="download" className="w-4 h-4" /> Baixar Template CSV
                </button>)}
                <div
                  className="rounded-lg border border-dashed border-transparent"
                  onDragOver={e => { e.preventDefault(); e.currentTarget.classList.add('border-primary'); }}
                  onDragLeave={e => { e.currentTarget.classList.remove('border-primary'); }}
                  onDrop={e => { e.preventDefault(); e.currentTarget.classList.remove('border-primary'); setFile(e.dataTransfer?.files?.[0] || null); }}
                >
                  <label className="block text-sm text-slate-400 mb-1">Arquivo CSV</label>
                  <input type="file" accept=".csv,.CSV,.txt" className="w-full text-sm text-slate-400 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-slate-700 file:text-slate-300 hover:file:bg-slate-600" onChange={e => setFile(e.target.files?.[0] || null)} />
                </div>
              </>
            ) : (
              <>
                <div className="space-y-2">
                  <div className="flex justify-between bg-slate-700/50 rounded-lg px-3 py-2">
                    <span className="text-slate-400">Importadas</span>
                    <span className={`${TONE_TEXT.success} font-bold`}>{result.imported || 0}</span>
                  </div>
                  <div className="flex justify-between bg-slate-700/50 rounded-lg px-3 py-2">
                    <span className="text-slate-400">Erros</span>
                    <span className="text-red-400 font-bold">{result.errors?.length || 0}</span>
                  </div>
                  {result.created?.categories?.length > 0 && (
                    <div className="bg-slate-700/50 rounded-lg px-3 py-2">
                      <span className="text-slate-400 text-sm">Categorias criadas: </span>
                      <span className="text-slate-200 text-sm">{result.created.categories.join(', ')}</span>
                    </div>
                  )}
                  {result.created?.suppliers?.length > 0 && (
                    <div className="bg-slate-700/50 rounded-lg px-3 py-2">
                      <span className="text-slate-400 text-sm">Fornecedores criados: </span>
                      <span className="text-slate-200 text-sm">{result.created.suppliers.join(', ')}</span>
                    </div>
                  )}
                </div>
                {result.errors?.length > 0 && (
                  <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 max-h-40 overflow-y-auto">
                    <p className="text-red-400 text-sm font-medium mb-1">Erros:</p>
                    {result.errors.slice(0, 10).map((err: any, idx: number) => (
                      <p key={idx} className="text-red-300 text-xs">Linha {err.line}: {err.message}</p>
                    ))}
                    {result.errors.length > 10 && <p className="text-red-300 text-xs">... e mais {result.errors.length - 10} erros</p>}
                  </div>
                )}
              </>
            )}
      </CascaDeModal>
    );
  };

export default function ExpensesTab({ initialSearch, onSearchConsumed }: { initialSearch?: string; onSearchConsumed?: () => void } = {}) {
  const confirmarPerigo = useConfirmarPerigo();
  const pedirTexto = usePedirTexto();
  const [subView, setSubView] = useState<SubView>('expenses');
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  // Expenses state
  const [expenses, setExpenses] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [suppliers, setSuppliers] = useState<any[]>([]);
  const [bankAccounts, setBankAccounts] = useState<any[]>([]);
  const [clients, setClients] = useState<any[]>([]);
  const currentMonthStr = (() => { const now = new Date(); return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`; })();
  // O período na URL (15/10/2026): ?mes=… / ?de=…&ate=…; F5 e link colado mantêm o mês. Ver utils/periodoNaUrl.
  const [periodFilter, setPeriodFilter] = usePeriodoNaUrl();
  const [categoryFilter, setCategoryFilter] = useState('');
  const [supplierFilter, setSupplierFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [bankAccountFilter, setBankAccountFilter] = useState('');
  const [clientFilter, setClientFilter] = useState('');
  const [searchText, setSearchText] = useState(initialSearch || '');
  const [debouncedSearch, setDebouncedSearch] = useState(initialSearch || '');
  // Modo varredura: filhas viram linhas de primeiro nivel (includeChildren no
  // backend). Serve para achar uma filha vencida, que na listagem normal fica
  // escondida dentro de um pai que pode estar Pendente.
  const [flatMode, setFlatMode] = useState(false);
  // When parent passes a new initialSearch (e.g. navigation from the bank
  // reconciliation trace), apply it once. The linked expense can be in any
  // month, so switch to "Todos os Meses" (months: []) — otherwise the default
  // current-month filter hides a hit from a past month.
  useEffect(() => {
    if (initialSearch) {
      setSearchText(initialSearch);
      setDebouncedSearch(initialSearch);
      setPeriodFilter(prev => ({ ...prev, mode: 'month', months: [] }));
      onSearchConsumed?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSearch]);
  const [sort, toggleSort] = useSortState({ col: 'dueDate', dir: 'asc' });

  // Modals
  const [expenseModal, setExpenseModal] = useState<'create' | 'edit' | null>(null);
  const [payModal, setPayModal] = useState<any | null>(null);
  const [receiptModal, setReceiptModal] = useState<any | null>(null);
  const [splitModal, setSplitModal] = useState<any>(null);
  const [rebalanceModal, setRebalanceModal] = useState<any>(null);
  const [editingExpense, setEditingExpense] = useState<any>(null);
  const [createDefaults, setCreateDefaults] = useState<any>(null);
  const [expandedRows, setExpandedRows] = useState<Record<number, any[]>>({});
  // Espelho de `expandedRows` em ref. O loadExpenses precisa saber o que estava
  // aberto para reabrir depois do fetch, mas nao pode DEPENDER de expandedRows:
  // ele proprio chama setExpandedRows, e o useEffect que dispara o load usa essas
  // deps, entao a dependencia direta viraria um loop de requisicoes.
  const expandedIdsRef = useRef<number[]>([]);
  useEffect(() => { expandedIdsRef.current = Object.keys(expandedRows).map(Number); }, [expandedRows]);
  const [loadingChildren, setLoadingChildren] = useState<Record<number, boolean>>({});
  const [selectedExpenses, setSelectedExpenses] = useState<Set<number>>(new Set());
  const [groupModal, setGroupModal] = useState(false);

  // Recurring state
  const [recurringExpenses, setRecurringExpenses] = useState<any[]>([]);
  const [recurringModal, setRecurringModal] = useState<'create' | 'edit' | null>(null);
  const [editingRecurring, setEditingRecurring] = useState<any>(null);
  const [recSort, toggleRecSort] = useSortState({ col: 'amount', dir: 'desc' });
  const [recSearch, setRecSearch] = useState('');
  const [recFilter, setRecFilter] = useState<'all' | 'active' | 'inactive'>('active');

  // Suppliers state
  const [supplierSearch, setSupplierSearch] = useState('');
  const [supplierModal, setSupplierModal] = useState<'create' | 'edit' | null>(null);
  const [editingSupplier, setEditingSupplier] = useState<any>(null);
  const [supplierSort, toggleSupplierSort] = useSortState({ col: 'name', dir: 'asc' });

  // Category modal
  const [categoryModal, setCategoryModal] = useState<'create' | 'edit' | null>(null);
  const [editingCategory, setEditingCategory] = useState<any>(null);

  // CSV import modal
  const [csvModal, setCsvModal] = useState(false);
  const [cardStatementModal, setCardStatementModal] = useState(false);
  const [excelExporting, setExcelExporting] = useState(false);

  // Budget state
  const [budgetMonth, setBudgetMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [budgetData, setBudgetData] = useState<any[]>([]);
  const [budgetInputs, setBudgetInputs] = useState<Record<number, string>>({});
  const [budgetLoading, setBudgetLoading] = useState(false);

  // Partners state
  const [partnerMonth, setPartnerMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [partnerData, setPartnerData] = useState<any[]>([]);
  const [partnerLoading, setPartnerLoading] = useState(false);
  const [allMonthsPartner, setAllMonthsPartner] = useState(false);

  // Debounce search text to avoid excessive API calls
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchText), 400);
    return () => clearTimeout(timer);
  }, [searchText]);

  // Projected expenses for current month (from recurring templates not yet generated)
  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  // Carrega filhas em cascata a partir dos ids que o usuario tinha abertos,
  // descendo enquanto alguma filha carregada tambem estiver na lista de desejados
  // (a hierarquia vai a 4 niveis). Teto de requisicoes para um grupo enorme nao
  // disparar dezenas de chamadas ao trocar de mes.
  const reabrirGrupos = useCallback(async (desejados: Set<number>, raizesDisponiveis: number[]) => {
    const TETO = 20;
    const reaberto: Record<number, any[]> = {};
    let fronteira = raizesDisponiveis.filter(id => desejados.has(id));
    let gastas = 0;
    while (fronteira.length > 0 && gastas < TETO) {
      const proxima: number[] = [];
      for (const id of fronteira) {
        if (gastas >= TETO) break;
        gastas++;
        try {
          const filhas = await expensesService.getChildren(id);
          reaberto[id] = filhas;
          for (const f of filhas) if (desejados.has(f.id)) proxima.push(f.id);
        } catch { /* reabrir e conveniencia; falha nao quebra a lista */ }
      }
      fronteira = proxima;
    }
    return reaberto;
  }, []);

  // ERRO NÃO É VAZIO (07/10/2026): as cargas desta tela só logavam. Com a API fora, a lista dizia "Nenhuma despesa
  // encontrada" com os cartões em R$ 0,00, as recorrentes "Nenhuma despesa recorrente cadastrada", os sócios
  // "Nenhum socio…", os fornecedores "Nenhum fornecedor encontrado", e o orçamento mostrava "Realizado R$ 0,00"
  // com os campos em branco, convidando a gravar por cima. Agora a falha vira estado (design/useEstadoDaCarga) e o
  // dado só é gravado quando a chamada dá certo: recarga que falha não apaga o que estava na tela.
  const { rodar: rodarCargaDasDespesas, ...cargaDasDespesas } = useEstadoDaCarga();
  // A LISTA VALE SÓ PARA O RECORTE EM QUE FOI LIDA (D1, 10/10/2026). Trocar mês, categoria, fornecedor, conta, cliente
  // ou a busca e a carga falhar deixava a lista do recorte ANTERIOR com o rótulo do novo, e os cartões somavam ela:
  // "Pagar", "Desmembrar", "Agrupar" e "Excluir" agiam sobre outro recorte. Agora a tela diz que este não carregou.
  const chaveDoRecorte = JSON.stringify({ p: periodToParams(periodFilter), c: categoryFilter, f: supplierFilter, s: statusFilter, b: bankAccountFilter, cl: clientFilter, q: debouncedSearch, x: flatMode });
  const recorteAtualRef = useRef(chaveDoRecorte);
  recorteAtualRef.current = chaveDoRecorte;
  const [despesasDoRecorte, setDespesasDoRecorte] = useState<string | null>(null);
  const loadExpenses = useCallback(async () => {
    const chave = recorteAtualRef.current;
    await rodarCargaDasDespesas(async () => {
      setSelectedExpenses(new Set());
      // O que estava expandido ANTES do reload. Antes daqui saia um
      // setExpandedRows({}), entao trocar o mes (ou qualquer filtro) fechava
      // todos os grupos que o usuario tinha aberto, e era preciso reabrir um a um.
      const abertosAntes = new Set(expandedIdsRef.current);
      const pParams = periodToParams(periodFilter);
      const params: any = {};
      if (pParams.month) params.month = pParams.month;
      if (pParams.startDate) params.startDate = pParams.startDate;
      if (pParams.endDate) params.endDate = pParams.endDate;
      if (categoryFilter) params.categoryId = Number(categoryFilter);
      if (supplierFilter) params.supplierId = Number(supplierFilter);
      if (statusFilter) params.status = statusFilter === 'Pendente' ? 'Pendente,Parcial' : statusFilter;
      if (bankAccountFilter) params.bankAccountId = Number(bankAccountFilter);
      if (clientFilter) params.clientId = Number(clientFilter);
      if (debouncedSearch) params.search = debouncedSearch;
      // Modo varredura: o backend ja aceita includeChildren e devolve filhas como
      // linhas de primeiro nivel, para cacar uma filha vencida sem abrir grupo.
      if (flatMode) params.includeChildren = 'true';
      const data = await expensesService.getAll(params);
      // Resposta de um recorte que já saiu da tela não entra.
      if (recorteAtualRef.current !== chave) return;
      const lista = Array.isArray(data) ? data : data.data || [];
      setExpenses(lista);
      setDespesasDoRecorte(chave);

      // Achatado, nao ha grupo para reabrir: cada lancamento ja e uma linha.
      if (flatMode) { setExpandedRows({}); return; }

      const idsDaLista = lista.map((e: any) => e.id);

      // Buscando, ja abre os agrupamentos. O backend devolve o PAI quando o texto
      // casa com a descricao de uma FILHA, mas nao diz qual: a linha vinha sem
      // nenhuma marca e o usuario tinha de adivinhar que precisava expandir.
      // Teto de 10 para uma busca ampla nao disparar dezenas de requisicoes.
      if (params.search) {
        setExpandedRows({});
        const comFilhas = lista.filter((e: any) => Number(e.childCount || 0) > 0).slice(0, 10);
        for (const pai of comFilhas) {
          try {
            const filhas = await expensesService.getChildren(pai.id);
            setExpandedRows(prev => ({ ...prev, [pai.id]: filhas }));
          } catch { /* expandir e conveniencia; falha nao quebra a lista */ }
        }
        return;
      }

      // Reabre so o que continua existindo no novo recorte. O que sumiu do filtro
      // e descartado, senao expandedRows cresceria sem limite ao navegar meses.
      setExpandedRows(abertosAntes.size > 0 ? await reabrirGrupos(abertosAntes, idsDaLista) : {});
    });
  }, [periodFilter, categoryFilter, supplierFilter, statusFilter, bankAccountFilter, clientFilter, debouncedSearch, flatMode, reabrirGrupos, rodarCargaDasDespesas]);

  const { rodar: rodarCargaDaBase, ...cargaDaBase } = useEstadoDaCarga();
  const loadBase = useCallback(async () => {
    await rodarCargaDaBase(async () => {
      const [cats, supps, banks, cls] = await Promise.all([
        expenseCategoriesService.getAll(),
        suppliersService.getAll(),
        bankAccountsService.getAll(),
        clientsService.getAll(),
      ]);
      setCategories(Array.isArray(cats) ? cats : []);
      setSuppliers(Array.isArray(supps) ? supps : supps.data || []);
      setBankAccounts(Array.isArray(banks) ? banks : []);
      setClients(Array.isArray(cls) ? cls : []);
    });
  }, [rodarCargaDaBase]);

  const { rodar: rodarCargaDasRecorrentes, ...cargaDasRecorrentes } = useEstadoDaCarga();
  const loadRecurring = useCallback(async () => {
    await rodarCargaDasRecorrentes(async () => {
      const data = await recurringExpensesService.getAll();
      setRecurringExpenses(Array.isArray(data) ? data : []);
    });
  }, [rodarCargaDasRecorrentes]);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([loadBase(), loadExpenses(), loadRecurring()]);
      setLoading(false);
    };
    init();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!loading) loadExpenses();
  }, [periodFilter, categoryFilter, supplierFilter, statusFilter, bankAccountFilter, clientFilter, debouncedSearch, flatMode]); // eslint-disable-line react-hooks/exhaustive-deps


  const listaDoRecorte = despesasDoRecorte === chaveDoRecorte;
  /** O que a tela pode mostrar, contar e oferecer: a lista do recorte de agora, nunca a de outro. */
  const despesasDaTela = listaDoRecorte ? expenses : [];
  // Search is now server-side (backend filters including child expenses)
  const filteredExpenses = despesasDaTela;

  const sortedExpenses = [...filteredExpenses].sort((a: any, b: any) => {
    let valA = a[sort.col] ?? '';
    let valB = b[sort.col] ?? '';
    if (sort.col === 'category') { valA = a.category?.name || ''; valB = b.category?.name || ''; }
    if (sort.col === 'supplier') { valA = a.supplier?.name || ''; valB = b.supplier?.name || ''; }
    if (sort.col === 'client') { valA = a.client?.name || a.client?.tradeName || a.client?.companyName || ''; valB = b.client?.name || b.client?.tradeName || b.client?.companyName || ''; }
    if (sort.col === 'deductFromPartner') { valA = a.deductFromPartner?.name || ''; valB = b.deductFromPartner?.name || ''; }
    if (sort.col === 'bankAccount') { valA = a.bankAccount?.bankName || ''; valB = b.bankAccount?.bankName || ''; }
    if (sort.col === 'amount') { valA = parseFloat(valA) || 0; valB = parseFloat(valB) || 0; }
    if (typeof valA === 'number' && typeof valB === 'number') return sort.dir === 'asc' ? valA - valB : valB - valA;
    return sort.dir === 'asc' ? String(valA).localeCompare(String(valB)) : String(valB).localeCompare(String(valA));
  });

  // Summary stats (exclude cancelled) — use remaining amount (amount - paidAmount) for pending/overdue
  // Sem nenhuma carga boa, os cartões dizem "—" e não R$ 0,00 (ver loadExpenses).
  const semNumerosDeDespesas = !listaDoRecorte;
  const activeExpenses = despesasDaTela.filter(e => e.status !== 'Cancelado');
  const totalExpenses = activeExpenses.reduce((sum, e) => sum + (parseFloat(e.amount) || 0), 0);
  const totalPaid = activeExpenses.reduce((sum, e) => sum + (parseFloat(e.paidAmount) || 0), 0);
  // Contagem do card "Pago" tem de medir a MESMA coisa que o valor. O valor soma
  // `paidAmount` de todas as ativas, incluindo o parcial de uma Pendente/Parcial;
  // a contagem contava só `status === 'Pago'`. Uma despesa de R$ 1.000 com R$ 300
  // pagos exibia "PAGO · R$ 300,00 · 0": zero despesas pagas, R$ 300 pagos.
  const countWithPayment = activeExpenses.filter(e => (parseFloat(e.paidAmount) || 0) > 0).length;
  // Os cards derivam da lista JÁ FILTRADA pela API. Com filtro ativo o rótulo
  // "Total do Mês" passava a mostrar só o total do filtro, sem mudar o texto:
  // com a categoria "Software" selecionada, lia-se "Total do Mês R$ 18.400".
  const hasNonPeriodFilter = !!(statusFilter || categoryFilter || supplierFilter || bankAccountFilter || clientFilter || searchText);
  const totalPending = activeExpenses.filter(e => e.status === 'Pendente' || e.status === 'Parcial').reduce((sum, e) => sum + ((parseFloat(e.amount) || 0) - (parseFloat(e.paidAmount) || 0)), 0);
  const totalOverdue = activeExpenses.filter(e => e.status === 'Vencido').reduce((sum, e) => sum + ((parseFloat(e.amount) || 0) - (parseFloat(e.paidAmount) || 0)), 0);
  // Quanto ficou DE FORA do card Total por estar cancelado. A linha continua na
  // tabela, entao sem isso a soma dos valores visiveis nao bate com o topo e nao
  // ha nada explicando a diferenca.
  const cancelledExpenses = despesasDaTela.filter(e => e.status === 'Cancelado');
  const totalCancelled = cancelledExpenses.reduce((sum, e) => sum + (parseFloat(e.amount) || 0), 0);

  // ============ EXPENSE FORM ============

  // ============ PAY MODAL (with partial payment support) ============

  // ============ ATTACHMENTS MODAL ============
  const attachmentTypeLabels: Record<string, string> = {
    holerite: 'Holerite',
    'nfs-e': 'NFS-e',
    fatura_cartao: 'Fatura Cartao',
    guia_imposto: 'Guia Imposto',
    comprovante: 'Comprovante',
    outro: 'Outro',
  };
  const attachmentTypeOptions = Object.entries(attachmentTypeLabels);


  // ============ SPLIT EXPENSE MODAL ============


  // ============ GROUP EXPENSES MODAL ============

  // ============ RECURRING FORM ============

  // ============ SUPPLIER FORM ============

  // ============ CATEGORY FORM ============

  // ============ GENERATE RECURRING ============
  // D4: com vários meses ou por datas, gerava o mês CORRENTE calado, sem confirm e sem dizer o mês.
  const geracaoDeRecorrentes = mesParaGerarRecorrentes(periodFilter);
  const handleGenerateRecurring = async () => {
    const mes = geracaoDeRecorrentes.mes;
    if (!mes) return;
    const modelos = cargaDasRecorrentes.atualizadoEm ? recurringExpenses.filter((r: any) => r.isActive).length : null;
    if (!await confirmarPerigo(confirmacaoDeGerarRecorrentes(mes, modelos))) return;
    try {
      const result = await expensesService.generateRecurring(mes);
      showToast(`Recorrências de ${mes} geradas: ${result.generated || 0} despesas criadas`);
      await loadExpenses();
    } catch (err: any) {
      showToast(err.response?.data?.message || 'Erro ao gerar recorrências', 'error');
    }
  };

  const handleExportExcel = async () => {
    if (exportacao.motivo) return;
    setExcelExporting(true);
    try {
      const pParams = periodToParams(periodFilter);
      const blob = await expensesService.exportExcel(pParams.month || undefined, statusFilter);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Despesas_${pParams.month || 'todos'}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      showToast('Erro ao exportar Excel', 'error');
    }
    setExcelExporting(false);
  };

  /** Os pagamentos da despesa, lidos na hora do confirm. null = não deu para ler (a frase diz isso). */
  const pagamentosDaDespesa = async (id: number): Promise<any[] | null> => {
    const { valor } = await lerSecao(expensesService.getPayments(id));
    return Array.isArray(valor) ? valor : null;
  };

  const handleCancelExpense = async (expense: any) => {
    if (!await confirmarPerigo(confirmacaoDeSaidaDaDespesa('cancelar', expense, await pagamentosDaDespesa(expense.id)))) return;
    try {
      await expensesService.cancel(expense.id);
      showToast('Despesa cancelada');
      await loadExpenses();
    } catch (err: any) {
      showToast(err.response?.data?.message || 'Erro ao cancelar', 'error');
    }
  };

  const handleDeleteExpense = async (expense: any) => {
    if (!await confirmarPerigo(confirmacaoDeSaidaDaDespesa('excluir', expense, await pagamentosDaDespesa(expense.id)))) return;
    try {
      await expensesService.delete(expense.id);
      showToast('Despesa excluída');
      await loadExpenses();
    } catch (err: any) {
      // 409 = a despesa tem lançamentos dentro. O backend recusa de propósito:
      // a FK era ON DELETE SET NULL, então apagar o pai soltava as filhas como
      // despesas avulsas, cada uma somando sozinha no total do mês, sem nada na
      // tela indicando isso. Aqui a segunda confirmação diz o número exato.
      if (err.response?.status === 409) {
        if (await confirmarPerigo(`${err.response.data.message}\n\nConfirma excluir tudo?`)) {
          try {
            await expensesService.delete(expense.id, { cascade: true });
            showToast('Despesa e lançamentos excluídos');
            await loadExpenses();
          } catch (e: any) {
            showToast(e.response?.data?.message || 'Erro ao excluir', 'error');
          }
        }
        return;
      }
      showToast(err.response?.data?.message || 'Erro ao excluir', 'error');
    }
  };

  const toggleExpand = async (expenseId: number) => {
    if (expandedRows[expenseId]) {
      // Collapse
      const updated = { ...expandedRows };
      delete updated[expenseId];
      setExpandedRows(updated);
      return;
    }
    // Expand - fetch children
    setLoadingChildren(prev => ({ ...prev, [expenseId]: true }));
    try {
      const children = await expensesService.getChildren(expenseId);
      setExpandedRows(prev => ({ ...prev, [expenseId]: children }));
    } catch (err) {
      // Antes só logava: o clique em expandir não fazia nada, e o grupo parecia não ter lançamentos.
      showToast(`Não consegui abrir o grupo: ${mensagemDoErroDeCarga(err)}`, 'error');
    } finally {
      setLoadingChildren(prev => ({ ...prev, [expenseId]: false }));
    }
  };

  const toggleSelectExpense = (id: number) => {
    setSelectedExpenses(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedExpenses.size === sortedExpenses.length) {
      setSelectedExpenses(new Set());
    } else {
      setSelectedExpenses(new Set(sortedExpenses.map((e: any) => e.id)));
    }
  };

  const clearSelection = () => setSelectedExpenses(new Set());

  // D9: fornecedor, categoria e recorrência não são apagados no servidor: são DESATIVADOS (isActive = false). O botão e
  // o confirm diziam "Excluir" e não nomeavam qual.
  const handleDeleteRecurring = async (rec: any) => {
    if (!await confirmarPerigo(confirmacaoDeDesativar('recorrência', rec.description))) return;
    try {
      await recurringExpensesService.delete(rec.id);
      showToast('Recorrência desativada');
      await loadRecurring();
    } catch (err: any) {
      showToast(err.response?.data?.message || 'Erro ao excluir', 'error');
    }
  };

  const handleToggleRecurringActive = async (recurring: any) => {
    try {
      await recurringExpensesService.update(recurring.id, { isActive: !recurring.isActive });
      showToast(recurring.isActive ? 'Recorrência desativada' : 'Recorrência ativada');
      await loadRecurring();
    } catch (err: any) {
      showToast(err.response?.data?.message || 'Erro ao atualizar', 'error');
    }
  };

  const handleGenerateRecurringRange = async (recurring: any) => {
    const now = new Date();
    const defaultUntil = `${now.getFullYear()}-12`;
    const input = await pedirTexto({
      title: `Gerar parcelas de "${recurring.description}"`,
      message: `Última gerada: ${recurring.lastGeneratedMonth || '—'}`,
      label: 'Até qual mês? (AAAA-MM)',
      valorInicial: defaultUntil,
      linhaUnica: true,
      obrigatorio: true,
      validar: (t) => (/^\d{4}-\d{2}$/.test(t) ? null : 'Use AAAA-MM (ex.: 2026-12).'),
      confirmLabel: 'Gerar',
    });
    if (!input) return;
    const toMonth = input.trim();
    const last = recurring.lastGeneratedMonth as string | null;
    let fromMonth: string;
    if (last) {
      const [ly, lm] = last.split('-').map(Number);
      const nm = lm === 12 ? 1 : lm + 1;
      const ny = lm === 12 ? ly + 1 : ly;
      fromMonth = `${ny}-${String(nm).padStart(2, '0')}`;
    } else {
      fromMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    }
    if (fromMonth > toMonth) {
      showToast(`Já existe geração até ${last}. Nada a fazer.`, 'error');
      return;
    }
    try {
      const res = await recurringExpensesService.generateRange({
        fromMonth,
        toMonth,
        recurringExpenseId: recurring.id,
      });
      showToast(`${res.generated} parcela${res.generated !== 1 ? 's' : ''} gerada${res.generated !== 1 ? 's' : ''} (${fromMonth} → ${toMonth})`);
      await loadRecurring();
      await loadExpenses();
    } catch (err: any) {
      showToast(err.response?.data?.message || 'Erro ao gerar parcelas', 'error');
    }
  };

  const handleDeleteSupplier = async (sup: any) => {
    if (!await confirmarPerigo(confirmacaoDeDesativar('fornecedor', sup.name || sup.tradeName))) return;
    try {
      await suppliersService.delete(sup.id);
      showToast('Fornecedor desativado');
      const data = await suppliersService.getAll();
      setSuppliers(Array.isArray(data) ? data : data.data || []);
    } catch (err: any) {
      showToast(err.response?.data?.message || 'Erro ao excluir', 'error');
    }
  };

  const handleDeleteCategory = async (cat: any) => {
    if (!await confirmarPerigo(confirmacaoDeDesativar('categoria', cat.name))) return;
    try {
      await expenseCategoriesService.delete(cat.id);
      showToast('Categoria desativada');
      const data = await expenseCategoriesService.getAll();
      setCategories(Array.isArray(data) ? data : []);
    } catch (err: any) {
      showToast(err.response?.data?.message || 'Erro ao excluir', 'error');
    }
  };

  // Budget loading
  // O orçado é POR MÊS, e os campos dele levam a "Salvar". Ao trocar de mês, o do mês anterior sai ANTES de carregar:
  // senão, com a carga do mês novo falhando, a tela mostraria os valores de setembro sob o cabeçalho de outubro, e
  // Salvar gravaria setembro por cima de outubro. Pelo mesmo motivo, a resposta de um mês que já não é o da tela é
  // descartada (troca rápida de mês). Recarga do MESMO mês que falha mantém o que estava, com aviso.
  const mesDoOrcamentoNaTelaRef = useRef<string | null>(null);
  const mesDoOrcamentoPedidoRef = useRef<string | null>(null);
  const { rodar: rodarCargaDoOrcamento, ...cargaDoOrcamento } = useEstadoDaCarga();
  const loadBudget = async () => {
    setBudgetLoading(true);
    const pedido = budgetMonth;
    mesDoOrcamentoPedidoRef.current = pedido;
    // Sem dados do mês, as linhas (e com elas os campos e o Salvar) não aparecem; a próxima carga boa troca os campos inteiros.
    if (mesDoOrcamentoNaTelaRef.current !== pedido) setBudgetData([]);
    await rodarCargaDoOrcamento(async () => {
      const data = await expensesService.getBudgetVsActual(pedido);
      if (mesDoOrcamentoPedidoRef.current !== pedido) return;
      mesDoOrcamentoNaTelaRef.current = pedido;
      setBudgetData(Array.isArray(data) ? data : []);
      const inputs: Record<number, string> = {};
      (Array.isArray(data) ? data : []).forEach((b: any) => { inputs[b.categoryId] = String(b.budget || ''); });
      setBudgetInputs(inputs);
    });
    setBudgetLoading(false);
  };

  useEffect(() => {
    if (subView === 'budget') loadBudget();
  }, [subView, budgetMonth]); // eslint-disable-line react-hooks/exhaustive-deps

  // Partners loading
  // Os sócios são POR MÊS (ou "todos os meses"). Como o orçado (ver loadBudget): ao trocar o recorte, a lista do
  // recorte anterior sai antes de carregar, e a resposta de um recorte que já não é o da tela é descartada.
  // E é um LOTE (um pedido por sócio): o sócio cuja chamada falha não some calado, a tela diz "faltaram" (lerEmLote).
  const recorteDosSociosNaTelaRef = useRef<string | null>(null);
  const recorteDosSociosPedidoRef = useRef<string | null>(null);
  const { rodar: rodarCargaDosSocios, ...cargaDosSocios } = useEstadoDaCarga();
  const loadPartners = async () => {
    setPartnerLoading(true);
    const recorte = allMonthsPartner ? 'todos' : partnerMonth;
    recorteDosSociosPedidoRef.current = recorte;
    if (recorteDosSociosNaTelaRef.current !== recorte) setPartnerData([]);
    await rodarCargaDosSocios(async () => {
      // Find partner IDs: divisão de lucro category suppliers + deduction targets
      const proLaboreCategory = categories.find((c: any) => {
        const name = (c.name || '').toLowerCase();
        return name.includes('divisão de lucro') || name.includes('divisao de lucro') || name.includes('pró-labore') || name.includes('pro labore');
      });
      const partnerIds = new Set<number>();

      // Strategy 1: suppliers with pro-labore expenses (may be children)
      if (proLaboreCategory) {
        const proLaboreExpenses = await expensesService.getAll({ categoryId: proLaboreCategory.id, includeChildren: true });
        const plList = Array.isArray(proLaboreExpenses) ? proLaboreExpenses : proLaboreExpenses.data || [];
        plList.forEach((e: any) => { if (e.supplierId) partnerIds.add(e.supplierId); });
      }

      // Strategy 2: expenses with deductFromPartnerId (parent-level only is fine)
      const allExpenses = await expensesService.getAll({});
      const expList = Array.isArray(allExpenses) ? allExpenses : allExpenses.data || [];
      expList.forEach((e: any) => { if (e.deductFromPartnerId) partnerIds.add(e.deductFromPartnerId); });

      if (recorteDosSociosPedidoRef.current !== recorte) return;
      if (partnerIds.size === 0) { recorteDosSociosNaTelaRef.current = recorte; setPartnerData([]); return; }
      const month = allMonthsPartner ? undefined : partnerMonth;
      const nomeDoSocio = (id: number) => {
        const s = suppliers.find((x: any) => x.id === id);
        return String(s?.name || s?.tradeName || `sócio ${id}`);
      };
      const { lidos, resultado } = await lerEmLote(
        Array.from(partnerIds),
        (id) => expensesService.getPartnerDeductions(id, month),
        nomeDoSocio,
      );
      if (recorteDosSociosPedidoRef.current !== recorte) return resultado;
      recorteDosSociosNaTelaRef.current = recorte;
      setPartnerData(lidos.map((l) => l.valor));
      return resultado;
    });
    setPartnerLoading(false);
  };

  useEffect(() => {
    if (subView === 'partners') loadPartners();
  }, [subView, partnerMonth, allMonthsPartner]); // eslint-disable-line react-hooks/exhaustive-deps


  const handleSaveBudget = async (categoryId: number) => {
    const amount = parseFloat(budgetInputs[categoryId] || '0');
    if (isNaN(amount)) return;
    try {
      await expenseCategoriesService.setBudget(categoryId, { month: budgetMonth, budgetAmount: amount });
      showToast('Orçamento salvo');
      await loadBudget();
    } catch (err: any) {
      showToast(err.response?.data?.message || 'Erro ao salvar orçamento', 'error');
    }
  };

  const exportacao = exportacaoDasDespesas(periodFilter, hasNonPeriodFilter);

  // ============ CSV IMPORT MODAL ============


  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
        <span className="ml-3 text-slate-400">Carregando...</span>
      </div>
    );
  }

  const filteredSuppliers = suppliers.filter((s: any) => {
    if (!supplierSearch) return true;
    const q = supplierSearch.toLowerCase();
    return (s.name || '').toLowerCase().includes(q) || (s.tradeName || '').toLowerCase().includes(q) || (s.companyName || '').toLowerCase().includes(q) || (s.cnpj || '').includes(q);
  });

  const sortedSuppliers = [...filteredSuppliers].sort((a: any, b: any) => {
    let valA: string, valB: string;
    switch (supplierSort.col) {
      case 'category': valA = a.defaultCategory?.name || ''; valB = b.defaultCategory?.name || ''; break;
      case 'cnpj': valA = a.cnpj || a.cpf || ''; valB = b.cnpj || b.cpf || ''; break;
      default: valA = a[supplierSort.col] || ''; valB = b[supplierSort.col] || '';
    }
    return supplierSort.dir === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
  });

  const modalCtx: ExpenseModalCtx = { showToast, loadExpenses, loadBase, loadRecurring, suppliers, setSuppliers, categories, setCategories, expenses, bankAccounts, clients, periodFilter, currentMonthStr, attachmentTypeOptions, attachmentTypeLabels, clearSelection };

  return (
    <div className="space-y-6">
      {/* Toast */}
      {toast && (
        <div className={`fixed bottom-4 right-4 z-[100] px-4 py-3 rounded-lg border shadow-lg text-white ${toast.type === 'success' ? 'bg-central-elevado border-central-borda' : 'bg-red-500 border-red-600'}`}>
          {toast.message}
        </div>
      )}

      {/* Header with sub-view toggle */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Despesas</h2>
          <p className="text-slate-400 text-sm mt-1">Gerencie despesas, recorrências e fornecedores</p>
        </div>
        <div className="flex items-center gap-2">
          {despesasDaTela.filter(e => e.status === 'Vencido').length > 0 && (
            <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-red-500/20 text-red-400" data-testid="selo-vencidas">
              {despesasDaTela.filter(e => e.status === 'Vencido').length} vencida(s)
            </span>
          )}
          <button onClick={() => setSubView('expenses')} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${subView === 'expenses' ? 'bg-slate-600 text-white font-semibold' : 'bg-slate-700 text-slate-300 hover:bg-slate-600'}`}>
            Despesas
          </button>
          <button onClick={() => setSubView('recurring')} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${subView === 'recurring' ? 'bg-slate-600 text-white font-semibold' : 'bg-slate-700 text-slate-300 hover:bg-slate-600'}`}>
            Recorrentes
          </button>
          <button onClick={() => setSubView('suppliers')} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${subView === 'suppliers' ? 'bg-slate-600 text-white font-semibold' : 'bg-slate-700 text-slate-300 hover:bg-slate-600'}`}>
            Fornecedores
          </button>
          <button onClick={() => setSubView('partners')} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${subView === 'partners' ? 'bg-slate-600 text-white font-semibold' : 'bg-slate-700 text-slate-300 hover:bg-slate-600'}`}>
            Sócios
          </button>
          <button onClick={() => setSubView('budget')} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${subView === 'budget' ? 'bg-slate-600 text-white font-semibold' : 'bg-slate-700 text-slate-300 hover:bg-slate-600'}`}>
            Orçamento
          </button>
        </div>
      </div>

      {/* ============ SUB-VIEW: EXPENSES ============ */}
      {subView === 'expenses' && (
        <>
          {/* Modo varredura: pai E filha vem na mesma lista, entao somar tudo
              contaria a mesma saida duas vezes. Os cards saem de cena em vez de
              exibir um total inflado que parece certo. */}
          {flatMode && (
            <div className={`bg-slate-800/50 border ${TONE_BORDER.progress} rounded-xl p-4 flex items-center justify-between`}>
              <div>
                <p className={`${TONE_TEXT.progress} text-xs uppercase font-semibold`}>Modo varredura</p>
                <p className="text-slate-300 text-sm mt-1">
                  Lançamentos aparecem como linhas próprias, junto dos grupos que os contêm.
                  Os totais ficam ocultos porque somariam pai e filha duas vezes.
                </p>
              </div>
              <button
                onClick={() => setFlatMode(false)}
                className="px-3 py-1.5 text-sm text-slate-200 border border-slate-600 rounded-lg hover:bg-slate-700 whitespace-nowrap"
              >
                Voltar aos totais
              </button>
            </div>
          )}

          {/* Sem nenhuma carga boa, os cartões dizem "—" e não R$ 0,00: 0 é uma afirmação (ver loadExpenses). */}
          {/* Cartões do topo (padrão das listas no Financeiro, 22/10/2026): o CartaoDoTopo, filtro de status com a barra
              primary no escolhido (saiu o anel laranja) e os números em branco. O Vencido leva a barra vermelha quando há. */}
          {!flatMode && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <CartaoDoTopo
              rotulo={hasNonPeriodFilter ? 'Total do filtro' : 'Total do mês'}
              valor={<span className={semNumerosDeDespesas ? 'text-slate-500' : ''}>{semNumerosDeDespesas ? '—' : `R$ ${fmt(totalExpenses)}`}</span>}
              sub={<>
                {semNumerosDeDespesas ? '—' : activeExpenses.length} despesas
                {cancelledExpenses.length > 0 && ` · R$ ${fmt(totalCancelled)} cancelado fora do total`}
              </>}
              ativo={statusFilter === ''}
              onClick={() => setStatusFilter('')}
            />
            <CartaoDoTopo
              rotulo="Pendente"
              valor={<span className={semNumerosDeDespesas ? 'text-slate-500' : ''}>{semNumerosDeDespesas ? '—' : `R$ ${fmt(totalPending)}`}</span>}
              sub={<>{semNumerosDeDespesas ? '—' : despesasDaTela.filter(e => e.status === 'Pendente' || e.status === 'Parcial').length} despesas</>}
              ativo={statusFilter === 'Pendente'}
              onClick={() => setStatusFilter(statusFilter === 'Pendente' ? '' : 'Pendente')}
            />
            <CartaoDoTopo
              rotulo="Pago"
              valor={<span className={semNumerosDeDespesas ? 'text-slate-500' : ''}>{semNumerosDeDespesas ? '—' : `R$ ${fmt(totalPaid)}`}</span>}
              sub={<>{semNumerosDeDespesas ? '—' : countWithPayment} com pagamento</>}
              ativo={statusFilter === 'Pago'}
              onClick={() => setStatusFilter(statusFilter === 'Pago' ? '' : 'Pago')}
            />
            <CartaoDoTopo
              rotulo="Vencido"
              valor={<span className={!semNumerosDeDespesas && totalOverdue > 0 ? '' : 'text-slate-500'}>{semNumerosDeDespesas ? '—' : `R$ ${fmt(totalOverdue)}`}</span>}
              sub={<>{semNumerosDeDespesas ? '—' : despesasDaTela.filter(e => e.status === 'Vencido').length} despesas</>}
              ativo={statusFilter === 'Vencido'}
              barra={!semNumerosDeDespesas && totalOverdue > 0 ? 'danger' : null}
              onClick={() => setStatusFilter(statusFilter === 'Vencido' ? '' : 'Vencido')}
            />
          </div>
          )}

          {/* Filtros no padrao do FiscalTab: uma linha so, sem rotulo em cima de
              cada campo. O rotulo repetia o que a primeira opcao ja diz ("Todas",
              "Todos") e custava uma linha inteira de altura. */}
          <ListToolbar
            period={
              <PeriodSelector
                value={periodFilter}
                onChange={setPeriodFilter}
                onRefresh={() => loadExpenses()}
              />
            }
            search={<ToolbarSearch value={searchText} onChange={setSearchText} placeholder="Buscar descrição..." widthClass="w-56" />}
            filters={<>
              <select className={`px-3 py-2 ${campo} rounded-lg text-sm`} value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)}>
                <option value="">Todas as categorias</option>
                {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              <select className={`px-3 py-2 ${campo} rounded-lg text-sm`} value={supplierFilter} onChange={e => setSupplierFilter(e.target.value)}>
                <option value="">Todos os fornecedores</option>
                {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
              <select className={`px-3 py-2 ${campo} rounded-lg text-sm`} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                <option value="">Todos os status</option>
                <option value="Pendente">Pendente</option>
                <option value="Parcial">Parcial</option>
                <option value="Pago">Pago</option>
                <option value="Vencido">Vencido</option>
                <option value="Cancelado">Cancelado</option>
              </select>
              <select className={`px-3 py-2 ${campo} rounded-lg text-sm`} value={bankAccountFilter} onChange={e => setBankAccountFilter(e.target.value)}>
                <option value="">Todas as contas</option>
                {bankAccounts.map(b => <option key={b.id} value={b.id}>{b.bankName}</option>)}
              </select>
              <select className={`px-3 py-2 ${campo} rounded-lg text-sm max-w-[200px]`} value={clientFilter} onChange={e => setClientFilter(e.target.value)}>
                <option value="">Todos os clientes</option>
                {clients.map(c => <option key={c.id} value={c.id}>{nomeDeExibicao(c)}</option>)}
              </select>
              <button
                onClick={() => setFlatMode(v => !v)}
                aria-pressed={flatMode}
                title="Traz os lançamentos de dentro dos grupos como linhas próprias. Útil para achar uma filha vencida sem abrir grupo por grupo."
                className={`px-3 py-2 rounded-lg border text-sm font-medium flex items-center gap-2 transition-colors ${
                  flatMode
                    ? 'bg-blue-600 border-blue-500 text-white'
                    : 'bg-slate-700 border-slate-600 text-slate-300 hover:bg-slate-600'
                }`}
              >
                <Icon name="list" className="w-4 h-4" />
                Mostrar lançamentos
              </button>
            </>}
            secondRow={<>
              <button onClick={() => { setExpenseModal('create'); setEditingExpense(null); setCreateDefaults(null); }} className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-strong text-sm font-medium flex items-center gap-2">
                <Icon name="plus" className="w-4 h-4" /> Nova Despesa
              </button>
              <button onClick={() => setCardStatementModal(true)} className="px-4 py-2 bg-slate-700 text-slate-300 rounded-lg hover:bg-slate-600 text-sm font-medium flex items-center gap-2">
                <Icon name="credit-card" className="w-4 h-4" /> Importar Fatura
              </button>
              {/* D10 (10/10/2026): o menu é o MaisAcoes com rótulo, sem o fundo de tela inteira montado à mão. As frases das
                  travas (D4 e D5 da #426) seguem visíveis dentro do item. */}
              <MaisAcoes
                rotulo="Mais ações"
                ancora="esquerda"
                itens={[
                  {
                    label: (<span>
                      {excelExporting ? 'Exportando...' : 'Exportar Excel'}
                      {(exportacao.motivo || exportacao.aviso) && <span className={`block text-[11px] ${TONE_TEXT.warning}`} data-testid="exportacao-aviso">{exportacao.motivo || exportacao.aviso}</span>}
                    </span>),
                    onClick: handleExportExcel,
                    disabled: excelExporting || !!exportacao.motivo,
                    title: exportacao.motivo || exportacao.aviso || undefined,
                  },
                  {
                    label: (<span>
                      Gerar Recorrentes{geracaoDeRecorrentes.mes ? ` de ${geracaoDeRecorrentes.mes}` : ''}
                      {geracaoDeRecorrentes.motivo && <span className={`block text-[11px] ${TONE_TEXT.warning}`} data-testid="gerar-recorrentes-motivo">{geracaoDeRecorrentes.motivo}</span>}
                    </span>),
                    onClick: handleGenerateRecurring,
                    disabled: !!geracaoDeRecorrentes.motivo,
                    title: geracaoDeRecorrentes.motivo || undefined,
                  },
                  { label: 'Nova Categoria', onClick: () => { setCategoryModal('create'); setEditingCategory(null); } },
                  ...(isAdmin() ? [{ label: 'Importar CSV', onClick: () => setCsvModal(true) }] : []),
                ]}
              />
            </>}
          />

          {/* Selection action bar */}
          {selectedExpenses.size > 0 && (() => {
            // Soma da seleção: o que falta pagar (desconta pagamento parcial) —
            // é o valor a resgatar da aplicação pra liquidar o dia.
            const sel = despesasDaTela.filter((e: any) => selectedExpenses.has(e.id));
            const totalSel = sel.reduce((sum: number, e: any) => sum + (parseFloat(String(e.amount)) || 0), 0);
            const aPagar = sel.reduce((sum: number, e: any) => {
              if (e.status === 'Pago' || e.status === 'Cancelado') return sum;
              return sum + Math.max(0, (parseFloat(String(e.amount)) || 0) - (parseFloat(String(e.paidAmount)) || 0));
            }, 0);
            return (
            <div className="bg-slate-800/50 border border-orange-600/60 rounded-lg p-3 flex items-center justify-between">
              <span className="text-sm text-orange-400">
                {selectedExpenses.size} {selectedExpenses.size === 1 ? 'despesa selecionada' : 'despesas selecionadas'}
                <span className="ml-3 text-white font-semibold">A pagar: R$ {fmt(aPagar)}</span>
                {Math.abs(totalSel - aPagar) > 0.005 && (
                  <span className="ml-2 text-xs text-slate-400">(total R$ {fmt(totalSel)})</span>
                )}
              </span>
              <div className="flex gap-2">
                <button onClick={clearSelection} className="px-3 py-1.5 text-sm text-slate-400 hover:text-white border border-slate-600 rounded-lg hover:bg-slate-700">
                  Limpar
                </button>
                {selectedExpenses.size >= 2 && (
                  <button onClick={() => setGroupModal(true)} className="px-3 py-1.5 text-sm text-white bg-primary rounded-lg hover:bg-primary-strong font-medium">
                    Agrupar {selectedExpenses.size} despesas
                  </button>
                )}
              </div>
            </div>
            );
          })()}

          {/* Table */}
          <EstadoDaLista
            carga={listaDoRecorte ? cargaDasDespesas : { ...cargaDasDespesas, estado: cargaDasDespesas.estado === 'erro' ? 'erro' : 'carregando', atualizadoEm: null }}
            temDados={listaDoRecorte && despesasDaTela.length > 0}
            oQue={`as despesas de ${rotuloDoRecorte(periodFilter)}${hasNonPeriodFilter ? ' (com os filtros escolhidos)' : ''}`}
            onTentarDeNovo={loadExpenses} vazio={A_PROPRIA_LISTA_DIZ_O_VAZIO}>
          {/* A casca das listas, SEM overflow de rolagem: o cabeçalho fixa no <main> e o ⋯ é fixo. As linhas que expandem
              (filhas e netas) seguem na mesma tabela, e o que estava aberto continua aberto ao navegar de mês. */}
          <CascaDaLista>
              <TabelaDaLista>
                <CabecalhoDaLista>
                    <ColunaDaLista largura="w-10" rotulo={
                      <input
                        type="checkbox"
                        checked={selectedExpenses.size === sortedExpenses.length && sortedExpenses.length > 0}
                        onChange={toggleSelectAll}
                        className="rounded border-slate-600 bg-slate-700 focus:ring-primary focus:ring-offset-0 cursor-pointer"
                      />
                    } />
                    <ColunaDaLista rotulo={<span className="sr-only">Expandir</span>} largura="w-10" />
                    {/* Vencimento primeiro: e por ele que a fila de pagamento e
                        varrida, e ja era o sort default. Categoria, Conta, Parcela,
                        Metodo e Data Pgto sairam de coluna propria e viraram a
                        segunda linha da celula vizinha, no padrao do FiscalTab. */}
                    {([
                      ['Vencimento', 'dueDate', 'w-36', 'esquerda'],
                      ['Descrição', 'description', '', 'esquerda'],
                      ['Fornecedor', 'supplier', 'w-44', 'esquerda'],
                      ['Valor', 'amount', 'w-36', 'direita'],
                      ['Status', 'status', 'w-28', 'esquerda'],
                      ['Rateio', 'client', 'w-40', 'esquerda'],
                    ] as const).map(([rotulo, col, largura, alinhamento]) => (
                      <ColunaDaLista key={col} rotulo={rotulo} largura={largura} alinhamento={alinhamento}
                        ordem={sort.col === col ? sort.dir : false} onOrdenar={() => toggleSort(col)} />
                    ))}
                    <ColunaDaLista rotulo={<span className="sr-only">Ações</span>} largura="w-36" />
                </CabecalhoDaLista>
                <tbody className="divide-y divide-slate-700/50">
                  {sortedExpenses.length === 0 ? (
                    <tr><td colSpan={9} className="px-4 py-8 text-center text-slate-500">Nenhuma despesa encontrada</td></tr>
                  ) : sortedExpenses.map((exp: any) => {
                    const chipClass = statusChipClass[exp.status] || statusChipClass.Cancelado;
                    // Cancelada continua listada mas NAO entra nos 4 cards do topo
                    // (activeExpenses filtra Cancelado). O unico sinal disso era o chip
                    // cinza, que e o mesmo cinza do texto secundario da tabela.
                    const isCancelled = exp.status === 'Cancelado';
                    // A vencida diz pelo prazo (a peça de Recebíveis) e pela barra no tom do atraso; o resto, só a data.
                    const vencida = exp.status === 'Vencido';
                    return (
                      <React.Fragment key={exp.id}>
                      <LinhaDaLista destacada={!!expandedRows[exp.id]} apagada={isCancelled}>
                        <td className={`${CELULA} ${barraDoEstado(vencida ? 'danger' : null)}`}>
                          <input
                            type="checkbox"
                            checked={selectedExpenses.has(exp.id)}
                            onChange={() => toggleSelectExpense(exp.id)}
                            className="rounded border-slate-600 bg-slate-700 focus:ring-primary focus:ring-offset-0 cursor-pointer"
                          />
                        </td>
                        <td className="px-2 py-3" data-expandir>
                          {/* No modo varredura as filhas ja sao linhas proprias:
                              expandir mostraria as mesmas linhas duas vezes. */}
                          {exp.childCount > 0 && !flatMode ? (
                            <button
                              onClick={(e) => { e.stopPropagation(); toggleExpand(exp.id); }}
                              className="p-1 text-slate-400 hover:text-white transition-colors"
                            >
                              {loadingChildren[exp.id] ? (
                                <Icon name="loader" className="w-4 h-4 animate-spin" />
                              ) : expandedRows[exp.id] ? (
                                <Icon name="chevron-down" className="w-4 h-4" />
                              ) : (
                                <Icon name="chevron-right" className="w-4 h-4" />
                              )}
                            </button>
                          ) : (
                            <span className="w-4 h-4 block" />
                          )}
                        </td>
                        {/* VENCIMENTO: a data de pagamento vira segunda linha, so
                            quando existe. Como coluna propria ela ficava com "—"
                            na maioria das linhas, gastando largura para dizer nada. */}
                        {/* No modo achatado a filha vem como linha de topo, e o
                            dueDate dela e a data da compra. O backend manda o
                            `rootDueDate` (vencimento da raiz) justamente ai. */}
                        <CelulaPrazo vencimento={exp.rootDueDate || exp.dueDate} quitado={!vencida} rotuloQuitado={null} mostrarData>
                          {exp.rootDueDate && exp.dueDate &&
                            String(exp.rootDueDate).slice(0, 10) !== String(exp.dueDate).slice(0, 10) && (
                            <div className="text-xs text-slate-500" title={TITULO_DATA_LANCAMENTO}>
                              {LABEL_LANCADO} {fmtDate(exp.dueDate)}
                            </div>
                          )}
                          {exp.paymentDate && (
                            <div className="text-xs text-slate-500">pago {fmtDate(exp.paymentDate)}</div>
                          )}
                        </CelulaPrazo>
                        {/* DESCRICAO: categoria e parcela descem para a segunda
                            linha, no lugar das duas colunas que ocupavam. */}
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            {flatMode && exp.parentExpenseId && (
                              <span className="text-slate-500 text-xs" title={`Lançamento dentro do grupo #${exp.parentExpenseId}`}>
                                {SETA_FILHA} #{exp.parentExpenseId}
                              </span>
                            )}
                            <span className="text-slate-500 text-xs font-mono">{exp.id}</span>
                            <span className={`text-sm text-white font-medium ${isCancelled ? 'line-through' : ''}`}>{exp.description}</span>
                            {isCancelled && (
                              <span className="px-1.5 py-0.5 text-xs rounded bg-slate-600/50 text-slate-400" title="Cancelada: não entra nos totais do topo">
                                fora do total
                              </span>
                            )}
                            {exp.childCount > 0 && (
                              <span className={`px-1.5 py-0.5 text-xs rounded ${TONE_BADGE.neutral}`}>
                                {exp.childCount} {exp.childCount === 1 ? 'item' : 'itens'}
                              </span>
                            )}
                            {/* Parcela em badge: "29/37" diz se a despesa ainda vai
                                se repetir, e como texto solto no meio da segunda
                                linha ela se perdia entre categoria e método. */}
                            <InstallmentBadge expense={exp} />
                          </div>
                          <div className="flex items-center gap-1.5 mt-0.5 text-xs text-slate-500">
                            {exp.category && (
                              <span className="inline-flex items-center gap-1">
                                <span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: exp.category.color || '#f97316' }} />
                                {exp.category.name}
                              </span>
                            )}
                            {exp.paymentMethod && <span>· {paymentMethodLabels[exp.paymentMethod]}</span>}
                          </div>
                        </td>
                        {/* Fornecedor estreito, com popover dos dados. Filtrar por
                            ele continua possivel, mas de dentro do popover: clicar
                            no nome e mais provavelmente "quem e este?" do que
                            "recorta a lista inteira". */}
                        <td className="px-4 py-3 text-sm truncate">
                          <SupplierPopover
                            supplier={exp.supplier}
                            onFilter={() => setSupplierFilter(String(exp.supplierId || exp.supplier?.id))}
                            onEdit={() => { setEditingSupplier(exp.supplier); setSupplierModal('edit'); }}
                          />
                        </td>
                        {/* VALOR: a conta bancaria vira a segunda linha, colorida
                            com a cor da propria conta. */}
                        <td className="px-4 py-3 text-right">
                          <div className={`text-sm text-white font-medium whitespace-nowrap ${isCancelled ? 'line-through' : ''}`}>
                            <VariableMark expense={exp} />R$ {fmt(exp.amount)}
                          </div>
                          <PlannedHint expense={exp} />
                          {exp.bankAccount && (
                            <div className="text-xs" style={{ color: exp.bankAccount.color || '#34d399' }}>
                              {exp.bankAccount.bankName}
                            </div>
                          )}
                          {parseFloat(exp.paidAmount) > 0 && exp.status !== 'Pago' && (
                            <div className="flex items-center gap-1.5 mt-1">
                              <div className={`flex-1 bg-slate-600 rounded-full h-1.5 ${TONE_TEXT.success}`}>
                                <div className="bg-current h-1.5 rounded-full" style={{ width: `${Math.min(100, (parseFloat(exp.paidAmount) / parseFloat(exp.amount)) * 100)}%` }} />
                              </div>
                              <span className={`text-xs ${TONE_TEXT.success} whitespace-nowrap`}>{fmt(exp.paidAmount)}</span>
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${chipClass}`}>
                            {exp.status}
                          </span>
                        </td>
                        {/* RATEIO: Cliente e Deducao eram duas colunas de largura
                            fixa (110px e 100px) quase sempre vazias. Juntas numa
                            so, com glifo diferente para nao depender so da cor. */}
                        <td className="px-4 py-3">
                          {exp.client && (
                            <ClientPopover clientId={exp.clientId || exp.client?.id}>
                              <div className="text-xs text-blue-400 truncate max-w-[140px]" title={`Repassado ao cliente: ${exp.client.name || exp.client.tradeName || exp.client.companyName}`}>
                                {GLIFO_CLIENTE} {exp.client.name || exp.client.tradeName || exp.client.companyName}
                              </div>
                            </ClientPopover>
                          )}
                          {exp.deductFromPartner && (
                            <div className="text-xs text-purple-400 truncate max-w-[140px]" title={`Deduzido do sócio: ${exp.deductFromPartner.tradeName || exp.deductFromPartner.name}`}>
                              {GLIFO_SOCIO} {exp.deductFromPartner.tradeName || exp.deductFromPartner.name}
                            </div>
                          )}
                          {!exp.client && !exp.deductFromPartner && <span className="text-slate-600 text-xs">{TRACO}</span>}
                        </td>
                        <td className="px-3 py-3">
                          <div className="flex items-center justify-end gap-1">
                            <button onClick={() => setReceiptModal(exp)} className={`relative p-2 rounded-lg ${exp.attachmentCount > 0 ? 'text-slate-200 hover:bg-slate-700' : 'text-slate-500 hover:text-slate-300 hover:bg-slate-600/50'}`} title={exp.attachmentCount > 0 ? `${exp.attachmentCount} anexo(s)` : 'Anexos'}>
                              <Icon name="paperclip" className="w-4 h-4" />
                              {exp.attachmentCount > 0 && (
                                <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 flex items-center justify-center text-[10px] font-bold rounded-full bg-slate-500 text-white">
                                  {exp.attachmentCount}
                                </span>
                              )}
                            </button>
                            {exp.status !== 'Cancelado' && (
                              <button onClick={() => { setEditingExpense(exp); setExpenseModal('edit'); }} className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg" title="Editar">
                                <Icon name="edit" className="w-4 h-4" />
                              </button>
                            )}
                            {/* D10 (10/10/2026): o "⋯" da linha é o MaisAcoes com âncora FIXA. A tabela tem overflow-x-auto, e
                                menu absolute nas últimas linhas era recortado pela caixa (por isso este era fixed à mão).
                                Pagar saiu da linha (o pagamento entra pela conciliação); o caminho manual fica aqui, que
                                é o necessário para pagamento parcial. Cancelar (reversível) e Excluir (não) eram
                                vizinhos e idênticos: o traço e o vermelho do Excluir fazem a distinção. */}
                            <MaisAcoes
                              ancoragem="fixa"
                              itens={[
                                ...(exp.status !== 'Cancelado' ? [{ label: exp.status === 'Pago' ? 'Ver pagamentos' : 'Pagar', onClick: () => setPayModal(exp) }] : []),
                                ...(exp.childCount > 0 && exp.status !== 'Cancelado' ? [{
                                  label: 'Adicionar item ao grupo',
                                  onClick: () => {
                                    setCreateDefaults({
                                      parentExpenseId: exp.id,
                                      categoryId: exp.categoryId,
                                      supplierId: exp.supplierId,
                                      bankAccountId: exp.bankAccountId,
                                      referenceMonth: exp.referenceMonth,
                                      clientId: exp.clientId,
                                    });
                                    setExpenseModal('create');
                                  },
                                }] : []),
                                ...(exp.status !== 'Cancelado' ? [{ label: 'Desmembrar', onClick: () => setSplitModal(exp) }] : []),
                                ...(exp.status !== 'Cancelado' && Number(exp.childCount || 0) >= 2 ? [{ label: 'Re-balancear parcelas', onClick: () => setRebalanceModal(exp) }] : []),
                                ...(exp.status !== 'Cancelado' ? [{ label: 'Cancelar', onClick: () => handleCancelExpense(exp) }] : []),
                                ...(isAdmin() ? [{ label: 'Excluir', danger: true, separadoAntes: true, onClick: () => handleDeleteExpense(exp) }] : []),
                              ]}
                            />
                          </div>
                        </td>
                      </LinhaDaLista>
                      {expandedRows[exp.id] && expandedRows[exp.id].map((child: any) => (
                        <React.Fragment key={`child-${child.id}`}>
                        <tr className="bg-sky-500/[0.07] border-l-[3px] border-sky-500/60 hover:bg-sky-500/[0.12] transition-colors">
                          <td className="px-2 py-2 w-8"></td>
                          <td className="px-2 py-2 w-8">
                            {child.childCount > 0 ? (
                              <button
                                onClick={(e) => { e.stopPropagation(); toggleExpand(child.id); }}
                                className="p-1 ml-1 text-slate-400 hover:text-white transition-colors"
                              >
                                {loadingChildren[child.id] ? (
                                  <Icon name="loader" className="w-3.5 h-3.5 animate-spin" />
                                ) : expandedRows[child.id] ? (
                                  <Icon name="chevron-down" className="w-3.5 h-3.5" />
                                ) : (
                                  <Icon name="chevron-right" className="w-3.5 h-3.5" />
                                )}
                              </button>
                            ) : (
                              <span className="block w-4 h-4 ml-1 border-l border-b border-slate-600 rounded-bl" />
                            )}
                          </td>
                          {/* Quem vence e a RAIZ, nao a linha. Numa fatura de cartao
                              o dueDate da linha e a data da COMPRA (parcela 10/12 de
                              out/2025 cai na fatura de agosto): mostrar aquilo sob o
                              titulo "Vencimento" fazia a coluna mentir. A data propria
                              vira segunda linha quando difere da raiz. */}
                          <td className="px-3 py-2 whitespace-nowrap">
                            <div className="text-sm text-slate-300">{exp.dueDate ? fmtDate(exp.dueDate) : TRACO}</div>
                            {child.dueDate && exp.dueDate && String(child.dueDate).slice(0, 10) !== String(exp.dueDate).slice(0, 10) && (
                              <div className="text-xs text-slate-500" title={TITULO_DATA_LANCAMENTO}>
                                {LABEL_LANCADO} {fmtDate(child.dueDate)}
                              </div>
                            )}
                            {child.paymentDate && <div className="text-xs text-slate-500">pago {fmtDate(child.paymentDate)}</div>}
                          </td>
                          <td className="px-4 py-2">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <span className="text-slate-500 text-xs font-mono">{child.id}</span>
                              <span className={`text-sm text-slate-300 ${child.status === 'Cancelado' ? 'line-through' : ''}`}>{child.description}</span>
                              {child.childCount > 0 && (
                                <span className={`px-1.5 py-0.5 text-xs rounded ${TONE_BADGE.neutral}`}>
                                  {child.childCount} {child.childCount === 1 ? 'item' : 'itens'}
                                </span>
                              )}
                              <InstallmentBadge expense={child} />
                            </div>
                            <div className="flex items-center gap-1.5 mt-0.5 text-xs text-slate-500">
                              {child.category && (
                                <span className="inline-flex items-center gap-1">
                                  <span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: child.category.color || '#f97316' }} />
                                  {child.category.name}
                                </span>
                              )}
                              {child.paymentMethod && <span>· {paymentMethodLabels[child.paymentMethod]}</span>}
                            </div>
                          </td>
                          <td className="px-4 py-2 text-sm truncate">
                            <SupplierPopover
                              supplier={child.supplier}
                              onFilter={() => setSupplierFilter(String(child.supplierId || child.supplier?.id))}
                              onEdit={() => { setEditingSupplier(child.supplier); setSupplierModal('edit'); }}
                            />
                          </td>
                          <td className="px-4 py-2 text-right">
                            <div className={`font-medium text-slate-300 text-sm whitespace-nowrap ${child.status === 'Cancelado' ? 'line-through' : ''}`}>
                              <VariableMark expense={child} />R$ {Number(child.amount).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                            </div>
                            <PlannedHint expense={child} />
                            {child.bankAccount && (
                              <div className="text-xs" style={{ color: child.bankAccount.color || '#34d399' }}>
                                {child.bankAccount.bankName}
                              </div>
                            )}
                            {parseFloat(child.paidAmount) > 0 && child.status !== 'Pago' && (
                              <div className="flex items-center gap-1.5 mt-1">
                                <div className={`flex-1 bg-slate-600 rounded-full h-1.5 ${TONE_TEXT.success}`}>
                                  <div className="bg-current h-1.5 rounded-full" style={{ width: `${Math.min(100, (parseFloat(child.paidAmount) / parseFloat(child.amount)) * 100)}%` }} />
                                </div>
                                <span className={`text-xs ${TONE_TEXT.success} whitespace-nowrap`}>{Number(child.paidAmount).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-2">
                            <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${statusChipClass[child.status] || statusChipClass.Cancelado}`}>{child.status}</span>
                          </td>
                          <td className="px-4 py-2">
                            {child.client && (
                              <ClientPopover clientId={child.clientId || child.client?.id}>
                                <div className="text-xs text-blue-400 truncate max-w-[140px]" title={`Repassado ao cliente: ${nomeDeExibicao(child.client)}`}>
                                  {GLIFO_CLIENTE} {nomeDeExibicao(child.client)}
                                </div>
                              </ClientPopover>
                            )}
                            {child.deductFromPartner && (
                              <div className="text-xs text-purple-400 truncate max-w-[140px]" title={`Deduzido do s\u00f3cio: ${child.deductFromPartner.tradeName || child.deductFromPartner.name}`}>
                                {GLIFO_SOCIO} {child.deductFromPartner.tradeName || child.deductFromPartner.name}
                              </div>
                            )}
                            {!child.client && !child.deductFromPartner && <span className="text-slate-600 text-xs">{TRACO}</span>}
                          </td>
                          {/* D1: o lápis à vista; a lixeira no ⋯, em perigo (só admin). */}
                          <CelulaAcoes
                            rotuloDoMenu={`Mais ações do lançamento ${child.id}`}
                            onEditar={() => { setEditingExpense(child); setExpenseModal('edit'); }}
                            Icon={Icon}
                            itens={isAdmin() ? [{ label: 'Excluir', danger: true, onClick: () => handleDeleteExpense(child) }] : []}
                          />
                        </tr>
                        {/* Level 3: Grandchildren */}
                        {expandedRows[child.id] && expandedRows[child.id].map((grandchild: any) => (
                          <tr key={`grandchild-${grandchild.id}`} className="bg-violet-500/[0.07] border-l-[3px] border-violet-500/50 hover:bg-violet-500/[0.12] transition-colors">
                            <td className="px-2 py-1.5 w-8"></td>
                            <td className="px-2 py-1.5 w-8">
                              <span className="block w-3 h-3 ml-3 border-l border-b border-slate-700 rounded-bl" />
                            </td>
                            <td className="px-3 py-1.5 whitespace-nowrap">
                              <div className="text-xs text-slate-500">{exp.dueDate ? fmtDate(exp.dueDate) : TRACO}</div>
                              {grandchild.dueDate && exp.dueDate && String(grandchild.dueDate).slice(0, 10) !== String(exp.dueDate).slice(0, 10) && (
                                <div className="text-xs text-slate-600" title={TITULO_DATA_LANCAMENTO}>
                                  {LABEL_LANCADO} {fmtDate(grandchild.dueDate)}
                                </div>
                              )}
                              {grandchild.paymentDate && <div className="text-xs text-slate-600">pago {fmtDate(grandchild.paymentDate)}</div>}
                            </td>
                            <td className="px-4 py-1.5 pl-8">
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <span className={`text-xs ${grandchild.displayName ? 'text-slate-300' : 'text-slate-400'} ${grandchild.status === 'Cancelado' ? 'line-through' : ''}`}>{grandchild.displayName || grandchild.description}</span>
                                <InstallmentBadge expense={grandchild} />
                                <IdentChips expense={grandchild} />
                                <NoteHint notes={grandchild.notes} />
                              </div>
                              <div className="flex items-center gap-1.5 mt-0.5 text-xs text-slate-600">
                                {grandchild.displayName && <span className="text-slate-500" title="Texto do extrato">{grandchild.description}</span>}
                                {grandchild.category && (
                                  <span className="inline-flex items-center gap-1">
                                    {grandchild.displayName && <span className="text-slate-700">·</span>}
                                    <span className="w-1.5 h-1.5 rounded-full inline-block" style={{ backgroundColor: grandchild.category.color || '#f97316' }} />
                                    {grandchild.category.name}
                                  </span>
                                )}
                              </div>
                              {identificacaoDoItem(grandchild.notes) && (
                                <div
                                  className="text-xs text-slate-500 mt-0.5 truncate max-w-[460px]"
                                  title={identificacaoDoItem(grandchild.notes)}
                                >
                                  {identificacaoDoItem(grandchild.notes)}
                                </div>
                              )}
                            </td>
                            <td className="px-4 py-1.5 text-slate-500 text-xs">
                              {grandchild.supplier?.name || '\u2014'}
                            </td>
                            <td className="px-4 py-1.5 text-right font-medium text-slate-400 text-xs">
                              <span className={grandchild.status === 'Cancelado' ? 'line-through' : ''}>
                                <VariableMark expense={grandchild} />R$ {Number(grandchild.amount).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                              </span>
                              <PlannedHint expense={grandchild} />
                            </td>
                            <td className="px-4 py-1.5">
                              <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${statusChipClass[grandchild.status] || statusChipClass.Cancelado}`}>{grandchild.status}</span>
                            </td>
                            <td className="px-4 py-1.5">
                              {grandchild.deductFromPartner ? (
                                <div className="text-xs text-purple-400 truncate max-w-[140px]" title={`Deduzido do s\u00f3cio: ${grandchild.deductFromPartner.tradeName || grandchild.deductFromPartner.name}`}>
                                  {GLIFO_SOCIO} {grandchild.deductFromPartner.tradeName || grandchild.deductFromPartner.name}
                                </div>
                              ) : <span className="text-slate-600 text-xs">{TRACO}</span>}
                            </td>
                            <td className="px-4 py-1.5">
                              <div className="flex items-center justify-end gap-1">
                                <button onClick={() => { setEditingExpense(grandchild); setExpenseModal('edit'); }} className="p-2 text-slate-500 hover:text-white hover:bg-slate-700 rounded-lg" title="Editar">
                                  <Icon name="edit" className="w-4 h-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                        </React.Fragment>
                      ))}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </TabelaDaLista>
          </CascaDaLista>
          </EstadoDaLista>

          {/* Categories list */}
          <div className="bg-central-cartao rounded-central border border-central-borda p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-white font-semibold">Categorias</h3>
            </div>
            <div className="flex flex-wrap gap-2">
              {categories.map((cat: any) => (
                <div key={cat.id} className="flex items-center gap-2 bg-slate-700/50 rounded-lg px-3 py-1.5 group">
                  <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: cat.color || '#f97316' }} />
                  {cat.icon && <span className="text-xs text-slate-400">{cat.icon}</span>}
                  <span className="text-sm text-slate-300">{cat.name}</span>
                  <button onClick={() => { setEditingCategory(cat); setCategoryModal('edit'); }} className="text-slate-500 hover:text-white opacity-0 group-hover:opacity-100 transition-opacity">
                    <Icon name="edit" className="w-3 h-3" />
                  </button>
                  {!cat.isDefault && (
                    <button onClick={() => handleDeleteCategory(cat)} title="Desativar a categoria" className="text-slate-500 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Icon name="trash" className="w-3 h-3" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* ============ SUB-VIEW: RECURRING ============ */}
      {subView === 'recurring' && (() => {
        // Sem nenhuma carga boa, os cartões dizem "—", não R$ 0,00 (ver loadRecurring).
        const semNumerosDeRecorrentes = !cargaDasRecorrentes.atualizadoEm && recurringExpenses.length === 0;
        const activeRecs = recurringExpenses.filter((r: any) => r.isActive);
        const inactiveRecs = recurringExpenses.filter((r: any) => !r.isActive);
        const totalMonthly = activeRecs.reduce((sum: number, r: any) => sum + Number(r.amount || 0), 0);
        const catTotals: Record<string, { name: string; color: string; total: number; count: number }> = {};
        activeRecs.forEach((r: any) => {
          const catName = r.category?.name || 'Sem categoria';
          const catColor = r.category?.color || '#64748b';
          if (!catTotals[catName]) catTotals[catName] = { name: catName, color: catColor, total: 0, count: 0 };
          catTotals[catName].total += Number(r.amount || 0);
          catTotals[catName].count += 1;
        });
        const sortedCats = Object.values(catTotals).sort((a, b) => b.total - a.total);
        const topCats = sortedCats.slice(0, 6);
        const top3Suppliers = [...activeRecs].sort((a, b) => Number(b.amount) - Number(a.amount)).slice(0, 3);
        const filteredRecs = recurringExpenses.filter((r: any) => {
          if (recFilter === 'active' && !r.isActive) return false;
          if (recFilter === 'inactive' && r.isActive) return false;
          if (recSearch) {
            const q = recSearch.toLowerCase();
            return (r.description || '').toLowerCase().includes(q) || (r.supplier?.name || '').toLowerCase().includes(q) || (r.category?.name || '').toLowerCase().includes(q);
          }
          return true;
        });
        const sortedRecs = [...filteredRecs].sort((a: any, b: any) => {
          let valA: any, valB: any;
          switch (recSort.col) {
            case 'category': valA = a.category?.name || ''; valB = b.category?.name || ''; break;
            case 'supplier': valA = a.supplier?.name || ''; valB = b.supplier?.name || ''; break;
            case 'bankAccount': valA = a.bankAccount?.bankName || ''; valB = b.bankAccount?.bankName || ''; break;
            case 'amount': valA = parseFloat(a.amount) || 0; valB = parseFloat(b.amount) || 0; break;
            case 'dayOfMonth': valA = a.dayOfMonth || 0; valB = b.dayOfMonth || 0; break;
            case 'isActive': valA = a.isActive ? 1 : 0; valB = b.isActive ? 1 : 0; break;
            default: valA = a[recSort.col] ?? ''; valB = b[recSort.col] ?? '';
          }
          if (typeof valA === 'string') return recSort.dir === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
          return recSort.dir === 'asc' ? valA - valB : valB - valA;
        });
        return (
        <>
          {/* Cards principais */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <CartaoDoTopo
              rotulo="Total Mensal"
              valor={semNumerosDeRecorrentes ? '—' : `R$ ${fmt(totalMonthly)}`}
              sub={<>{semNumerosDeRecorrentes ? '—' : `R$ ${fmt(totalMonthly * 12)}`}/ano</>}
            />
            <CartaoDoTopo
              rotulo="Recorrências Ativas"
              valor={semNumerosDeRecorrentes ? '—' : activeRecs.length}
              sub={`${inactiveRecs.length} inativa${inactiveRecs.length !== 1 ? 's' : ''}`}
            />
            <div className="bg-central-cartao rounded-central border border-central-borda p-4">
              <p className="text-xs text-slate-400 uppercase font-semibold mb-1">Maiores Despesas</p>
              <div className="space-y-1 mt-1">
                {top3Suppliers.map((r: any) => (
                  <div key={r.id} className="flex items-center justify-between">
                    <span className="text-xs text-slate-400 truncate max-w-[120px]">{r.description}</span>
                    <span className="text-xs text-white font-medium">R$ {fmt(r.amount)}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-central-cartao rounded-central border border-central-borda p-4">
              <p className="text-xs text-slate-400 uppercase font-semibold mb-1">Categorias ({sortedCats.length})</p>
              <div className="space-y-1 mt-1">
                {topCats.map(cat => (
                  <div key={cat.name} className="flex items-center justify-between">
                    <span className="text-xs truncate max-w-[120px]" style={{ color: cat.color }}>{cat.name} ({cat.count})</span>
                    <span className="text-xs text-slate-300 font-medium">R$ {fmt(cat.total)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          {/* Barra de proporção por categoria */}
          {totalMonthly > 0 && (
            <div className="bg-central-cartao rounded-central border border-central-borda p-4">
              <div className="flex rounded-full overflow-hidden h-3">
                {sortedCats.map(cat => (
                  <div key={cat.name} title={`${cat.name}: R$ ${fmt(cat.total)} (${((cat.total / totalMonthly) * 100).toFixed(1)}%)`} style={{ width: `${(cat.total / totalMonthly) * 100}%`, backgroundColor: cat.color }} className="transition-all hover:opacity-80" />
                ))}
              </div>
              <div className="flex flex-wrap gap-3 mt-2">
                {sortedCats.map(cat => (
                  <div key={cat.name} className="flex items-center gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: cat.color }} />
                    <span className="text-xs text-slate-400">{cat.name} <span className="text-slate-500">({((cat.total / totalMonthly) * 100).toFixed(0)}%)</span></span>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="flex items-center gap-3 justify-between">
            <div className="flex items-center gap-3 flex-1">
              <div className="relative flex-1 max-w-md">
                <Icon name="search" className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input className={`w-full pl-10 pr-3 py-2 ${campo} rounded-lg text-sm`} placeholder="Buscar recorrência..." value={recSearch} onChange={e => setRecSearch(e.target.value)} />
              </div>
              <div className="flex rounded-lg border border-slate-700 overflow-hidden">
                {([['active', 'Ativos'], ['all', 'Todos'], ['inactive', 'Inativos']] as const).map(([key, label]) => (
                  <button key={key} onClick={() => setRecFilter(key)} className={`px-3 py-2 text-xs font-medium transition-colors ${recFilter === key ? 'bg-slate-600 text-white font-semibold' : 'bg-slate-800 text-slate-400 hover:text-white'}`}>
                    {label}
                  </button>
                ))}
              </div>
            </div>
            <button onClick={() => { setRecurringModal('create'); setEditingRecurring(null); }} className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-strong text-sm font-medium flex items-center gap-2">
              <Icon name="plus" className="w-4 h-4" /> Nova Recorrência
            </button>
          </div>
          <EstadoDaLista carga={cargaDasRecorrentes} temDados={recurringExpenses.length > 0} oQue="as despesas recorrentes" onTentarDeNovo={loadRecurring} vazio={A_PROPRIA_LISTA_DIZ_O_VAZIO}>
          <CascaDaLista>
            <TabelaDaLista>
              <CabecalhoDaLista>
                {([
                  ['Descrição', 'description', '', 'esquerda'],
                  ['Categoria', 'category', 'w-40', 'esquerda'],
                  ['Fornecedor', 'supplier', 'w-44', 'esquerda'],
                  ['Valor', 'amount', 'w-40', 'direita'],
                  ['Dia', 'dayOfMonth', 'w-16', 'centro'],
                  ['Conta', 'bankAccount', 'w-36', 'esquerda'],
                  ['Método', 'paymentMethod', 'w-28', 'esquerda'],
                  ['Ativo', 'isActive', 'w-24', 'centro'],
                ] as const).map(([rotulo, col, largura, alinhamento]) => (
                  <ColunaDaLista key={col} rotulo={rotulo} largura={largura} alinhamento={alinhamento}
                    ordem={recSort.col === col ? recSort.dir : false} onOrdenar={() => toggleRecSort(col)} />
                ))}
                <ColunaDaLista rotulo={<span className="sr-only">Ações</span>} largura="w-24" />
              </CabecalhoDaLista>
              <tbody className="divide-y divide-slate-700/50">
                {sortedRecs.length === 0 ? (
                  <tr><td colSpan={9} className="px-4 py-8 text-center text-slate-500">{recSearch || recFilter !== 'active' ? 'Nenhuma recorrência encontrada' : 'Nenhuma despesa recorrente cadastrada'}</td></tr>
                ) : sortedRecs.map((rec: any) => (
                  <LinhaDaLista key={rec.id} apagada={!rec.isActive}>
                    <td className="px-4 py-3 text-slate-200 font-medium truncate" title={rec.description}>{rec.description}</td>
                    <td className="px-4 py-3">
                      {rec.category && (
                        <span className="px-2 py-0.5 text-xs rounded-full" style={{ backgroundColor: (rec.category.color || '#f97316') + '33', color: rec.category.color || '#f97316' }}>
                          {rec.category.name}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-400 truncate">{rec.supplier?.name || '—'}</td>
                    <td className="px-4 py-3 text-white font-medium text-right whitespace-nowrap tabular-nums">R$ {fmt(rec.amount)}</td>
                    <td className="px-4 py-3 text-slate-300 text-center">{rec.dayOfMonth}</td>
                    <td className="px-4 py-3">{rec.bankAccount ? <span className="px-2 py-0.5 text-xs rounded-full" style={{ backgroundColor: `${rec.bankAccount.color || '#10b981'}20`, color: rec.bankAccount.color || '#34d399' }}>{rec.bankAccount.bankName}</span> : <span className="text-slate-500">{TRACO}</span>}</td>
                    <td className="px-4 py-3 text-slate-400">{paymentMethodLabels[rec.paymentMethod] || '—'}</td>
                    <td className="px-4 py-3 text-center">
                      {/* E1: Ativo é o normal, neutro com peso; o Inativo esmaece a linha inteira. */}
                      <button onClick={() => handleToggleRecurringActive(rec)} className={`px-2.5 py-1 rounded-full text-xs ${rec.isActive ? `${TONE_BADGE.neutral} font-semibold` : 'border border-central-borda text-slate-500 font-medium'}`}>
                        {rec.isActive ? 'Ativo' : 'Inativo'}
                      </button>
                    </td>
                    {/* D1: o lápis à vista; o resto no ⋯, com o Desativar em perigo. */}
                    <CelulaAcoes
                      rotuloDoMenu={`Mais ações da recorrência ${rec.description}`}
                      onEditar={() => { setEditingRecurring(rec); setRecurringModal('edit'); }}
                      Icon={Icon}
                      itens={[
                        { label: 'Gerar parcelas até…', onClick: () => handleGenerateRecurringRange(rec) },
                        { label: 'Desativar', title: 'Desativar', danger: true, separadoAntes: true, onClick: () => handleDeleteRecurring(rec) },
                      ]}
                    />
                  </LinhaDaLista>
                ))}
              </tbody>
            </TabelaDaLista>
          </CascaDaLista>
          </EstadoDaLista>
        </>
        );
      })()}

      {/* ============ SUB-VIEW: SUPPLIERS ============ */}
      {subView === 'suppliers' && (() => {
        const activeSuppliers = suppliers.filter((s: any) => s.isActive !== false);
        const inactiveSuppliers = suppliers.filter((s: any) => s.isActive === false);
        const withCategory = suppliers.filter((s: any) => s.defaultCategoryId);
        const withoutCategory = suppliers.filter((s: any) => !s.defaultCategoryId && s.isActive !== false);
        return (
          <div className="space-y-4">
            {/* Cards de Resumo */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <CartaoDoTopo rotulo="Total de fornecedores" valor={suppliers.length} sub={`${activeSuppliers.length} ativos`} />
              <CartaoDoTopo rotulo="Ativos" valor={activeSuppliers.length} sub="Fornecedores ativos" />
              <CartaoDoTopo rotulo="Com categoria" valor={withCategory.length} sub="Categoria padrão definida" />
              {/* Sem categoria é o que pede ação: a barra âmbar, como os cartões de atenção das outras telas. */}
              <CartaoDoTopo rotulo="Sem categoria" valor={withoutCategory.length} sub="Classificar" barra={withoutCategory.length > 0 ? 'warning' : null} />
            </div>

            {/* Barra de Busca e Ações */}
            <div className="bg-central-cartao rounded-central border border-central-borda p-4">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="relative flex-1">
                  <Icon name="search" className="absolute left-3 top-3 w-5 h-5 text-slate-500" />
                  <input type="text" placeholder="Buscar por nome, CNPJ, email..." value={supplierSearch} onChange={e => setSupplierSearch(e.target.value)} className={`w-full pl-10 pr-4 py-2.5 placeholder-slate-500 rounded-lg ${campo}`} />
                </div>
                <button onClick={() => { setSupplierModal('create'); setEditingSupplier(null); }} className="flex items-center gap-2 px-4 py-2.5 bg-primary text-white rounded-lg hover:bg-primary-strong font-medium whitespace-nowrap">
                  <Icon name="plus" className="w-5 h-5" /> Novo Fornecedor
                </button>
              </div>
            </div>

            {/* Tabela */}
            <EstadoDaLista carga={cargaDaBase} temDados={suppliers.length > 0} oQue="os fornecedores" onTentarDeNovo={loadBase} vazio={A_PROPRIA_LISTA_DIZ_O_VAZIO}>
            <CascaDaLista>
                <TabelaDaLista>
                  <CabecalhoDaLista>
                      <ColunaDaLista rotulo="Fornecedor" ordem={supplierSort.col === 'name' ? supplierSort.dir : false} onOrdenar={() => toggleSupplierSort('name')} />
                      <ColunaDaLista rotulo="CNPJ/CPF" largura="w-44" ordem={supplierSort.col === 'cnpj' ? supplierSort.dir : false} onOrdenar={() => toggleSupplierSort('cnpj')} />
                      <ColunaDaLista rotulo="Contato" largura="w-64" />
                      <ColunaDaLista rotulo="Categoria" largura="w-40" alinhamento="centro" ordem={supplierSort.col === 'category' ? supplierSort.dir : false} onOrdenar={() => toggleSupplierSort('category')} />
                      <ColunaDaLista rotulo="Status" largura="w-24" alinhamento="centro" />
                      <ColunaDaLista rotulo={<span className="sr-only">Ações</span>} largura="w-24" />
                  </CabecalhoDaLista>
                  <tbody className="divide-y divide-slate-800">
                    {sortedSuppliers.length === 0 ? (
                      <tr><td colSpan={6} className="px-4 py-12 text-center text-slate-500">Nenhum fornecedor encontrado</td></tr>
                    ) : sortedSuppliers.map((sup: any) => (
                      <LinhaDaLista key={sup.id} apagada={sup.isActive === false}>
                        <td className={CELULA}>
                          <div className="min-w-0">
                            <p className="font-medium text-white truncate" title={sup.name}>{sup.name}</p>
                            {(() => {
                              const sub = sup.companyName || sup.tradeName;
                              return sub && sub !== sup.name
                                ? <p className="text-xs text-slate-500 truncate" title={sub}>{sub}</p>
                                : null;
                            })()}
                          </div>
                        </td>
                        <td className={`${CELULA} text-sm font-mono text-slate-400 whitespace-nowrap`}>{sup.cnpj || sup.cpf || sup.taxId || <span className="text-slate-600">{TRACO}</span>}</td>
                        <td className={CELULA}>
                          {sup.phone && <p className="text-sm text-white">{sup.phone}</p>}
                          {sup.email ? <p className="text-xs text-slate-500 truncate" title={sup.email}>{sup.email}</p> : !sup.phone && <span className="text-slate-600 text-xs">{TRACO}</span>}
                        </td>
                        <td className={`${CELULA} text-center`}>
                          {sup.defaultCategory ? (
                            <span className="px-2.5 py-1 rounded-full text-xs font-medium" style={{ backgroundColor: (sup.defaultCategory.color || '#f97316') + '20', color: sup.defaultCategory.color || '#f97316' }}>
                              {sup.defaultCategory.name}
                            </span>
                          ) : (
                            <span className="bg-slate-700 text-slate-400 px-2.5 py-1 rounded-full text-xs font-medium">Nenhuma</span>
                          )}
                        </td>
                        <td className={`${CELULA} text-center`}>
                          {/* E1: Ativo é o normal, neutro; o Inativo esmaece a linha. */}
                          <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${sup.isActive !== false ? TONE_BADGE.neutral : 'border border-central-borda text-slate-500'}`}>
                            {sup.isActive !== false ? 'Ativo' : 'Inativo'}
                          </span>
                        </td>
                        {/* D1: o lápis à vista; o Desativar no ⋯, em perigo. */}
                        <CelulaAcoes
                          rotuloDoMenu={`Mais ações do fornecedor ${sup.name}`}
                          onEditar={() => { setEditingSupplier(sup); setSupplierModal('edit'); }}
                          Icon={Icon}
                          itens={[{ label: 'Desativar', title: 'Desativar', danger: true, onClick: () => handleDeleteSupplier(sup) }]}
                        />
                      </LinhaDaLista>
                    ))}
                  </tbody>
                </TabelaDaLista>
            </CascaDaLista>
            </EstadoDaLista>
          </div>
        );
      })()}

      {/* ============ SUB-VIEW: PARTNERS ============ */}
      {subView === 'partners' && (
        <>
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-white font-semibold">Deduções por Sócio</h3>
              <p className="text-slate-400 text-sm">Divisao de lucro, deducoes e liquido por socio</p>
            </div>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 text-sm text-slate-400 cursor-pointer">
                <input type="checkbox" checked={allMonthsPartner} onChange={e => setAllMonthsPartner(e.target.checked)} className="accent-primary" />
                Todos os meses
              </label>
              {!allMonthsPartner && (
                <div className="flex items-center gap-1">
                  <button onClick={() => setPartnerMonth(navigateMonth(partnerMonth, -1))} className="p-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-slate-300 hover:text-white transition-colors">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                  </button>
                  <span className="text-white text-sm font-medium min-w-[140px] text-center select-none">{formatMonthLabel(partnerMonth)}</span>
                  <button onClick={() => setPartnerMonth(navigateMonth(partnerMonth, 1))} className="p-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-slate-300 hover:text-white transition-colors">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                  </button>
                </div>
              )}
            </div>
          </div>

          {partnerLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-500"></div>
              <span className="ml-3 text-slate-400">Carregando dados dos sócios...</span>
            </div>
          ) : (
            <EstadoDaLista
              carga={cargaDosSocios}
              temDados={partnerData.length > 0}
              oQue="os sócios"
              onTentarDeNovo={loadPartners}
              vazio={
                <div className="bg-central-cartao rounded-central border border-central-borda p-8 text-center">
                  <p className="text-slate-500">Nenhum socio com divisao de lucro ou deducoes encontrado.</p>
                  <p className="text-slate-600 text-sm mt-1">Cadastre despesas na categoria "Divisao de Lucro" ou marque deducoes para socios.</p>
                </div>
              }
            >
            <div className="space-y-4">
              {partnerData.map((pd: any) => (
                <div key={pd.partner.id} className="bg-central-cartao rounded-central border border-central-borda overflow-clip">
                  <div className="p-5">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-white text-lg font-bold">{pd.partner.name}</h4>
                      <span className={`px-3 py-1 rounded-full text-sm font-bold ${pd.netProLabore >= 0 ? TONE_BADGE.neutral : TONE_BADGE.danger}`}>
                        Líquido: R$ {fmt(pd.netProLabore)} {(pd.pendingTotal || 0) > 0 ? `(R$ ${fmt(pd.pendingTotal)} pendente)` : ''}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                      <div className="bg-slate-700/50 rounded-lg p-3">
                        <p className="text-slate-400 text-xs uppercase">Divisao de Lucro Bruto</p>
                        <p className="text-white text-xl font-bold mt-1">R$ {fmt(pd.proLaboreTotal)}</p>
                      </div>
                      <div className="bg-slate-700/50 rounded-lg p-3">
                        <p className="text-red-400 text-xs uppercase">Deduções</p>
                        <p className="text-red-400 text-xl font-bold mt-1">- R$ {fmt(pd.deductionsTotal)}</p>
                      </div>
                      <div className="bg-slate-700/50 rounded-lg p-3">
                        <p className="text-slate-400 text-xs uppercase">Recebido</p>
                        <p className="text-white text-xl font-bold mt-1">R$ {fmt(pd.receivedTotal)}</p>
                      </div>
                      <div className="bg-slate-700/50 rounded-lg p-3">
                        <p className="text-slate-400 text-xs uppercase">A Receber</p>
                        <p className={`text-xl font-bold mt-1 ${(pd.pendingTotal || 0) > 0 ? TONE_TEXT.warning : 'text-slate-500'}`}>R$ {fmt(pd.pendingTotal || 0)}</p>
                      </div>
                    </div>
                    {pd.proLaborePayments?.length > 0 && (
                      <div className="mb-4">
                        <p className="text-slate-400 text-xs uppercase mb-2">Pagamentos</p>
                        <div className="bg-slate-700/20 rounded-lg overflow-hidden">
                          <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <CabecalhoInterno>
                              <tr className="border-b border-slate-700/50">
                                <th className="px-3 py-2 text-xs text-slate-500 uppercase text-left">Status</th>
                                <th className="px-3 py-2 text-xs text-slate-500 uppercase text-left">Descrição</th>
                                <th className="px-3 py-2 text-xs text-slate-500 uppercase text-left">Conta</th>
                                <th className="px-3 py-2 text-xs text-slate-500 uppercase text-right">Data</th>
                                <th className="px-3 py-2 text-xs text-slate-500 uppercase text-right">Valor</th>
                              </tr>
                            </CabecalhoInterno>
                            <tbody className="divide-y divide-slate-700/30">
                              {pd.proLaborePayments.map((p: any) => {
                                const parcial = pagamentoParcial(p);
                                return (
                                <tr key={p.id} className="hover:bg-slate-700/30 transition-colors">
                                  <td className="px-3 py-2">
                                    <span className={`px-2 py-0.5 text-xs rounded-full ${p.status === 'Pago' ? TONE_BADGE.open : TONE_BADGE.warning}`}>{p.status}</span>
                                  </td>
                                  <td className="px-3 py-2 text-slate-300">{p.description}</td>
                                  <td className="px-3 py-2">{p.bankAccount ? <span className="px-2 py-0.5 text-xs rounded-full" style={{ backgroundColor: `${p.bankAccount.color || '#10b981'}20`, color: p.bankAccount.color || '#34d399' }}>{p.bankAccount.bankName}</span> : <span className="text-slate-600">{TRACO}</span>}</td>
                                  {parcial ? (
                                    // Parcial: a data da coluna é a do ÚLTIMO pagamento (o backend deriva do razão), não o
                                    // vencimento. Sem o rótulo, 16/10 numa linha "Parcial" parecia o vencimento.
                                    <td className="px-3 py-2 text-slate-500 text-right text-xs whitespace-nowrap" data-data-do-parcial>
                                      <div>pago em {fmtDate(p.paymentDate)}</div>
                                      <div className="text-slate-600">vence {fmtDate(p.dueDate)}</div>
                                    </td>
                                  ) : (
                                    <td className="px-3 py-2 text-slate-500 text-right text-xs">{fmtDate(p.paymentDate || p.dueDate)}</td>
                                  )}
                                  {parcial ? (
                                    // O "Recebido" do cartão já soma o parcial; a linha mostrava só o total da despesa, e o
                                    // pagamento de 7k parecia não ter entrado (20/10/2026, #1026).
                                    <td className="px-3 py-2 text-right" data-pagamento-parcial>
                                      <div className="text-xs whitespace-nowrap">
                                        <span className={`font-medium ${TONE_TEXT.open}`}>R$ {fmt(parcial.pago)} pago</span>
                                        <span className="text-slate-400"> de R$ {fmt(parcial.total)}</span>
                                        <span className={TONE_TEXT.warning}> · faltam R$ {fmt(parcial.falta)}</span>
                                      </div>
                                      <div className={`bg-slate-600 rounded-full h-1.5 mt-1 ${TONE_TEXT.open}`}>
                                        <div className="bg-current h-1.5 rounded-full" style={{ width: `${parcial.pct}%` }} data-barra-do-pago />
                                      </div>
                                    </td>
                                  ) : (
                                    <td className={`px-3 py-2 text-right font-medium ${p.status === 'Pago' ? TONE_TEXT.open : TONE_TEXT.warning}`}>R$ {fmt(p.amount)}</td>
                                  )}
                                </tr>
                                );
                              })}
                            </tbody>
                          </table>
                          </div>
                        </div>
                      </div>
                    )}
                    {pd.deductions.length > 0 && (
                      <div>
                        <p className="text-slate-400 text-xs uppercase mb-2">Deduções</p>
                        <div className="bg-slate-700/20 rounded-lg overflow-hidden">
                          <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <CabecalhoInterno>
                              <tr className="border-b border-slate-700/50">
                                <th className="px-3 py-2 text-xs text-slate-500 uppercase text-left">Descrição</th>
                                <th className="px-3 py-2 text-xs text-slate-500 uppercase text-left">Categoria</th>
                                <th className="px-3 py-2 text-xs text-slate-500 uppercase text-left">Fornecedor</th>
                                <th className="px-3 py-2 text-xs text-slate-500 uppercase text-center">Parcela</th>
                                <th className="px-3 py-2 text-xs text-slate-500 uppercase text-right">Data</th>
                                <th className="px-3 py-2 text-xs text-slate-500 uppercase text-right">Valor</th>
                              </tr>
                            </CabecalhoInterno>
                            <tbody className="divide-y divide-slate-700/30">
                              {pd.deductions.map((d: any) => (
                                <tr key={d.id} className="hover:bg-slate-700/30 transition-colors">
                                  <td className="px-3 py-2 text-slate-300">{d.description}</td>
                                  <td className="px-3 py-2">{d.category ? <span className="px-2 py-0.5 text-xs rounded-full" style={{ backgroundColor: (d.category.color || '#f97316') + '33', color: d.category.color || '#f97316' }}>{d.category.name}</span> : <span className="text-slate-600">{TRACO}</span>}</td>
                                  <td className="px-3 py-2 text-slate-400 text-xs">{d.supplier?.name || '—'}</td>
                                  <td className="px-3 py-2 text-center">{d.installmentNumber && d.totalInstallments ? <span className="px-1.5 py-0.5 text-xs rounded bg-slate-600/50 text-slate-400 font-mono">{d.installmentNumber}/{d.totalInstallments}</span> : <span className="text-slate-600">{TRACO}</span>}</td>
                                  <td className="px-3 py-2 text-slate-500 text-right text-xs">{fmtDate(d.dueDate)}</td>
                                  <td className="px-3 py-2 text-right text-red-400 font-medium">- R$ {fmt(d.amount)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
            </EstadoDaLista>
          )}
        </>
      )}

      {/* ============ SUB-VIEW: BUDGET ============ */}
      {subView === 'budget' && (
        <>
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-white font-semibold">Orçamento por Categoria</h3>
              <p className="text-slate-400 text-sm">Defina limites mensais de gastos por categoria</p>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={() => setBudgetMonth(navigateMonth(budgetMonth, -1))} className="p-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-slate-300 hover:text-white transition-colors">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
              </button>
              <span className="text-white text-sm font-medium min-w-[140px] text-center select-none">{formatMonthLabel(budgetMonth)}</span>
              <button onClick={() => setBudgetMonth(navigateMonth(budgetMonth, 1))} className="p-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-slate-300 hover:text-white transition-colors">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
              </button>
            </div>
          </div>

          {budgetLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-500"></div>
              <span className="ml-3 text-slate-400">Carregando orçamento...</span>
            </div>
          ) : (
            <>
              {/* Summary */}
              {budgetData.length > 0 && (() => {
                const exceeded = budgetData.filter((b: any) => b.budget > 0 && b.actual > b.budget).length;
                const withBudget = budgetData.filter((b: any) => b.budget > 0).length;
                return (
                  <div className="bg-central-cartao rounded-central border border-central-borda p-4">
                    <div className="flex items-center gap-6">
                      <div>
                        <span className="text-slate-400 text-sm">Categorias com orçamento</span>
                        <p className="text-white font-bold text-lg">{withBudget}</p>
                      </div>
                      {exceeded > 0 && (
                        <div>
                          <span className="text-red-400 text-sm">Excederam orçamento</span>
                          <p className="text-red-400 font-bold text-lg">{exceeded}</p>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* Sem o orçado, as linhas mostrariam "Realizado R$ 0,00" com os campos em branco, e salvar ali gravaria
                  por cima do orçamento que existe. Com a carga falhando, as linhas não aparecem. Sem as categorias
                  (dados base), não há linha nenhuma, e isso também não é "sem orçamento". */}
              <EstadoDaLista carga={cargaDaBase} temDados={categories.length > 0} oQue="as categorias" onTentarDeNovo={loadBase} vazio={A_PROPRIA_LISTA_DIZ_O_VAZIO}>
              <EstadoDaLista carga={cargaDoOrcamento} temDados={budgetData.length > 0} oQue="o orçamento" onTentarDeNovo={loadBudget} vazio={A_PROPRIA_LISTA_DIZ_O_VAZIO}>
              <div className="space-y-3">
                {categories.filter(c => c.isActive).map((cat: any) => {
                  const bd = budgetData.find((b: any) => b.categoryId === cat.id);
                  const actual = bd?.actual || 0;
                  const budget = bd?.budget || 0;
                  const pct = budget > 0 ? (actual / budget) * 100 : 0;
                  const barColor = pct > 100 ? '#ef4444' : pct > 80 ? '#eab308' : '#22c55e';

                  return (
                    <div key={cat.id} className="bg-central-cartao rounded-central border border-central-borda p-4">
                      <div className="flex items-center gap-4">
                        <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: cat.color || '#f97316' }} />
                        {cat.icon && <span className="text-sm text-slate-400">{cat.icon}</span>}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-white font-medium">{cat.name}</span>
                            <div className="flex items-center gap-3">
                              <span className="text-slate-400 text-sm">Realizado: <span className="text-white font-medium">R$ {fmt(actual)}</span></span>
                              {budget > 0 && <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ backgroundColor: barColor + '33', color: barColor }}>{pct.toFixed(0)}%</span>}
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="flex-1">
                              {budget > 0 && (
                                <div className="w-full bg-slate-700 rounded-full h-2">
                                  <div className="h-2 rounded-full transition-all" style={{ width: `${Math.min(pct, 100)}%`, backgroundColor: barColor }} />
                                </div>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-slate-500 text-xs">R$</span>
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                className={`w-28 px-2 py-1 ${campo} rounded text-sm`}
                                placeholder="Orçamento"
                                value={budgetInputs[cat.id] || ''}
                                onChange={e => setBudgetInputs(prev => ({ ...prev, [cat.id]: e.target.value }))}
                              />
                              <button onClick={() => handleSaveBudget(cat.id)} className="px-3 py-1 bg-primary text-white rounded text-sm hover:bg-primary-strong">
                                Salvar
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              </EstadoDaLista>
              </EstadoDaLista>
            </>
          )}
        </>
      )}

      {/* Modals */}
      {expenseModal && <ExpenseForm ctx={modalCtx} onClose={() => { setExpenseModal(null); setEditingExpense(null); setCreateDefaults(null); }} expense={expenseModal === 'edit' ? editingExpense : undefined} defaults={expenseModal === 'create' ? createDefaults : undefined} />}
      {payModal && <PayModal ctx={modalCtx} expense={payModal} onClose={() => setPayModal(null)} />}
      {receiptModal && <ReceiptModal ctx={modalCtx} expense={receiptModal} onClose={() => setReceiptModal(null)} />}
      {splitModal && <SplitExpenseModal ctx={modalCtx} expense={splitModal} onClose={() => setSplitModal(null)} onSplit={async (id) => {
        try {
          const children = await expensesService.getChildren(id);
          setExpandedRows(prev => ({ ...prev, [id]: children }));
        } catch (e) {}
      }} />}
      {rebalanceModal && <RebalanceModal ctx={modalCtx} expense={rebalanceModal} onClose={() => setRebalanceModal(null)} onDone={async (id) => {
        try {
          const children = await expensesService.getChildren(id);
          setExpandedRows(prev => ({ ...prev, [id]: children }));
        } catch (e) {}
        await loadExpenses();
      }} />}
      {recurringModal && <RecurringForm ctx={modalCtx} onClose={() => { setRecurringModal(null); setEditingRecurring(null); }} recurring={recurringModal === 'edit' ? editingRecurring : undefined} />}
      {supplierModal && <SupplierForm ctx={modalCtx} onClose={() => { setSupplierModal(null); setEditingSupplier(null); }} supplier={supplierModal === 'edit' ? editingSupplier : undefined} />}
      {categoryModal && <CategoryForm ctx={modalCtx} onClose={() => { setCategoryModal(null); setEditingCategory(null); }} category={categoryModal === 'edit' ? editingCategory : undefined} />}
      {csvModal && <CsvImportModal ctx={modalCtx} onClose={() => setCsvModal(false)} />}
      {cardStatementModal && <CardStatementImportModal onClose={() => setCardStatementModal(false)} categories={categories} setCategories={setCategories} bankAccounts={bankAccounts} suppliers={suppliers} showToast={showToast} loadExpenses={loadExpenses} loadBase={loadBase} />}
      {groupModal && <GroupExpensesModal ctx={modalCtx} selectedIds={Array.from(selectedExpenses)} onClose={() => setGroupModal(false)} />}
    </div>
  );
}
